import { IconArrowsMaximize } from '@tabler/icons-react';
import React, { useMemo, useRef, useState } from 'react';
import {
    Button,
    Divider,
    Group as MGroup,
    NumberInput,
    SegmentedControl,
    Stack as MStack,
    Text as MText,
} from '@mantine/core';

import { ReparoApi } from '../../api';
import { getOrgConfig } from '../../api/organization';
import {
    ATTR_ROLLUP_PREFIX,
    isRollupActive,
    NO_ROLLUP,
    resolveEffectiveRollup,
    resolveOrgRollupDefault,
    STATIC_ROLLUP_OPTIONS,
    UNASSIGNED_GROUP_LABEL,
} from '@constants/inventoryRollup';
import { compareDrrBySortBy, drrSortParams } from '@utils/drrSort';

/** Grouping-dimension key for the warehouse column in the SKU Ageing WAREHOUSE scope. */
const AGEING_WAREHOUSE_DIMENSION = 'warehouse';
import { useQuery } from '@tanstack/react-query';
import {
    getDrrView,
    getWarehouseStock,
    getWarehouseStockPlanning,
    type ChannelShipmentLeg,
    type DrrBucket,
    type DrrViewResponse,
    type DrrViewRow,
    type RateBasis,
    type SortBy,
    getInventoryAgeing,
    type InventoryAgeingResponse,
    type InventoryAgeingRow,
    type InventoryAgeingScope,
} from '../../api/inventory';
import { leafRowsOf } from '../../utils/rowTree';
import usePersistentState from '../../hooks/usePersistentState';
import { useSkuExplosion } from '../../hooks/useSkuExplosion';
import useToaster from '../../hooks/toaster/useToaster';
import {
    addScaledDrrRow,
    addScaledPlanningRow,
    addScaledSnapshotRow,
    cloneDrrRow,
    clonePlanningRow,
    cloneSnapshotRow,
    getDrrRowId,
    getPlanningRowId,
    getSnapshotRowId,
    makeEmptyDrrRow,
    makeEmptyPlanningRow,
    makeEmptySnapshotRow,
    makeSnapshotRecompute,
    recomputeDrrRow,
    recomputePlanningRow,
} from './inventoryExplosion';
import {
    useEntityAttributes,
    resolveAttributeFilterEntityIds,
} from '../../hooks/useEntityAttributes';
import {
    toIsActive,
    ACTIVE_STATUS_OPTIONS,
} from '../../hooks/useProductFilterParams';
import {
    getMatchingEntityIds,
    getEntityMasterTable,
} from '../../api/entity-attributes';
import PageHeader from '../common/PageHeader';
import AskAiButton from '@components/QuickHelpPanel/AskAiButton';
import { useWidgetContextCapture } from '@hooks/useWidgetContextCapture';
import FilterDropdown from '../ui/Dropdown';
import GroupingLevels from '../ui/GroupingLevels';
import { ColumnPicker } from '../ui/ColumnPicker';
import { RateBasisControl } from '../ui/RateBasisControl';
import {
    DataSheet,
    DataSheetPagination,
    DataSheetLegend,
    type DataSheetCaptureHandle,
    type DataSheetColumn,
    type DataSheetFilterConfig,
    dataSheetTokens,
} from '../ui/DataSheet';
import type { FilterValue } from '../ui/EntityFilterDropdown';
import DrrNodeBreakdownModal from './DrrNodeBreakdownModal';
import StockTransferModal from './StockTransferModal';
import ChannelShipmentModal from './ChannelShipmentModal';
import {
    STOCK_TRANSFER_HIDE_KEY,
    STOCK_TRANSFER_LABEL,
    STOCK_TRANSFER_TO_WAREHOUSE_TOOLTIP,
} from './stockTransfer.constants';
import StockTransferHeader from './StockTransferHeader';
import AgeingReceiptsModal from './AgeingReceiptsModal';
import { InventoryAgeingTable } from './InventoryAgeingTable';
import {
    AGE_BUCKET_COLORS,
    AGEING_DEFAULT_HIDDEN,
    AGEING_DEFAULT_OFF_DEFS,
    AGEING_DEFAULT_ON_DEFS,
    AGEING_WAREHOUSE_ON_DEFS,
    buildAgeingColumns,
    OLDER_THAN_HISTORY_COLOR,
    type AgeingCellTarget,
} from './inventoryAgeingColumns';
import { ageBucketLabels, attachAgeAggregates, DEFAULT_AGEING_EDGES } from './inventoryAgeing';
import InTransitBreakdownModal from './InTransitBreakdownModal';
import OpeningStockCalculationModal from './OpeningStockCalculationModal';
import ShareModal from '../ui/ShareModal';
import { ConsultantCapability } from '../../constants/consultant-capability';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import useApp from '../../providers/App/useApp';
import { formatCount } from '@utils/currency';
import type { WarehouseStockPlanningAggregateRow } from '@api/inventory';

// Default Fallbacks
const DEFAULT_UNDER_STOCK = 15;
const DEFAULT_OVER_STOCK = 60;

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

export type WeekPlan = {
    label: string; // "Week 1"
    opening: number;
    consumption: number; // Added for calculation
    doh: number | null;
    /** Total incoming for the period — the sum of the three legs below. */
    inTransit: number;
    /** The three inbound legs, all bucketed on expected arrival at the warehouse. */
    incomingLegs: IncomingLegs;
    belowSafety?: boolean;
};

/** In-transit / in-production (PO) / in-production (WO) — the split behind "Incoming". */
export type IncomingLegs = {
    inTransit: number;
    inProductionPo: number;
    inProductionWo: number;
};

const toIncomingLegs = (period: {
    in_transit?: number;
    in_production_po?: number;
    in_production_wo?: number;
}): IncomingLegs => ({
    inTransit: period.in_transit ?? 0,
    inProductionPo: period.in_production_po ?? 0,
    inProductionWo: period.in_production_wo ?? 0,
});

/**
 * "in transit 120 · in prod (PO) 400" — zero legs are dropped, and a single remaining leg
 * returns null because a subtext that just restates the total is noise, not information.
 */
const formatIncomingLegs = (legs?: IncomingLegs): string | null => {
    if (!legs) return null;
    const parts: string[] = [];
    if (legs.inTransit > 0) {
        parts.push(`in transit ${formatCount(legs.inTransit)}`);
    }
    if (legs.inProductionPo > 0) {
        parts.push(`in prod (PO) ${formatCount(legs.inProductionPo)}`);
    }
    if (legs.inProductionWo > 0) {
        parts.push(`in prod (WO) ${formatCount(legs.inProductionWo)}`);
    }
    return parts.length > 1 ? parts.join(' · ') : null;
};

export type FinishedGoodsPlanningRow = {
    productId: string;
    skuCode: string;
    description: string;
    totalInventory: number;
    weeks: WeekPlan[];
    category: string | null;
    tier: string | null;
    productGroup: string | null;
    isActive: boolean | null;
    /** Set only on rollup rows: the member SKUs, rendered as expandable sub-rows. */
    subRows?: FinishedGoodsPlanningRow[];
    /** Set only on rollup rows: how many SKUs the group covers. */
    skuCount?: number;
};

type SnapshotWarehouse = {
    name: string;
    quantity: number;
    onHoldStock: number | null;
    /**
     * Channel views only: units committed to this warehouse and not yet received. Null
     * outside a channel view, where the server has no reading to give. Never part of
     * `quantity` — see renderStockWithInTransit.
     */
    inTransit: number | null;
    /**
     * Split channels only: the planned-not-dispatched part of what is committed, disjoint from
     * `inTransit`. Null when the channel's source cannot split the figure.
     */
    scheduled: number | null;
    /**
     * Channel views only: units that have LEFT this warehouse for a sibling warehouse of the
     * same channel and not yet landed. Opposite direction to `inTransit`, and null when the
     * channel does not report the figure at all.
     */
    warehouseTransfer: number | null;
    isChannel: boolean;
    /** The synthetic Stock Transfer location (units between two of our warehouses). */
    isStockTransfer: boolean;
    /** One of the brand's material stores: shown after the other warehouses. */
    isMaterialStore?: boolean;
};

export type FinishedGoodsSnapshotRow = {
    productId: string;
    skuCode: string;
    description: string;
    totalInventory: number;
    onHoldStock: number | null;
    /** Channel views only: this SKU's total inbound across the channel's warehouses. */
    inTransit: number | null;
    /** Split channels only: the planned-not-dispatched part, disjoint from `inTransit`. */
    scheduled: number | null;
    /** Channel views only: this SKU's total units moving between the channel's warehouses. */
    warehouseTransfer: number | null;
    consumptionPerDay: number;
    daysOnHand: number | null;
    warehouses: SnapshotWarehouse[];
    category: string | null;
    tier: string | null;
    productGroup: string | null;
    isActive: boolean | null;
    /** Set only on rollup rows: the member SKUs, rendered as expandable sub-rows. */
    subRows?: FinishedGoodsSnapshotRow[];
    /** Set only on rollup rows: how many SKUs the group covers. */
    skuCount?: number;
};

/**
 * Snapshot location columns in display order — internal warehouses (the brand's material stores
 * after the rest: they hold raw and packing material), then Stock Transfer (units between two of
 * those warehouses, so it belongs with them), then channel rollups, each alphabetical. Shared by
 * the grid and its CSV export so the two never order differently.
 */
function orderSnapshotLocations(
    rows: FinishedGoodsSnapshotRow[],
): Array<{ name: string; isChannel: boolean; isStockTransfer: boolean }> {
    const byName = new Map<string, { isChannel: boolean; isStockTransfer: boolean; isMaterialStore: boolean }>();
    rows.forEach((row) =>
        row.warehouses.forEach((warehouse) => {
            if (warehouse.name && !byName.has(warehouse.name)) {
                byName.set(warehouse.name, {
                    isChannel: warehouse.isChannel,
                    isStockTransfer: warehouse.isStockTransfer,
                    isMaterialStore: !!warehouse.isMaterialStore,
                });
            }
        }),
    );
    const rank = (entry: { isChannel: boolean; isStockTransfer: boolean; isMaterialStore: boolean }) =>
        entry.isStockTransfer ? 2 : entry.isChannel ? 3 : entry.isMaterialStore ? 1 : 0;
    return [...byName.entries()]
        .sort(([nameA, a], [nameB, b]) => rank(a) - rank(b) || nameA.localeCompare(nameB))
        .map(([name, { isChannel, isStockTransfer }]) => ({ name, isChannel, isStockTransfer }));
}

/* ------------------------------------------------------------------ */
/*  Column visibility constants                                        */
/* ------------------------------------------------------------------ */

// Snapshot: default-ON = always pre-checked; default-OFF = unchecked until toggled
const SNAPSHOT_DEFAULT_ON_DEFS: { key: string; label: string }[] = [
    { key: 'totalInventory', label: 'Total Inventory' },
    { key: 'daysOnHand', label: 'MoH / DoH' },
];
const SNAPSHOT_DEFAULT_OFF_DEFS: { key: string; label: string }[] = [
    { key: 'onHoldStock', label: 'On Hold Stock' },
    { key: 'category', label: 'Category' },
    { key: 'tier', label: 'Tier' },
    { key: 'productGroup', label: 'Product Group' },
    { key: 'isActive', label: 'Active Status' },
];

// Planning / Pipeline: no default-ON toggleable cols — all optional
const PLANNING_DEFAULT_OFF_DEFS: { key: string; label: string }[] = [
    { key: 'category', label: 'Category' },
    { key: 'tier', label: 'Tier' },
    { key: 'productGroup', label: 'Product Group' },
    { key: 'isActive', label: 'Active Status' },
];

// Default hidden sets (seeded into localStorage on first load via _v2 key)
const SNAPSHOT_DEFAULT_HIDDEN = SNAPSHOT_DEFAULT_OFF_DEFS.map((d) => d.key);
const PLANNING_DEFAULT_HIDDEN = PLANNING_DEFAULT_OFF_DEFS.map((d) => d.key);

/* ------------------------------------------------------------------ */
/*  Snapshot view table - Using DataSheet                             */
/* ------------------------------------------------------------------ */

export const dohHeaderLabel = (inventoryPeriod: 'DAY' | 'MONTH') =>
    inventoryPeriod === 'MONTH' ? 'MoH' : 'DoH';

/**
 * DoH is computed in days; a merchant on a MONTH period reads it in months. Shared by
 * the grid and its CSV export so a downloaded figure matches the one on screen.
 */
const toDisplayDohFor =
    (inventoryPeriod: 'DAY' | 'MONTH') =>
    (doh: number | null | undefined): number | null =>
        doh === null || doh === undefined
            ? null
            : inventoryPeriod === 'MONTH'
              ? doh / 30
              : doh;

/**
 * Red/amber threshold rendering for DoH/MoH cells — the single source for the
 * styling previously copy-pasted between the Snapshot and Planning tables.
 */
export const renderDohValue = (
    doh: number | null | undefined,
    underStockLimit: number,
    overStockLimit: number,
): React.ReactNode => {
    let color = 'rgb(71, 85, 105)';
    let fontWeight = 400;
    if (doh !== null && doh !== undefined) {
        if (doh < underStockLimit) {
            color = 'rgb(239, 68, 68)';
            fontWeight = 600;
        } else if (doh > overStockLimit) {
            color = 'rgb(245, 158, 11)';
            fontWeight = 600;
        }
    }
    return (
        <span style={{ color, fontWeight, fontVariantNumeric: 'tabular-nums' }}>
            {doh !== null && doh !== undefined ? Number(doh).toFixed(1) : 'NA'}
        </span>
    );
};

/* ------------------------------------------------------------------ */
/*  Row rollup (group SKU rows by a product property)                  */
/* ------------------------------------------------------------------ */

// NO_ROLLUP / ATTR_ROLLUP_PREFIX / UNASSIGNED_GROUP_LABEL / STATIC_ROLLUP_OPTIONS now live in
// @constants/inventoryRollup so the Settings brand-default picker shares one vocabulary with
// this grid. Imported at the top of the file.

/**
 * Sums stock and demand across the group's SKUs, then divides the sums — a group's
 * DoH is Σstock / ΣDRR, never an average of per-SKU DoH values (which would let one
 * barely-selling SKU's enormous DoH dominate the group).
 *
 * `stock` stays null when no member carried one: non-node-backed channels share the
 * internal pool, so they have a DRR but no stock of their own, and must not report a
 * DoH at group level either.
 *
 * `warehouse_transfer` is summed IN PARALLEL with stock, not added to it — it is already
 * inside every member's `stock` (the server folds it before the response), and is carried up
 * only so the group row can export the same split its member rows do. Adding it to `stock`
 * here would double count.
 */
const aggregateDrrBuckets = (buckets: (DrrBucket | undefined)[]): DrrBucket => {
    let drr = 0;
    let stock: number | null = null;
    // Same null-vs-zero rule as stock: only the channel pivot populates in-transit, so a
    // group whose members carried none must report "no reading", not a zero.
    let inTransit: number | null = null;
    let scheduled: number | null = null;
    let warehouseTransfer: number | null = null;
    // Inside `stock` like warehouse transfer; summed alongside so the group row can print it.
    let stockTransfer: number | null = null;
    for (const bucket of buckets) {
        if (!bucket) continue;
        drr += bucket.drr || 0;
        if (bucket.stock_transfer !== null && bucket.stock_transfer !== undefined) {
            stockTransfer = (stockTransfer ?? 0) + bucket.stock_transfer;
        }
        if (bucket.stock !== null && bucket.stock !== undefined) {
            stock = (stock ?? 0) + bucket.stock;
        }
        if (bucket.in_transit !== null && bucket.in_transit !== undefined) {
            inTransit = (inTransit ?? 0) + bucket.in_transit;
        }
        if (bucket.scheduled !== null && bucket.scheduled !== undefined) {
            scheduled = (scheduled ?? 0) + bucket.scheduled;
        }
        if (bucket.warehouse_transfer !== null && bucket.warehouse_transfer !== undefined) {
            warehouseTransfer = (warehouseTransfer ?? 0) + bucket.warehouse_transfer;
        }
    }
    return {
        drr,
        stock,
        // In-transit stays out of the cover arithmetic at group level too — it is not stock.
        doh: stock !== null && drr > 0 ? stock / drr : null,
        in_transit: inTransit,
        scheduled,
        warehouse_transfer: warehouseTransfer,
        stock_transfer: stockTransfer,
    };
};

/**
 * Buckets rows by a group value and hands each bucket to `buildGroupRow`.
 *
 * Named groups come out alphabetically; ungrouped/uncategorised SKUs are a
 * catch-all rather than a real group, so they always sink to the bottom. A
 * header sort overrides this ordering client-side.
 */
const groupRowsByValue = <T,>(
    rows: T[],
    resolveGroupValue: (row: T) => string,
    buildGroupRow: (groupValue: string, members: T[]) => T,
    /**
     * Orders the built group rows. Omitted, groups sort alphabetically as they always
     * have. Supplied, it compares the aggregates — which is why rows are built before
     * sorting rather than after: a group's DRR/DoH only exists once it is aggregated.
     */
    compareGroups?: (a: T, b: T) => number,
): T[] => {
    const groups = new Map<string, T[]>();
    rows.forEach((row) => {
        const key = resolveGroupValue(row) || UNASSIGNED_GROUP_LABEL;
        const bucket = groups.get(key);
        if (bucket) {
            bucket.push(row);
        } else {
            groups.set(key, [row]);
        }
    });

    return [...groups.entries()]
        .map(([groupValue, members]) => ({
            groupValue,
            row: buildGroupRow(groupValue, members),
        }))
        .sort((a, b) => {
            // Unassigned is a bucket, not a result — it stays last under every ordering.
            const aUnassigned = a.groupValue === UNASSIGNED_GROUP_LABEL;
            const bUnassigned = b.groupValue === UNASSIGNED_GROUP_LABEL;
            if (aUnassigned !== bUnassigned) return aUnassigned ? 1 : -1;
            if (compareGroups) return compareGroups(a.row, b.row);
            return a.groupValue.localeCompare(b.groupValue);
        })
        .map((entry) => entry.row);
};

/** The product properties every view's rows expose for grouping. */
type RollupSource = {
    productId: string;
    category: string | null;
    tier: string | null;
    productGroup: string | null;
    isActive: boolean | null;
};

/**
 * The group a row belongs to under the active rollup dimension. Empty string
 * means "no value" and is bucketed as Unassigned by `groupRowsByValue`.
 */
const resolveRollupValue = (
    rollupBy: string,
    row: RollupSource,
    attrValuesByProduct?: Map<string, Record<string, string>>,
): string => {
    if (rollupBy.startsWith(ATTR_ROLLUP_PREFIX)) {
        return attrValuesByProduct?.get(row.productId)?.[rollupBy] ?? '';
    }
    switch (rollupBy) {
        case 'category':
            return row.category ?? '';
        case 'tier':
            return row.tier ?? '';
        case 'productGroup':
            return row.productGroup ?? '';
        case 'isActive':
            return row.isActive === null || row.isActive === undefined
                ? ''
                : row.isActive
                  ? 'Active'
                  : 'Inactive';
        default:
            return '';
    }
};

/** Rollup rows carry the group name in place of a product identity. */
const isGroupRow = (row: { subRows?: unknown[] }): boolean => !!row.subRows;

/**
 * Snapshot rollup: quantities sum, and the group's DoH is Σstock / Σdemand —
 * the same ratio-of-sums the backend's own aggregate row uses, never an
 * average of per-SKU DoH (which would let one barely-selling SKU dominate).
 */
const buildSnapshotGroupRow = (
    groupValue: string,
    members: FinishedGoodsSnapshotRow[],
    inventoryPeriod: 'DAY' | 'MONTH',
): FinishedGoodsSnapshotRow => {
    const totalInventory = members.reduce(
        (sum, member) => sum + (member.totalInventory || 0),
        0,
    );
    const consumptionPerDay = members.reduce(
        (sum, member) => sum + (member.consumptionPerDay || 0),
        0,
    );
    const hasOnHold = members.some((member) => member.onHoldStock !== null);
    const daysOnHand =
        consumptionPerDay > 0
            ? totalInventory /
              consumptionPerDay /
              (inventoryPeriod === 'MONTH' ? 30 : 1)
            : null;

    const warehouses: SnapshotWarehouse[] = [];
    const warehouseIndex = new Map<string, SnapshotWarehouse>();
    members.forEach((member) =>
        member.warehouses.forEach((warehouse) => {
            const existing = warehouseIndex.get(warehouse.name);
            if (existing) {
                existing.quantity += warehouse.quantity || 0;
                if (warehouse.onHoldStock !== null) {
                    existing.onHoldStock =
                        (existing.onHoldStock ?? 0) + warehouse.onHoldStock;
                }
                if (warehouse.inTransit !== null) {
                    existing.inTransit =
                        (existing.inTransit ?? 0) + warehouse.inTransit;
                }
                if (warehouse.scheduled !== null) {
                    existing.scheduled = (existing.scheduled ?? 0) + warehouse.scheduled;
                }
                if (warehouse.warehouseTransfer !== null) {
                    existing.warehouseTransfer =
                        (existing.warehouseTransfer ?? 0) + warehouse.warehouseTransfer;
                }
                return;
            }
            const copy: SnapshotWarehouse = { ...warehouse };
            warehouseIndex.set(warehouse.name, copy);
            warehouses.push(copy);
        }),
    );

    return {
        productId: `group:${groupValue}`,
        skuCode: groupValue,
        description: '',
        totalInventory,
        onHoldStock: hasOnHold
            ? members.reduce((sum, member) => sum + (member.onHoldStock ?? 0), 0)
            : null,
        // Null unless some member actually carried a reading — the same rule as on-hold, so a
        // group in the Overall view (where nothing reports in-transit) stays blank.
        inTransit: members.some((member) => member.inTransit !== null)
            ? members.reduce((sum, member) => sum + (member.inTransit ?? 0), 0)
            : null,
        scheduled: members.some((member) => member.scheduled !== null)
            ? members.reduce((sum, member) => sum + (member.scheduled ?? 0), 0)
            : null,
        // Same null-unless-some-member-reported rule, so a rollup on a channel that does not
        // report warehouse transfer stays blank rather than claiming a zero.
        warehouseTransfer: members.some((member) => member.warehouseTransfer !== null)
            ? members.reduce((sum, member) => sum + (member.warehouseTransfer ?? 0), 0)
            : null,
        consumptionPerDay,
        daysOnHand,
        warehouses,
        // A rollup row spans many products, so it carries no single identity.
        category: null,
        tier: null,
        productGroup: null,
        isActive: null,
        skuCount: members.length,
        subRows: members,
    };
};

/**
 * Planning / Pipeline rollup: per-week quantities sum, and each week's group
 * DoH is Σopening / Σdemand-rate. A member's rate is derived from its own
 * opening ÷ DoH rather than its `consumption` field, so the group reproduces
 * exactly the demand basis the backend used per SKU whatever that basis is.
 * Members with no DoH contribute stock but no demand, as they should.
 */
const buildPlanningGroupRow = (
    groupValue: string,
    members: FinishedGoodsPlanningRow[],
): FinishedGoodsPlanningRow => {
    const weekCount = members.reduce(
        (max, member) => Math.max(max, member.weeks.length),
        0,
    );
    const weeks: WeekPlan[] = Array.from({ length: weekCount }, (_, index) => {
        let opening = 0;
        let consumption = 0;
        let inTransit = 0;
        let demandRate = 0;
        const incomingLegs: IncomingLegs = {
            inTransit: 0,
            inProductionPo: 0,
            inProductionWo: 0,
        };
        members.forEach((member) => {
            const week = member.weeks[index];
            if (!week) return;
            opening += week.opening || 0;
            consumption += week.consumption || 0;
            inTransit += week.inTransit || 0;
            incomingLegs.inTransit += week.incomingLegs?.inTransit || 0;
            incomingLegs.inProductionPo += week.incomingLegs?.inProductionPo || 0;
            incomingLegs.inProductionWo += week.incomingLegs?.inProductionWo || 0;
            if (week.doh && week.doh > 0) {
                demandRate += (week.opening || 0) / week.doh;
            }
        });
        const doh = demandRate > 0 ? opening / demandRate : null;
        return {
            label:
                members.find((member) => member.weeks[index])?.weeks[index]
                    ?.label ?? '',
            opening,
            consumption,
            inTransit,
            incomingLegs,
            doh,
            belowSafety: (doh || 0) < 14,
        };
    });

    return {
        productId: `group:${groupValue}`,
        skuCode: groupValue,
        description: '',
        totalInventory: members.reduce(
            (sum, member) => sum + (member.totalInventory || 0),
            0,
        ),
        weeks,
        category: null,
        tier: null,
        productGroup: null,
        isActive: null,
        skuCount: members.length,
        subRows: members,
    };
};

/** Group-name cell shared by the rolled-up Snapshot / Planning / Pipeline grids. */
const GroupNameCell: React.FC<{ row: { skuCode: string; skuCount?: number } }> =
    ({ row }) => (
        <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 600 }}>{row.skuCode}</span>
            <span
                style={{
                    fontSize: 11,
                    color: dataSheetTokens.colors.textMuted,
                }}
            >
                {row.skuCount ?? 0} SKU{(row.skuCount ?? 0) === 1 ? '' : 's'}
            </span>
        </span>
    );

// Exported for the /email-render/table headless route, which reproduces the DoH view's
// grouping with exactly this aggregation so a scheduled screenshot matches the screen.
export const rollupDrrRows = (
    rows: DrrViewRow[],
    resolveGroupValue: (row: DrrViewRow) => string,
    /** Optional second level, nested inside each parent by the same aggregation. */
    resolveSubGroupValue?: (row: DrrViewRow) => string,
    /** Sort By ordering, applied to every level so a sub-group ranks like its parents. */
    compareGroups?: (a: DrrViewRow, b: DrrViewRow) => number,
): DrrViewRow[] =>
    groupRowsByValue(
        rows,
        resolveGroupValue,
        (groupValue, members) => {
        const channelKeys = new Set<string>();
        members.forEach((member) =>
            Object.keys(member.channels).forEach((key) => channelKeys.add(key)),
        );
        const channels: Record<string, DrrBucket> = {};
        channelKeys.forEach((key) => {
            channels[key] = aggregateDrrBuckets(
                members.map((member) => member.channels[key]),
            );
        });
        const hasSecondary = members.some((member) => member.channel_secondary);

        return {
            product_id: `group:${groupValue}`,
            sku: groupValue,
            // A rollup row has no description of its own — the SKU count renders as
            // subtext under the group name instead.
            name: '',
            skuCount: members.length,
            // A rollup row spans many products, so it carries no single product identity.
            category: null,
            tier: null,
            product_group: null,
            is_active: null,
            overall: aggregateDrrBuckets(members.map((member) => member.overall)),
            internal: aggregateDrrBuckets(members.map((member) => member.internal)),
            channels,
            ...(hasSecondary && {
                channel_secondary: aggregateDrrBuckets(
                    members.map((member) => member.channel_secondary),
                ),
            }),
            // Sub-groups when a second level is chosen, leaves otherwise. Built by this
            // same function, so a sub-group aggregates by exactly the rule its parent does.
            subRows: resolveSubGroupValue
                ? rollupDrrRows(
                      members,
                      resolveSubGroupValue,
                      undefined,
                      compareGroups,
                  )
                : members,
        };
        },
        compareGroups,
    );

const formatDrr = (value: number): string =>
    value >= 100 ? formatCount(Math.round(value)) : value.toFixed(1);

/**
 * Inventory cells read as whole units, like the Snapshot's quantities. Null is
 * unknown, never zero — an internal-fulfilled channel holds no stock of its own
 * (it draws on the internal pool) and a channel whose stock feed reported nothing
 * has no reading at all. A node-backed channel that is simply sold out reports 0.
 */
const formatStock = (value: number | null | undefined): string =>
    value === null || value === undefined
        ? '—'
        : formatCount(Math.round(value));

/**
 * The In Transit cell — units committed to a location but not yet received.
 *
 * Its own column rather than subtext under Inv, because stacking two figures in an 84px
 * cell truncated both. Muted, so the eye still reads Inv/DRR/DoH as the primary row and
 * treats this as the supporting figure it is: it is deliberately absent from `stock` and
 * therefore from cover, since inventory on a truck is not inventory you can sell.
 *
 * `null` means this bucket has no such reading at all (a non-channel group) and renders as
 * an em dash — distinct from a channel node that genuinely has nothing inbound, which
 * reads 0. Which buckets get the column at all is the server's call; see `in_transit_buckets`.
 */
const renderInTransit = (value: number | null | undefined): React.ReactNode => (
    <span
        style={{
            fontVariantNumeric: 'tabular-nums',
            color: dataSheetTokens.colors.textMuted,
        }}
    >
        {value === null || value === undefined
            ? '—'
            : formatCount(Math.round(value))}
    </span>
);

/**
 * An In Transit / Scheduled figure the operator can click to see the shipments behind it —
 * the same dotted affordance the Stock Transfer and DRR cells use. Offered only for the
 * buckets whose figure is split into legs: that split exists exactly when the channel reports
 * its own shipments, which is also when there are shipment rows to show. A stock-feed balance
 * (Blinkit, Flipkart) is one number with nothing behind it, so its cell stays plain.
 */
const renderShipmentFigure = (
    value: number | null | undefined,
    onClick: (() => void) | null,
): React.ReactNode =>
    onClick && value ? (
        <span
            onClick={(event) => {
                event.stopPropagation();
                onClick();
            }}
            title="View the shipments"
            style={{
                cursor: 'pointer',
                textDecoration: 'underline dotted',
                textUnderlineOffset: 3,
            }}
        >
            {renderInTransit(value)}
        </span>
    ) : (
        renderInTransit(value)
    );

/*
 * Inv / Total Inventory renders as ONE figure. On a channel that reports inter-FC movement it
 * is on-hand PLUS units in flight between the channel's own warehouses — the server folds the
 * two before they get here (see `warehouse_transfer` on the response DTOs), because an in-flight
 * unit sits in no warehouse's ending balance and a total without it is short by exactly the
 * amount moving.
 *
 * The split is deliberately NOT on screen — no second column, no subtext. It survives only in
 * the CSV, where it has a column of its own, for anyone who needs to reconcile a figure against
 * an FC's physical position.
 */

/**
 * Column set for the DoH view, built outside the table so the toolbar's CSV export
 * can use the same definitions the grid renders. The export reads only `key`,
 * `csvHeader ?? header` and `csvValue`, but sharing the whole builder keeps one
 * source of truth for the channel groups rather than a parallel CSV-only list that
 * would drift as channels come and go.
 */
type DrrColumnArgs = {
    resp: DrrViewResponse | null;
    channels: { key: string; node_backed: boolean }[];
    rollupLabel: string | null;
    hiddenSet: Set<string>;
    showInventory: boolean;
    showInTransit: boolean;
    showStockTransfer: boolean;
    showDrr: boolean;
    showDoh: boolean;
    dohLabel: string;
    toDisplayDoh: (doh: number | null | undefined) => number | null;
    underStockLimit: number;
    overStockLimit: number;
    isNodeMode: boolean;
    isInternalPivot?: boolean;
    onChannelClick: (channelKey: string, row: DrrViewRow) => void;
    /** Opens the gate passes behind a Stock Transfer cell (bucket key, row). */
    onStockTransferClick?: (bucketKey: string, row: DrrViewRow) => void;
    /** Opens the shipments behind an In Transit / Scheduled cell (bucket key, leg, row). */
    onInTransitClick?: (bucketKey: string, leg: ChannelShipmentLeg, row: DrrViewRow) => void;
};

const buildDrrColumns = ({
    resp,
    channels,
    rollupLabel,
    hiddenSet,
    showInventory,
    showInTransit,
    showStockTransfer,
    showDrr,
    showDoh,
    dohLabel,
    toDisplayDoh,
    underStockLimit,
    overStockLimit,
    isNodeMode,
    isInternalPivot,
    onChannelClick,
    onStockTransferClick,
    onInTransitClick,
}: DrrColumnArgs): DataSheetColumn<DrrViewRow>[] => {
    /*
     * Which groups earn an In Transit column. The server names them (see `in_transit_buckets`)
     * because only it knows which warehouses carry the pivoted channel's abbreviation key —
     * so `internal` and `Unattributed` are absent here by construction, not by a label test
     * the grid would have to keep in sync with the backend's notion of "channel".
     */
    const inTransitBuckets = new Set(resp?.in_transit_buckets ?? []);
    /*
     * Which of those groups also earn a Scheduled column — the subset whose source can tell
     * "planned, not dispatched" from "dispatched, not received". For these In Transit holds
     * the dispatched remainder and the two columns sum to the single figure others show.
     */
    const scheduledBuckets = new Set(resp?.scheduled_buckets ?? []);
    /*
     * Which groups earn a Warehouse Transfer column. Declared separately from in-transit
     * because the two are different readings a channel may report independently: in-transit
     * is stock arriving from the brand, warehouse transfer is the channel moving stock it
     * already holds between its own nodes. Empty for every channel that does not report it.
     */
    const warehouseTransferBuckets = new Set(resp?.warehouse_transfer_buckets ?? []);
    /*
     * Which groups count Stock Transfer: its units are INSIDE `stock` (and DoH), so for these
     * groups Inv prints `stock − stock_transfer` — what is on the shelf — and the moving part
     * gets its own column. Internal sections only; the server names them.
     */
    const stockTransferBuckets = new Set(resp?.stock_transfer_buckets ?? []);
    // One identity column: product id over description (or group name over SKU
    // count). Two sticky columns for what is really one label wasted the width the
    // channel groups need.
    const cols: DataSheetColumn<DrrViewRow>[] = [
        {
            key: 'product_id',
            header: rollupLabel ?? 'Product ID',
            // The stacked second line (description / SKU count) becomes its own
            // column in structural exports, where subtext doesn't exist.
            captureDetailHeader: rollupLabel ? 'Details' : 'SKU Description',
            width: 260,
            sticky: true,
            // Sort/filter/CSV on the label actually shown — a rollup row's
            // product_id is the synthetic `group:<value>` key.
            valueAccessor: (row) => (row.subRows ? row.sku : row.product_id),
            csvValue: (_value, row) =>
                row.subRows ? row.sku : row.product_id,
            render: (_value, row) => (
                <span
                    style={{
                        display: 'inline-flex',
                        flexDirection: 'column',
                        lineHeight: 1.25,
                        minWidth: 0,
                    }}
                >
                    <span
                        style={
                            row.subRows
                                ? { fontWeight: 600 }
                                : {
                                      fontFamily:
                                          "'JetBrains Mono', 'Fira Code', ui-monospace, monospace",
                                      fontSize: 12,
                                      color: 'rgb(71, 85, 105)',
                                  }
                        }
                    >
                        {row.subRows ? row.sku : row.product_id}
                    </span>
                    <span
                        style={{
                            fontSize: 11,
                            color: dataSheetTokens.colors.textMuted,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {row.subRows
                            ? `${row.skuCount} SKU${row.skuCount === 1 ? '' : 's'}`
                            : row.name}
                    </span>
                </span>
            ),
        },
        {
            /*
             * Rendered as subtext inside the identity cell above, so it has no column of its
             * own on screen — but a spreadsheet has no subtext, and without this the download
             * carried bare product ids with no way to tell what they were.
             */
            key: 'name',
            header: 'SKU Description',
            csvOnly: true,
            csvValue: (_value, row) => (row.subRows ? '' : (row.name ?? '')),
        },
    ];
    if (!hiddenSet.has('category')) {
        cols.push({
            key: 'category',
            header: 'Category',
            section: 'entity' as const,
            render: (value) => value ?? '—',
        });
    }
    const pushRateColumns = (
        groupLabel: string,
        keyPrefix: string,
        getBucket: (row: DrrViewRow) => DrrBucket | undefined,
        options?: {
            clickChannelKey?: string;
            dohUnavailable?: boolean;
            /** Key this group answers to in `in_transit_buckets`. Omitted = never in-transit. */
            bucketKey?: string;
        },
    ) => {
        // Whether this bucket's Inv includes warehouse transfer. Drives the CSV column only —
        // on screen Inv is one figure either way.
        const includesTransfer = Boolean(
            options?.bucketKey && warehouseTransferBuckets.has(options.bucketKey),
        );
        const bucketKey = options?.bucketKey;
        const hasStockTransfer = Boolean(bucketKey && stockTransferBuckets.has(bucketKey));
        // On the shelf: `stock` minus the Stock Transfer it already contains.
        const onHand = (row: DrrViewRow): number | null => {
            const bucket = getBucket(row);
            if (bucket?.stock === null || bucket?.stock === undefined) return null;
            return hasStockTransfer ? bucket.stock - (bucket.stock_transfer ?? 0) : bucket.stock;
        };
        if (showInventory) {
            cols.push({
                key: `${keyPrefix}_inv`,
                header: 'Inv',
                csvHeader: `${groupLabel} Inv`,
                group: groupLabel,
                section: 'grouping' as const,
                align: 'right' as const,
                // 100, not the 84 its neighbours use: a six-figure stock with Indian grouping
                // (1,12,345) needs ~60px of digits inside 32px of cell padding, and 84 clipped it.
                width: 100,
                valueAccessor: (row) => onHand(row),
                csvValue: (_value, row) => onHand(row) ?? '',
                render: (_value, row) => (
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatStock(onHand(row))}
                    </span>
                ),
            });
            // The transfer half has no cell on screen, so without this the download carries an
            // Inv figure nobody could reconcile against the FC's physical position.
            if (includesTransfer) {
                cols.push({
                    key: `${keyPrefix}_whse_transfer_csv`,
                    header: 'Whse Transfer',
                    csvHeader: `${groupLabel} Whse Transfer (incl. in Inv)`,
                    group: groupLabel,
                    csvOnly: true,
                    csvValue: (_value, row) => getBucket(row)?.warehouse_transfer ?? '',
                });
            }
        }
        if (showStockTransfer && hasStockTransfer && bucketKey) {
            // A single internal warehouse's column says "to this warehouse"; a total says "between".
            const isWarehouseColumn = bucketKey !== 'overall' && bucketKey !== 'internal';
            cols.push({
                key: `${keyPrefix}_stock_transfer`,
                header: STOCK_TRANSFER_LABEL,
                renderHeader: () => (
                    <StockTransferHeader
                        {...(isWarehouseColumn
                            ? { tooltip: STOCK_TRANSFER_TO_WAREHOUSE_TOOLTIP }
                            : {})}
                    />
                ),
                csvHeader: `${groupLabel} ${STOCK_TRANSFER_LABEL}`,
                group: groupLabel,
                section: 'grouping' as const,
                align: 'right' as const,
                width: 96,
                valueAccessor: (row) => getBucket(row)?.stock_transfer ?? null,
                csvValue: (_value, row) => getBucket(row)?.stock_transfer ?? '',
                render: (_value, row) => {
                    const value = getBucket(row)?.stock_transfer;
                    // Rollup rows span many SKUs; the drawer is per SKU.
                    if (!onStockTransferClick || row.subRows || !value) {
                        return renderInTransit(value);
                    }
                    return (
                        <span
                            onClick={(event) => {
                                event.stopPropagation();
                                onStockTransferClick(bucketKey, row);
                            }}
                            title="View the stock transfers"
                            style={{
                                cursor: 'pointer',
                                textDecoration: 'underline dotted',
                                textUnderlineOffset: 3,
                            }}
                        >
                            {renderInTransit(value)}
                        </span>
                    );
                },
            });
        }
        if (showInTransit && options?.bucketKey && inTransitBuckets.has(options.bucketKey)) {
            cols.push({
                key: `${keyPrefix}_in_transit`,
                header: 'In Transit',
                csvHeader: `${groupLabel} In Transit`,
                group: groupLabel,
                section: 'grouping' as const,
                align: 'right' as const,
                width: 84,
                valueAccessor: (row) => getBucket(row)?.in_transit ?? null,
                csvValue: (_value, row) => getBucket(row)?.in_transit ?? '',
                render: (_value, row) =>
                    renderShipmentFigure(
                        getBucket(row)?.in_transit,
                        // Rollup rows span many SKUs; the drawer is per SKU.
                        onInTransitClick && !row.subRows && scheduledBuckets.has(bucketKey!)
                            ? () => onInTransitClick(bucketKey!, 'IN_TRANSIT', row)
                            : null,
                    ),
            });
        }
        // Same switch as In Transit: the two are one reading shown in two legs.
        if (showInTransit && options?.bucketKey && scheduledBuckets.has(options.bucketKey)) {
            cols.push({
                key: `${keyPrefix}_scheduled`,
                header: 'Scheduled',
                csvHeader: `${groupLabel} Scheduled`,
                group: groupLabel,
                section: 'grouping' as const,
                align: 'right' as const,
                width: 84,
                valueAccessor: (row) => getBucket(row)?.scheduled ?? null,
                csvValue: (_value, row) => getBucket(row)?.scheduled ?? '',
                render: (_value, row) =>
                    renderShipmentFigure(
                        getBucket(row)?.scheduled,
                        onInTransitClick && !row.subRows
                            ? () => onInTransitClick(bucketKey!, 'SCHEDULED', row)
                            : null,
                    ),
            });
        }
        // No Whse Transfer column: those units are inside Inv now. A column beside Inv read as
        // a figure to add on top of it, which double counted. CSV keeps the split.
        if (showDrr) cols.push({
            key: `${keyPrefix}_drr`,
            header: 'DRR',
            csvHeader: `${groupLabel} DRR`,
            group: groupLabel,
            section: 'grouping' as const,
            align: 'right' as const,
            width: 84,
            valueAccessor: (row) => getBucket(row)?.drr ?? 0,
            csvValue: (_value, row) => getBucket(row)?.drr ?? 0,
            render: (_value, row) => {
                const content = formatDrr(getBucket(row)?.drr ?? 0);
                // A rollup row spans many SKUs, so the per-node drill-in (which is
                // keyed on one product) doesn't apply to it.
                if (!options?.clickChannelKey || row.subRows) {
                    return (
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{content}</span>
                    );
                }
                return (
                    <span
                        onClick={(event) => {
                            event.stopPropagation();
                            onChannelClick(options.clickChannelKey!, row);
                        }}
                        title="View per-node breakdown"
                        style={{
                            cursor: 'pointer',
                            textDecoration: 'underline dotted',
                            textUnderlineOffset: 3,
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {content}
                    </span>
                );
            },
        });
        if (showDoh) cols.push({
            key: `${keyPrefix}_doh`,
            header: dohLabel,
            csvHeader: `${groupLabel} ${dohLabel}`,
            group: groupLabel,
            section: 'grouping' as const,
            align: 'right' as const,
            width: 84,
            valueAccessor: (row) =>
                options?.dohUnavailable
                    ? null
                    : (toDisplayDoh(getBucket(row)?.doh) ?? null),
            csvValue: (_value, row) =>
                options?.dohUnavailable
                    ? ''
                    : (toDisplayDoh(getBucket(row)?.doh) ?? ''),
            render: (_value, row) => {
                if (options?.dohUnavailable) return '—';
                return renderDohValue(
                    toDisplayDoh(getBucket(row)?.doh),
                    underStockLimit,
                    overStockLimit,
                );
            },
        });
    };
    const isOrPivot = isNodeMode && !!resp?.channel_is_or;
    // Per-brand opt-in, and only in a channel pivot. The server decides (it is the one that
    // knows the brand's config and whether it actually populated the figures), so the grid
    // keys off the response rather than re-deriving the rule. Excluded on the INTERNAL pivot
    // itself, where 'Internal Total' already leads.
    const showInternalInPivot =
        isNodeMode &&
        !isInternalPivot &&
        !!resp?.internal_included &&
        !hiddenSet.has('internal');
    // Leftmost here, unlike the Overall layout where it follows Overall: in a pivot the
    // question is "what am I holding myself" before "what is sitting at the channel".
    if (showInternalInPivot) {
        pushRateColumns('Internal', 'internal', (row) => row.internal, {
            clickChannelKey: 'INTERNAL',
            // The server lists 'internal' only for Stock Transfer here; in-transit and
            // warehouse transfer never name it in a pivot, so nothing else appears.
            bucketKey: 'internal',
        });
    }
    pushRateColumns(
        isNodeMode
            ? isOrPivot
                ? 'Channel Total (Primary)'
                : isInternalPivot
                  ? 'Internal Total'
                  : 'Channel Total'
            : 'Overall',
        'overall',
        (row) => row.overall,
        // The only fixed group the server ever names — 'Channel Total' in a channel pivot.
        // In the Overall layout and the Internal pivot it is simply not in the list, so the
        // column does not appear and no branch here decides that.
        { bucketKey: 'overall' },
    );
    // OR pivot: the channel total is PO-derived sell-in (primaries), so a
    // sell-out group (secondaries) sits beside it to expose the gap.
    if (isOrPivot) {
        pushRateColumns(
            'Channel Total (Secondary)',
            'channel_secondary',
            (row) => row.channel_secondary,
        );
    }
    if (!isNodeMode && !hiddenSet.has('internal')) {
        // Clickable like a node-backed channel: drills into the
        // per-internal-warehouse breakdown (INTERNAL sentinel pivot).
        pushRateColumns('Internal', 'internal', (row) => row.internal, {
            clickChannelKey: 'INTERNAL',
            // Named 'internal' by the server in the Overall layout only; the channel pivot
            // lists 'overall' and its nodes, so this key is simply absent there and no
            // column appears.
            bucketKey: 'internal',
        });
    }
    for (const channel of channels) {
        if (hiddenSet.has(`ch_${channel.key}`)) continue;
        pushRateColumns(
            channel.key,
            `ch_${channel.key}`,
            (row) => row.channels[channel.key],
            {
                // In node mode these keys are the channel's own nodes, which is exactly what
                // the server lists; in the Overall layout it lists nothing, so passing the key
                // unconditionally is safe and keeps the rule in one place.
                bucketKey: channel.key,
                ...(isNodeMode
                    ? channel.node_backed
                        ? undefined
                        : { dohUnavailable: true }
                    : channel.node_backed
                      ? { clickChannelKey: channel.key }
                      : { dohUnavailable: true }),
            },
        );
    }
    return cols;
};

/**
 * DRR View (inventory-drr-view-v1): per-SKU DRR + DoH at overall / internal /
 * per-channel grain. Channel columns are DataSheet column groups; node-backed
 * channels' DRR cells drill into the per-node breakdown.
 */
// Exported for the /email-render/table headless route — the scheduled email photographs
// THIS component, so any change to how the DoH view looks flows into emails automatically.
export const DrrViewTable: React.FC<{
    resp: DrrViewResponse | null;
    /** Rollup rows when a grouping is active; otherwise the response's SKU rows. */
    rows: DrrViewRow[];
    /** Label of the active rollup dimension; null when showing SKU rows. */
    rollupLabel: string | null;
    underStockLimit: number;
    overStockLimit: number;
    inventoryPeriod: 'DAY' | 'MONTH';
    hiddenColumns: string[];
    onChannelClick: (channelKey: string, row: DrrViewRow) => void;
    /** Opens the gate passes behind a Stock Transfer cell. Absent = cells are not clickable. */
    onStockTransferClick?: (bucketKey: string, row: DrrViewRow) => void;
    /** Opens the shipments behind an In Transit / Scheduled cell. Absent = cells are not clickable. */
    onInTransitClick?: (bucketKey: string, leg: ChannelShipmentLeg, row: DrrViewRow) => void;
    /** True when the node pivot shows internal warehouses (not a channel's nodes). */
    isInternalPivot?: boolean;
    /** The SKU column's break-down control — see the other three tables. */
    explosion?: React.ComponentProps<typeof DataSheet>['explosion'];
    /**
     * Every row the filters matched, before paging — what the Total row is computed over when
     * break-down is on.
     *
     * The server's `aggregate_row` is built from its full filtered set and deliberately NOT
     * from the page it returns (a page is a viewport, not a selection). The client-side
     * recompute has to use the same scope or the Total would silently become a page subtotal
     * the moment you switched break-down on, and would change as you paged.
     */
    aggregateSourceRows?: DrrViewRow[];
    /** The email composer's view-capture handle — see DataSheet's captureRef. */
    captureRef?: React.MutableRefObject<DataSheetCaptureHandle | null>;
}> = ({
    resp,
    rows,
    rollupLabel,
    underStockLimit,
    overStockLimit,
    inventoryPeriod,
    hiddenColumns,
    onChannelClick,
    onStockTransferClick,
    onInTransitClick,
    isInternalPivot,
    explosion,
    aggregateSourceRows,
    captureRef,
}) => {
    const hiddenSet = useMemo(() => new Set(hiddenColumns), [hiddenColumns]);
    const dohLabel = dohHeaderLabel(inventoryPeriod);
    const toDisplayDoh = toDisplayDohFor(inventoryPeriod);
    const channels = resp?.channels ?? [];
    const isNodeMode = resp?.mode === 'nodes';
    // One switch across every group: a third sub-column per channel widens the grid
    // by half again, so the compact DRR|DoH layout stays one click away.
    const showInventory = !hiddenSet.has('inv');
    // Its own switch rather than riding on `showInventory`: on a channel pivot with many FCs
    // a fourth sub-column per group is a lot of width, and in-transit is also the one figure
    // an operator may want without the stock column beside it.
    const showInTransit = !hiddenSet.has('inTransit');
    // Display only: hidden or not, the units stay inside Inv's stock and the DoH.
    const showStockTransfer = !hiddenSet.has(STOCK_TRANSFER_HIDE_KEY);
    // DRR and DoH get switches for the same reason: on a pivot with a dozen FCs that is four
    // sub-columns per group, and an operator reading stock positions wants the rate pair gone
    // rather than the whole channel.
    const showDrr = !hiddenSet.has('drr');
    const showDoh = !hiddenSet.has('doh');

    const columns = useMemo<DataSheetColumn<DrrViewRow>[]>(
        () =>
            buildDrrColumns({
                resp,
                channels,
                rollupLabel,
                hiddenSet,
                showInventory,
                showInTransit,
                showStockTransfer,
                showDrr,
                showDoh,
                dohLabel,
                toDisplayDoh,
                underStockLimit,
                overStockLimit,
                isNodeMode,
                isInternalPivot,
                onChannelClick,
                onStockTransferClick,
                onInTransitClick,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [channels, hiddenSet, showInventory, showInTransit, showStockTransfer, onStockTransferClick, onInTransitClick, resp?.stock_transfer_buckets, showDrr, showDoh, dohLabel, underStockLimit, overStockLimit, inventoryPeriod, onChannelClick, isNodeMode, resp?.channel_is_or, resp?.internal_included, resp?.in_transit_buckets, resp?.scheduled_buckets, resp?.warehouse_transfer_buckets, rollupLabel, isInternalPivot],
    );

    const aggregateRow = useMemo(() => {
        /*
         * The server's totals row describes the UN-exploded set, so once break-down is on it
         * contradicts every row beneath it — a Total that does not match its own column is
         * worse than no Total. Recomputed from the rows on screen instead, bucket by bucket,
         * with cover re-derived from the sums exactly as `aggregateDrrBuckets` does.
         */
        const totals: Record<string, DrrBucket | undefined> | undefined =
            explosion?.enabled
                ? (() => {
                      const flat = leafRowsOf(aggregateSourceRows ?? rows);
                      const keys = new Set<string>();
                      flat.forEach((row) =>
                          Object.keys(row.channels).forEach((key) => keys.add(key)),
                      );
                      return {
                          overall: aggregateDrrBuckets(flat.map((r) => r.overall)),
                          internal: aggregateDrrBuckets(flat.map((r) => r.internal)),
                          channel_secondary: flat.some((r) => r.channel_secondary)
                              ? aggregateDrrBuckets(
                                    flat.map((r) => r.channel_secondary),
                                )
                              : undefined,
                          ...Object.fromEntries(
                              [...keys].map((key) => [
                                  key,
                                  aggregateDrrBuckets(
                                      flat.map((r) => r.channels[key]),
                                  ),
                              ]),
                          ),
                      };
                  })()
                : resp?.aggregate_row;
        if (!totals) return undefined;
        const out: Record<string, React.ReactNode> = { product_id: 'Total' };
        const stockTransferKeys = new Set(resp?.stock_transfer_buckets ?? []);
        const fill = (prefix: string, bucket?: DrrBucket, bucketKey?: string) => {
            if (!bucket) return;
            // Same on-the-shelf rule as the Inv cells: stock minus the Stock Transfer inside it.
            const hasStockTransfer = !!bucketKey && stockTransferKeys.has(bucketKey);
            out[`${prefix}_stock_transfer`] = hasStockTransfer
                ? renderInTransit(bucket.stock_transfer)
                : undefined;
            if (showInventory) {
                out[`${prefix}_inv`] = formatStock(
                    hasStockTransfer && bucket.stock != null
                        ? bucket.stock - (bucket.stock_transfer ?? 0)
                        : bucket.stock,
                );
                // Feeds the CSV-only split column; no on-screen cell answers to this key.
                out[`${prefix}_whse_transfer_csv`] = renderInTransit(bucket.warehouse_transfer);
            }
            // Keyed on the column existing, not on the value: a group with no In Transit
            // column simply has no such key, and DataSheet ignores extras either way.
            out[`${prefix}_in_transit`] = renderInTransit(bucket.in_transit);
            out[`${prefix}_scheduled`] = renderInTransit(bucket.scheduled);
            if (showDrr) out[`${prefix}_drr`] = formatDrr(bucket.drr);
            if (showDoh)
                out[`${prefix}_doh`] =
                    bucket.doh == null ? 'NA' : (toDisplayDoh(bucket.doh) ?? 0).toFixed(1);
        };
        fill('overall', totals.overall, 'overall');
        if (isNodeMode && resp?.channel_is_or) {
            fill('channel_secondary', totals.channel_secondary);
        }
        // Matches the column rule above: Internal is in the totals row exactly when it is a
        // group on screen — the Overall layout always, a channel pivot only when the brand
        // opted in and the server populated it.
        if (!isNodeMode || (!isInternalPivot && resp?.internal_included)) {
            fill('internal', totals.internal, 'internal');
        }
        for (const channel of channels) {
            fill(`ch_${channel.key}`, totals[channel.key], channel.key);
        }
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resp, rows, aggregateSourceRows, explosion?.enabled, channels, isNodeMode, isInternalPivot, inventoryPeriod, showInventory]);

    return (
        <DataSheet
            data={rows}
            columns={columns}
            getRowKey={(row) => row.product_id}
            getSubRows={(row) => row.subRows}
            explosion={explosion}
            aggregateRow={aggregateRow}
            captureRef={captureRef}
            bare
            hideHeaderBar
            // Lets the page own the scrolling instead of wrapping the grid in its
            // own overflow container. Without it the sticky header + totals row
            // anchor to a wrapper that is as tall as the content, so they scroll
            // off with the page. The other three inventory tables do the same.
            maxHeight="auto"
        />
    );
};

const FinishedGoodsSnapshotTable: React.FC<{
    rows: FinishedGoodsSnapshotRow[];
    underStockLimit: number;
    overStockLimit: number;
    inventoryPeriod: 'DAY' | 'MONTH';
    hiddenColumns: string[];
    attrColumnDefs?: { key: string; label: string }[];
    attrValuesByProduct?: Map<string, Record<string, string>>;
    visibleAttrKeys?: string[];
    backendAggregateRow?: {
        quantity: number;
        on_hold_stock?: number | null;
        in_transit?: number | null;
        scheduled?: number | null;
        warehouse_transfer?: number | null;
        consumption_per_day?: number;
        consumption_per_month?: number;
        days_on_hand?: number;
        months_on_hand?: number;
        groupings: Record<
            string,
            {
                quantity: number;
                on_hold_stock?: number | null;
                in_transit?: number | null;
                scheduled?: number | null;
                warehouse_transfer?: number | null;
            }
        >;
    } | null;
    columnSort?: { column: string; direction: 'asc' | 'desc' } | null;
    onColumnSortChange?: (
        key: string | null,
        direction: 'asc' | 'desc' | null,
    ) => void;
    /** Label of the active rollup dimension; null when showing SKU rows. */
    rollupLabel?: string | null;
    /**
     * The SKU column's break-down control. Produced by `useSkuExplosion` in the container,
     * which also supplies the exploded `rows` — DataSheet renders the toggle, the container
     * owns the arithmetic.
     */
    explosion?: React.ComponentProps<typeof DataSheet>['explosion'];
    /**
     * Every row the filters matched, before paging — the scope the Total row uses once
     * break-down is on, matching what the server's own totals cover.
     */
    aggregateSourceRows?: FinishedGoodsSnapshotRow[];
    /**
     * Column keys the server says carry an in-transit reading — `'overall'` for Total
     * Inventory, plus one grouping key per channel warehouse. Absent outside a channel scope.
     */
    inTransitBucketKeys?: string[];
    /**
     * Subset of `inTransitBucketKeys` whose reading is split into Scheduled + In Transit, so a
     * Scheduled sub-column appears beside In Transit for exactly these.
     */
    scheduledBucketKeys?: string[];
    /**
     * Column keys the server says carry a warehouse-transfer reading, same contract as
     * `inTransitBucketKeys`. Absent for every channel that does not report the figure, which
     * is what keeps the column off the grid for merchants who are not on the feature.
     */
    warehouseTransferBucketKeys?: string[];
    /** Opens the gate passes behind a SKU's Stock Transfer cell. */
    onStockTransferClick?: (row: FinishedGoodsSnapshotRow) => void;
    /**
     * Opens the shipments behind an In Transit / Scheduled cell: 'overall' for the Total
     * columns, else the warehouse column's key.
     */
    onInTransitClick?: (
        bucketKey: string,
        leg: ChannelShipmentLeg,
        row: FinishedGoodsSnapshotRow,
    ) => void;
}> = ({
    rows,
    underStockLimit,
    overStockLimit,
    inventoryPeriod,
    hiddenColumns,
    attrColumnDefs,
    attrValuesByProduct,
    visibleAttrKeys = [],
    backendAggregateRow,
    columnSort,
    onColumnSortChange,
    rollupLabel,
    explosion,
    aggregateSourceRows,
    inTransitBucketKeys,
    scheduledBucketKeys,
    warehouseTransferBucketKeys,
    onStockTransferClick,
    onInTransitClick,
}) => {
    const isRolledUp = !!rollupLabel;
    const hiddenSet = useMemo(() => new Set(hiddenColumns), [hiddenColumns]);
    const showOnHold = !hiddenSet.has('onHoldStock');
    /*
     * Which columns earn an In Transit sub-column. The server names them (`in_transit_buckets`)
     * rather than the grid inferring "is this a channel warehouse" from a name — only the
     * backend knows which warehouses carry the selected channel's abbreviation key, and in the
     * Overall layout the same column list mixes internal warehouses with channel rollups.
     */
    const inTransitBuckets = useMemo(
        () => new Set(inTransitBucketKeys ?? []),
        [inTransitBucketKeys],
    );
    const showInTransit = !hiddenSet.has('inTransit');
    const showTotalInTransit =
        showInTransit &&
        !hiddenSet.has('totalInventory') &&
        inTransitBuckets.has('overall');
    // Subset of in-transit, under the same switch: one reading shown in two legs.
    const scheduledBuckets = useMemo(
        () => new Set(scheduledBucketKeys ?? []),
        [scheduledBucketKeys],
    );
    const showTotalScheduled = showTotalInTransit && scheduledBuckets.has('overall');
    // Declared independently of in-transit: a channel can report one, the other, or both.
    const warehouseTransferBuckets = useMemo(
        () => new Set(warehouseTransferBucketKeys ?? []),
        [warehouseTransferBucketKeys],
    );
    const showTotalWarehouseTransfer =
        !hiddenSet.has('totalInventory') && warehouseTransferBuckets.has('overall');

    // Get unique warehouse/channel columns from data.
    // Internal warehouses (alphabetical) come first, then channel rollups (alphabetical).
    const warehouseColumns = useMemo(() => orderSnapshotLocations(rows), [rows]);

    // Build columns dynamically — section tags let DataSheet enforce order:
    // sticky → entity → metric → grouping
    const columns = useMemo<DataSheetColumn<FinishedGoodsSnapshotRow>[]>(() => {
        const baseColumns: DataSheetColumn<FinishedGoodsSnapshotRow>[] = [
            {
                key: 'productId',
                header: rollupLabel ?? 'Product ID',
                width: 160,
                sticky: true,
                sortable: true,
                // Sort/filter/CSV on the label actually shown — a rollup row's
                // productId is the synthetic `group:<value>` key.
                valueAccessor: (row) =>
                    isGroupRow(row) ? row.skuCode : row.productId,
                csvValue: (_value, row) =>
                    isGroupRow(row) ? row.skuCode : row.productId,
                render: (value, row) =>
                    isGroupRow(row) ? (
                        <GroupNameCell row={row} />
                    ) : (
                        <span
                            style={{
                                fontFamily:
                                    "'JetBrains Mono', 'Fira Code', ui-monospace, monospace",
                                fontSize: 12,
                                color: 'rgb(71, 85, 105)',
                            }}
                        >
                            {value}
                        </span>
                    ),
            },
            {
                key: 'description',
                header: 'SKU Description',
                width: 280,
                sticky: true,
                sortable: true,
            },
            ...(!hiddenSet.has('totalInventory')
                ? [
                      {
                          key: 'totalInventory',
                          header: 'Total Inventory',
                          sortable: true,
                          section: 'metric' as const,
                          align: 'right' as const,
                          width: 120,
                          render: (value: any) => (
                              <span
                                  style={{
                                      fontWeight: 600,
                                      fontVariantNumeric: 'tabular-nums',
                                  }}
                              >
                                  {value != null ? formatCount(value) : '—'}
                              </span>
                          ),
                      },
                  ]
                : []),
            // Channel scope only, and the server says so — 'overall' is its key for the
            // Total Inventory column, the same convention the DoH grid uses.
            ...(showTotalInTransit
                ? [
                      {
                          key: 'inTransit',
                          header: 'In Transit',
                          sortable: true,
                          section: 'metric' as const,
                          align: 'right' as const,
                          width: 100,
                          valueAccessor: (row: FinishedGoodsSnapshotRow) =>
                              row.inTransit ?? null,
                          csvValue: (
                              _value: unknown,
                              row: FinishedGoodsSnapshotRow,
                          ) => row.inTransit ?? '',
                          render: (
                              _value: unknown,
                              row: FinishedGoodsSnapshotRow,
                          ) =>
                              renderShipmentFigure(
                                  row.inTransit,
                                  // Rollup rows span many SKUs; the drawer is per SKU.
                                  onInTransitClick && !row.subRows && scheduledBuckets.has('overall')
                                      ? () => onInTransitClick('overall', 'IN_TRANSIT', row)
                                      : null,
                              ),
                      },
                  ]
                : []),
            ...(showTotalScheduled
                ? [
                      {
                          key: 'scheduled',
                          header: 'Scheduled',
                          sortable: true,
                          section: 'metric' as const,
                          align: 'right' as const,
                          width: 100,
                          valueAccessor: (row: FinishedGoodsSnapshotRow) =>
                              row.scheduled ?? null,
                          csvValue: (
                              _value: unknown,
                              row: FinishedGoodsSnapshotRow,
                          ) => row.scheduled ?? '',
                          render: (
                              _value: unknown,
                              row: FinishedGoodsSnapshotRow,
                          ) =>
                              renderShipmentFigure(
                                  row.scheduled,
                                  onInTransitClick && !row.subRows
                                      ? () => onInTransitClick('overall', 'SCHEDULED', row)
                                      : null,
                              ),
                      },
                  ]
                : []),
            // No column on screen: these units are inside Total Inventory and show as its
            // `A + B` subtext. Kept as a CSV-only field so the download can still be
            // reconciled against an FC's physical position, which subtext cannot survive.
            ...(showTotalWarehouseTransfer
                ? [
                      {
                          key: 'warehouseTransfer',
                          header: 'Whse Transfer',
                          csvHeader: 'Whse Transfer (incl. in Total Inventory)',
                          csvOnly: true,
                          csvValue: (
                              _value: unknown,
                              row: FinishedGoodsSnapshotRow,
                          ) => row.warehouseTransfer ?? '',
                      },
                  ]
                : []),
            ...(showOnHold
                ? [
                      {
                          key: 'onHoldStock',
                          header: 'On Hold Stock',
                          sortable: true,
                          section: 'metric' as const,
                          align: 'right' as const,
                          width: 110,
                          render: (_: any, row: FinishedGoodsSnapshotRow) => (
                              <span
                                  style={{
                                      fontVariantNumeric: 'tabular-nums',
                                      color: 'rgb(71, 85, 105)',
                                  }}
                              >
                                  {row.onHoldStock !== null &&
                                  row.onHoldStock !== undefined
                                      ? formatCount(row.onHoldStock)
                                      : '—'}
                              </span>
                          ),
                      },
                  ]
                : []),
            ...(!hiddenSet.has('daysOnHand')
                ? [
                      {
                          key: 'daysOnHand',
                          header: inventoryPeriod === 'MONTH' ? 'MoH' : 'DoH',
                          sortable: true,
                          section: 'metric' as const,
                          align: 'right' as const,
                          width: 80,
                          render: (_: any, row: FinishedGoodsSnapshotRow) =>
                              renderDohValue(
                                  row.daysOnHand,
                                  underStockLimit,
                                  overStockLimit,
                              ),
                      },
                  ]
                : []),
        ];

        // Entity-level product metadata columns (section: 'entity' → DataSheet places after sticky)
        const productMetaCols: DataSheetColumn<FinishedGoodsSnapshotRow>[] = [];
        if (!hiddenSet.has('category')) {
            productMetaCols.push({
                key: 'category',
                header: 'Category',
                width: 120,
                section: 'entity',
                render: (_: any, row: FinishedGoodsSnapshotRow) => (
                    <span>{row.category ?? '—'}</span>
                ),
            });
        }
        if (!hiddenSet.has('tier')) {
            productMetaCols.push({
                key: 'tier',
                header: 'Tier',
                width: 100,
                section: 'entity',
                render: (_: any, row: FinishedGoodsSnapshotRow) => (
                    <span>{row.tier ?? '—'}</span>
                ),
            });
        }
        if (!hiddenSet.has('productGroup')) {
            productMetaCols.push({
                key: 'productGroup',
                header: 'Product Group',
                width: 140,
                section: 'entity',
                render: (_: any, row: FinishedGoodsSnapshotRow) => (
                    <span>{row.productGroup ?? '—'}</span>
                ),
            });
        }
        if (!hiddenSet.has('isActive')) {
            productMetaCols.push({
                key: 'isActive',
                header: 'Active',
                width: 80,
                section: 'entity',
                render: (_: any, row: FinishedGoodsSnapshotRow) => (
                    <span
                        style={{
                            color: row.isActive
                                ? 'rgb(34, 197, 94)'
                                : 'rgb(239, 68, 68)',
                        }}
                    >
                        {row.isActive === null || row.isActive === undefined
                            ? '—'
                            : row.isActive
                              ? 'Yes'
                              : 'No'}
                    </span>
                ),
            });
        }

        // Dynamic entity attribute columns — only render those explicitly enabled by admin
        const attrCols: DataSheetColumn<FinishedGoodsSnapshotRow>[] = (
            attrColumnDefs ?? []
        )
            .filter((def) => visibleAttrKeys.includes(def.key))
            .map((def) => ({
                key: def.key,
                header: def.label,
                width: 140,
                section: 'entity' as const,
                render: (_: any, row: FinishedGoodsSnapshotRow) => {
                    const val = attrValuesByProduct?.get(row.productId)?.[
                        def.key
                    ];
                    return <span>{val ?? '—'}</span>;
                },
            }));

        // Dynamic warehouse columns (section: 'grouping' → always last)
        // When showOnHold: WH name becomes a group header with "Total Inv" + "On Hold" sub-columns.
        // Channel rollup columns get an " Inventory" suffix so they're visually distinct from internal warehouses.
        const warehouseCols: DataSheetColumn<FinishedGoodsSnapshotRow>[] = [];
        warehouseColumns.forEach(({ name, isChannel, isStockTransfer }) => {
            const displayHeader = isChannel ? `${name} Inventory` : name;
            const cellFor = (row: FinishedGoodsSnapshotRow) =>
                row.warehouses.find((w) => w.name === name);
            // Stock Transfer is a location of its own (already inside Total Inventory) with no
            // on-hold or in-transit reading. Hiding it changes the view, never the total.
            if (isStockTransfer) {
                if (hiddenSet.has(STOCK_TRANSFER_HIDE_KEY)) return;
                warehouseCols.push({
                    key: `warehouse_${name}`,
                    header: STOCK_TRANSFER_LABEL,
                    renderHeader: () => <StockTransferHeader />,
                    csvHeader: STOCK_TRANSFER_LABEL,
                    sortable: true,
                    section: 'grouping',
                    align: 'right' as const,
                    width: 130,
                    render: (_: any, row: FinishedGoodsSnapshotRow) => {
                        const quantity = cellFor(row)?.quantity ?? 0;
                        // Rollup rows span many SKUs; the drawer is per SKU.
                        if (!onStockTransferClick || row.subRows || !quantity) {
                            return (
                                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                                    {formatCount(quantity)}
                                </span>
                            );
                        }
                        return (
                            <span
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onStockTransferClick(row);
                                }}
                                title="View the stock transfers"
                                style={{
                                    fontVariantNumeric: 'tabular-nums',
                                    cursor: 'pointer',
                                    textDecoration: 'underline dotted',
                                    textUnderlineOffset: 3,
                                }}
                            >
                                {formatCount(quantity)}
                            </span>
                        );
                    },
                });
                return;
            }
            // Per column, decided by the server: only warehouses carrying the selected
            // channel's abbreviation key are in the list, so internal columns in the Overall
            // layout never grow one.
            const showWhInTransit = showInTransit && inTransitBuckets.has(name);
            const showWhScheduled = showWhInTransit && scheduledBuckets.has(name);
            // Warehouse transfer adds no sub-column — it is inside this warehouse's quantity
            // and prints as its `A + B` subtext — so it does NOT make the column a group.
            const showWhTransfer = warehouseTransferBuckets.has(name);
            const isGrouped = showOnHold || showWhInTransit;
            warehouseCols.push({
                key: `warehouse_${name}`,
                header: isGrouped ? 'Total Inv' : displayHeader,
                ...(isGrouped
                    ? { group: displayHeader }
                    : {
                          // Display-only: underscores become spaces so single-token names
                          // (zepto_Ahmedabad) wrap at word boundaries inside the 100px column;
                          // `header` keeps the raw name for CSV export and AI table views.
                          renderHeader: () => displayHeader.replace(/_/g, ' '),
                      }),
                csvHeader: `${displayHeader} Inv`,
                sortable: true,
                section: 'grouping',
                align: 'right' as const,
                // Sized so the longest common city token (~104px) plus cell padding
                // and the sort caret fits on one line at the 11px header font.
                width: isGrouped ? 90 : 150,
                render: (_: any, row: FinishedGoodsSnapshotRow) => (
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatCount((cellFor(row)?.quantity ?? 0))}
                    </span>
                ),
            });
            if (showWhInTransit) {
                warehouseCols.push({
                    key: `warehouse_${name}_inTransit`,
                    header: 'In Transit',
                    csvHeader: `${displayHeader} In Transit`,
                    sortable: true,
                    group: displayHeader,
                    section: 'grouping',
                    align: 'right' as const,
                    width: 90,
                    valueAccessor: (row: FinishedGoodsSnapshotRow) =>
                        cellFor(row)?.inTransit ?? null,
                    csvValue: (_: any, row: FinishedGoodsSnapshotRow) =>
                        cellFor(row)?.inTransit ?? '',
                    render: (_: any, row: FinishedGoodsSnapshotRow) =>
                        renderShipmentFigure(
                            cellFor(row)?.inTransit,
                            onInTransitClick && !row.subRows && showWhScheduled
                                ? () => onInTransitClick(name, 'IN_TRANSIT', row)
                                : null,
                        ),
                });
            }
            if (showWhScheduled) {
                warehouseCols.push({
                    key: `warehouse_${name}_scheduled`,
                    header: 'Scheduled',
                    csvHeader: `${displayHeader} Scheduled`,
                    sortable: true,
                    group: displayHeader,
                    section: 'grouping',
                    align: 'right' as const,
                    width: 90,
                    valueAccessor: (row: FinishedGoodsSnapshotRow) =>
                        cellFor(row)?.scheduled ?? null,
                    csvValue: (_: any, row: FinishedGoodsSnapshotRow) =>
                        cellFor(row)?.scheduled ?? '',
                    render: (_: any, row: FinishedGoodsSnapshotRow) =>
                        renderShipmentFigure(
                            cellFor(row)?.scheduled,
                            onInTransitClick && !row.subRows
                                ? () => onInTransitClick(name, 'SCHEDULED', row)
                                : null,
                        ),
                });
            }
            // CSV only — on screen it is the subtext under this warehouse's Inv.
            if (showWhTransfer) {
                warehouseCols.push({
                    key: `warehouse_${name}_warehouseTransfer`,
                    header: 'Whse Transfer',
                    csvHeader: `${displayHeader} Whse Transfer (incl. in Inv)`,
                    csvOnly: true,
                    ...(isGrouped ? { group: displayHeader } : {}),
                    csvValue: (_: any, row: FinishedGoodsSnapshotRow) =>
                        cellFor(row)?.warehouseTransfer ?? '',
                });
            }
            if (showOnHold) {
                warehouseCols.push({
                    key: `warehouse_${name}_onHold`,
                    header: 'On Hold',
                    sortable: true,
                    group: displayHeader,
                    section: 'grouping',
                    align: 'right' as const,
                    width: 80,
                    render: (_: any, row: FinishedGoodsSnapshotRow) => {
                        const onHold = cellFor(row)?.onHoldStock;
                        return (
                            <span
                                style={{
                                    fontVariantNumeric: 'tabular-nums',
                                    color: 'rgb(107, 114, 128)',
                                }}
                            >
                                {onHold !== null && onHold !== undefined
                                    ? formatCount(onHold)
                                    : '—'}
                            </span>
                        );
                    },
                });
            }
        });

        return [
            ...baseColumns,
            ...productMetaCols,
            ...attrCols,
            ...warehouseCols,
        ];
    }, [
        warehouseColumns,
        onStockTransferClick,
        onInTransitClick,
        underStockLimit,
        overStockLimit,
        inventoryPeriod,
        hiddenSet,
        showOnHold,
        showInTransit,
        showTotalInTransit,
        inTransitBuckets,
        showTotalScheduled,
        scheduledBuckets,
        showTotalWarehouseTransfer,
        warehouseTransferBuckets,
        attrColumnDefs,
        attrValuesByProduct,
        visibleAttrKeys,
        rollupLabel,
    ]);

    const aggregateRow = useMemo(() => {
        // ReactNode, not string: the In Transit totals render through the same muted helper
        // their column does, so the Total row reads on the same terms as the cells above it.
        const result: Record<string, React.ReactNode> = {};
        /** Sums a nullable reading, staying null when nothing reported one. */
        const sumNullable = <T,>(
            items: T[],
            pick: (item: T) => number | null | undefined,
        ): number | null =>
            items.some((item) => pick(item) !== null && pick(item) !== undefined)
                ? items.reduce((sum, item) => sum + (pick(item) ?? 0), 0)
                : null;
        // The backend total describes the UN-exploded set, so once break-down is on it would
        // contradict the rows under it. The client-side branch below already sums the rows on
        // screen, which is exactly what is wanted here.
        if (backendAggregateRow && !explosion?.enabled) {
            // Use backend-computed totals (across ALL matching products, not just current page)
            if (!hiddenSet.has('totalInventory')) {
                result.totalInventory = formatCount(Math.floor(
                    backendAggregateRow.quantity,
                ));
            }
            if (showTotalInTransit) {
                result.inTransit = renderInTransit(backendAggregateRow.in_transit);
            }
            if (showTotalScheduled) {
                result.scheduled = renderInTransit(backendAggregateRow.scheduled);
            }
            if (showTotalWarehouseTransfer) {
                // Feeds the CSV-only column; no on-screen cell answers to this key any more.
                result.warehouseTransfer = renderInTransit(
                    backendAggregateRow.warehouse_transfer,
                );
            }
            if (showOnHold) {
                result.onHoldStock = formatCount((
                    backendAggregateRow.on_hold_stock ?? 0
                ));
            }
            warehouseColumns.forEach(({ name }) => {
                const grouping = backendAggregateRow.groupings[name];
                result[`warehouse_${name}`] = formatCount(Math.floor(
                    grouping?.quantity ?? 0,
                ));
                if (showInTransit && inTransitBuckets.has(name)) {
                    result[`warehouse_${name}_inTransit`] = renderInTransit(
                        grouping?.in_transit,
                    );
                    if (scheduledBuckets.has(name)) {
                        result[`warehouse_${name}_scheduled`] = renderInTransit(
                            grouping?.scheduled,
                        );
                    }
                }
                if (warehouseTransferBuckets.has(name)) {
                    result[`warehouse_${name}_warehouseTransfer`] = renderInTransit(
                        grouping?.warehouse_transfer,
                    );
                }
                if (showOnHold) {
                    result[`warehouse_${name}_onHold`] = formatCount((
                        grouping?.on_hold_stock ?? 0
                    ));
                }
            });
        } else {
            // Leaves of the whole filtered set — same scope the server's own totals use.
            const source = leafRowsOf(aggregateSourceRows ?? rows);
            if (!hiddenSet.has('totalInventory')) {
                result.totalInventory = formatCount(source
                    .reduce((sum, row) => sum + (row.totalInventory || 0), 0));
            }
            if (showTotalInTransit) {
                result.inTransit = renderInTransit(
                    sumNullable(source, (row) => row.inTransit),
                );
            }
            if (showTotalScheduled) {
                result.scheduled = renderInTransit(
                    sumNullable(source, (row) => row.scheduled),
                );
            }
            if (showTotalWarehouseTransfer) {
                result.warehouseTransfer = renderInTransit(
                    sumNullable(source, (row) => row.warehouseTransfer),
                );
            }
            if (showOnHold) {
                result.onHoldStock = formatCount(source
                    .reduce((sum, row) => sum + (row.onHoldStock ?? 0), 0));
            }
            warehouseColumns.forEach(({ name }) => {
                const cells = source.map((row) =>
                    row.warehouses.find((w) => w.name === name),
                );
                result[`warehouse_${name}`] = formatCount(cells
                    .reduce((sum, wh) => sum + (wh?.quantity || 0), 0));
                if (showInTransit && inTransitBuckets.has(name)) {
                    result[`warehouse_${name}_inTransit`] = renderInTransit(
                        sumNullable(cells, (wh) => wh?.inTransit),
                    );
                    if (scheduledBuckets.has(name)) {
                        result[`warehouse_${name}_scheduled`] = renderInTransit(
                            sumNullable(cells, (wh) => wh?.scheduled),
                        );
                    }
                }
                if (warehouseTransferBuckets.has(name)) {
                    result[`warehouse_${name}_warehouseTransfer`] = renderInTransit(
                        sumNullable(cells, (wh) => wh?.warehouseTransfer),
                    );
                }
                if (showOnHold) {
                    result[`warehouse_${name}_onHold`] = formatCount(cells
                        .reduce((sum, wh) => sum + (wh?.onHoldStock ?? 0), 0));
                }
            });
        }
        return result;
    }, [rows, aggregateSourceRows, warehouseColumns, showOnHold, showInTransit, showTotalInTransit, inTransitBuckets, showTotalScheduled, scheduledBuckets, showTotalWarehouseTransfer, warehouseTransferBuckets, hiddenSet, backendAggregateRow, explosion?.enabled]);

    return (
        <DataSheet<FinishedGoodsSnapshotRow>
            data={rows}
            columns={columns}
            aggregateRow={aggregateRow}
            bare
            hideHeaderBar
            maxHeight="auto"
            getRowKey={(row) => row.productId}
            getSubRows={(row) => row.subRows}
            explosion={explosion}
            emptyState="No snapshot data available"
            // Server-side sort can only order SKUs, so when the rows on screen
            // are groups the header sort falls back to DataSheet's client-side
            // sort (the server sort still orders SKUs within each group).
            {...(isRolledUp
                ? {}
                : {
                      controlledSortKey: columnSort?.column ?? null,
                      controlledSortDirection: columnSort?.direction ?? null,
                      onSortChange: onColumnSortChange,
                  })}
        />
    );
};

/* ------------------------------------------------------------------ */
/*  Planning view table - Using DataSheet                             */
/* ------------------------------------------------------------------ */

const FinishedGoodsPlanningTable: React.FC<{
    rows: FinishedGoodsPlanningRow[];
    onOpeningStockClick: (
        row: FinishedGoodsPlanningRow,
        weekIndex: number,
    ) => void;
    underStockLimit: number;
    overStockLimit: number;
    inventoryPeriod: 'DAY' | 'MONTH';
    hiddenColumns: string[];
    attrColumnDefs?: { key: string; label: string }[];
    attrValuesByProduct?: Map<string, Record<string, string>>;
    visibleAttrKeys?: string[];
    /** Label of the active rollup dimension; null when showing SKU rows. */
    rollupLabel?: string | null;
    /**
     * The SKU column's break-down control. Produced by `useSkuExplosion` in the container,
     * which also supplies the exploded `rows` — DataSheet renders the toggle, the container
     * owns the arithmetic.
     */
    explosion?: React.ComponentProps<typeof DataSheet>['explosion'];
    /**
     * Every row the filters matched, before paging — the scope the Total row is summed over.
     * See `leafRowsOf`.
     */
    aggregateSourceRows?: FinishedGoodsPlanningRow[];
    /**
     * The server's Total row, summed over every SKU the filters matched before the page was
     * cut. Wins over the client-side sum whenever it is set: when the grid holds a server-cut
     * page, `rows` is a viewport and summing it would make the Total a page subtotal that
     * changes as you page. The container leaves it unset when the client already holds the
     * whole set (rollup / break-down fetches), where the client sum is the right scope.
     */
    backendAggregateRow?: WarehouseStockPlanningAggregateRow | null;
}> = ({
    rows,
    onOpeningStockClick,
    underStockLimit,
    overStockLimit,
    inventoryPeriod,
    hiddenColumns,
    attrColumnDefs,
    attrValuesByProduct,
    visibleAttrKeys = [],
    rollupLabel,
    explosion,
    aggregateSourceRows,
    backendAggregateRow,
}) => {
    const hiddenSet = useMemo(() => new Set(hiddenColumns), [hiddenColumns]);
    const weekHeaders = rows.length > 0 ? rows[0].weeks : [];

    // Build columns dynamically including week-based columns
    const columns = useMemo<DataSheetColumn<FinishedGoodsPlanningRow>[]>(() => {
        const baseColumns: DataSheetColumn<FinishedGoodsPlanningRow>[] = [
            {
                key: 'productId',
                header: rollupLabel ?? 'Product ID',
                width: 160,
                sticky: true,
                valueAccessor: (row) =>
                    isGroupRow(row) ? row.skuCode : row.productId,
                csvValue: (_value, row) =>
                    isGroupRow(row) ? row.skuCode : row.productId,
                render: (value, row) =>
                    isGroupRow(row) ? (
                        <GroupNameCell row={row} />
                    ) : (
                        <span
                            style={{
                                fontFamily:
                                    "'JetBrains Mono', 'Fira Code', ui-monospace, monospace",
                                fontSize: 12,
                                color: 'rgb(71, 85, 105)',
                            }}
                        >
                            {value}
                        </span>
                    ),
            },
            {
                key: 'description',
                header: 'SKU Description',
                width: 280,
                sticky: true,
            },
        ];

        // Entity-level product metadata columns (section: 'entity' → DataSheet places after sticky)
        const productMetaCols: DataSheetColumn<FinishedGoodsPlanningRow>[] = [];
        if (!hiddenSet.has('category')) {
            productMetaCols.push({
                key: 'category',
                header: 'Category',
                width: 120,
                section: 'entity',
                render: (_: any, row: FinishedGoodsPlanningRow) => (
                    <span>{row.category ?? '—'}</span>
                ),
            });
        }
        if (!hiddenSet.has('tier')) {
            productMetaCols.push({
                key: 'tier',
                header: 'Tier',
                width: 100,
                section: 'entity',
                render: (_: any, row: FinishedGoodsPlanningRow) => (
                    <span>{row.tier ?? '—'}</span>
                ),
            });
        }
        if (!hiddenSet.has('productGroup')) {
            productMetaCols.push({
                key: 'productGroup',
                header: 'Product Group',
                width: 140,
                section: 'entity',
                render: (_: any, row: FinishedGoodsPlanningRow) => (
                    <span>{row.productGroup ?? '—'}</span>
                ),
            });
        }
        if (!hiddenSet.has('isActive')) {
            productMetaCols.push({
                key: 'isActive',
                header: 'Active',
                width: 80,
                section: 'entity',
                render: (_: any, row: FinishedGoodsPlanningRow) => (
                    <span
                        style={{
                            color: row.isActive
                                ? 'rgb(34, 197, 94)'
                                : 'rgb(239, 68, 68)',
                        }}
                    >
                        {row.isActive === null || row.isActive === undefined
                            ? '—'
                            : row.isActive
                              ? 'Yes'
                              : 'No'}
                    </span>
                ),
            });
        }

        // Dynamic entity attribute columns — only render those explicitly enabled by admin
        const attrCols: DataSheetColumn<FinishedGoodsPlanningRow>[] = (
            attrColumnDefs ?? []
        )
            .filter((def) => visibleAttrKeys.includes(def.key))
            .map((def) => ({
                key: def.key,
                header: def.label,
                width: 140,
                section: 'entity' as const,
                render: (_: any, row: FinishedGoodsPlanningRow) => {
                    const val = attrValuesByProduct?.get(row.productId)?.[
                        def.key
                    ];
                    return <span>{val ?? '—'}</span>;
                },
            }));

        // Add columns for each week (section: 'grouping' → always last)
        const weekColumns: DataSheetColumn<FinishedGoodsPlanningRow>[] = [];

        // Period 0 is truncated server-side to start from today, so its forecast / in-transit /
        // projection numbers reflect only the remaining days in the current period. Surface that
        // as a tooltip on the first month's column group header.
        const today = new Date();
        const endOfCurrentMonth = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0,
        );
        const daysLeftInCurrentMonth =
            endOfCurrentMonth.getDate() - today.getDate() + 1;
        const currentPeriodTooltip = `${daysLeftInCurrentMonth}d left — figures cover the remaining days of this period`;

        weekHeaders.forEach((week, idx) => {
            // Opening Stock / Current Stock column
            weekColumns.push({
                key: `week_${idx}_opening`,
                header: idx === 0 ? 'Current Stock' : 'Opening Stock',
                group: week.label,
                groupTooltip: idx === 0 ? currentPeriodTooltip : undefined,
                section: 'grouping',
                align: 'right',
                width: 100,
                render: (_: any, row: FinishedGoodsPlanningRow) => {
                    const weekData = row.weeks[idx];
                    // The opening-stock breakdown is keyed on one product, so a
                    // rollup row spanning many SKUs has nothing to drill into.
                    const isClickable = idx > 0 && !isGroupRow(row);

                    return (
                        <div
                            style={{
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 4,
                            }}
                        >
                            <span
                                style={{ fontVariantNumeric: 'tabular-nums' }}
                            >
                                {formatCount(Math.floor(
                                    weekData?.opening ?? 0,
                                ))}
                            </span>
                            {isClickable && (
                                <IconArrowsMaximize
                                    style={{
                                        width: 12,
                                        height: 12,
                                        cursor: 'pointer',
                                        color: 'rgb(148, 163, 184)',
                                        flexShrink: 0,
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onOpeningStockClick(row, idx);
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.color =
                                            'rgb(59, 130, 246)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.color =
                                            'rgb(148, 163, 184)';
                                    }}
                                />
                            )}
                        </div>
                    );
                },
            });

            // Forecast column — total forecasted units for this SKU/period.
            // For FORWARD-lookup orgs this is the uploaded forecast; for BACKWARD orgs
            // it's the sales-history-derived consumption (same field, both feed MoH).
            weekColumns.push({
                key: `week_${idx}_forecast`,
                header: 'Forecast',
                group: week.label,
                section: 'grouping',
                align: 'right',
                width: 100,
                render: (_: any, row: FinishedGoodsPlanningRow) => {
                    const forecast = row.weeks[idx]?.consumption;
                    return (
                        <span
                            style={{
                                fontVariantNumeric: 'tabular-nums',
                                color: 'rgb(71, 85, 105)',
                            }}
                        >
                            {forecast != null
                                ? formatCount(Math.round(forecast))
                                : '—'}
                        </span>
                    );
                },
            });

            // DoH / MoH column
            weekColumns.push({
                key: `week_${idx}_doh`,
                header: inventoryPeriod === 'MONTH' ? 'MoH' : 'DoH',
                group: week.label,
                section: 'grouping',
                align: 'right',
                width: 70,
                render: (_: any, row: FinishedGoodsPlanningRow) =>
                    renderDohValue(
                        row.weeks[idx]?.doh,
                        underStockLimit,
                        overStockLimit,
                    ),
            });

            // In-Transit column
            weekColumns.push({
                key: `week_${idx}_inTransit`,
                header: 'Incoming',
                group: week.label,
                section: 'grouping',
                align: 'right',
                width: 100,
                render: (_: any, row: FinishedGoodsPlanningRow) => {
                    const weekData = row.weeks[idx];
                    const inTransit = weekData?.inTransit;
                    const legs = formatIncomingLegs(weekData?.incomingLegs);

                    return (
                        <MStack gap={0} align="flex-end">
                            <span
                                style={{
                                    fontVariantNumeric: 'tabular-nums',
                                    color: 'rgb(71, 85, 105)',
                                }}
                            >
                                {inTransit || inTransit === 0
                                    ? formatCount(inTransit)
                                    : '—'}
                            </span>
                            {legs && (
                                <MText size="10px" c="dimmed" ta="right">
                                    {legs}
                                </MText>
                            )}
                        </MStack>
                    );
                },
            });
        });

        return [
            ...baseColumns,
            ...productMetaCols,
            ...attrCols,
            ...weekColumns,
        ];
    }, [
        weekHeaders,
        underStockLimit,
        overStockLimit,
        inventoryPeriod,
        onOpeningStockClick,
        hiddenSet,
        attrColumnDefs,
        attrValuesByProduct,
        visibleAttrKeys,
        rollupLabel,
    ]);

    const aggregateRow = useMemo(() => {
        const result: Record<string, string> = {};
        if (backendAggregateRow) {
            // The whole matched set, summed by the server before the page was cut.
            weekHeaders.forEach((_, idx) => {
                const bucket = backendAggregateRow.plan[idx];
                if (!bucket) return;
                result[`week_${idx}_opening`] = formatCount(bucket.opening_stock);
                result[`week_${idx}_forecast`] = formatCount(bucket.consumption);
                result[`week_${idx}_inTransit`] = formatCount(bucket.incoming);
            });
            return result;
        }
        // Leaves of the whole filtered set, never the page — see `leafRowsOf`.
        const source = leafRowsOf(aggregateSourceRows ?? rows);
        weekHeaders.forEach((_, idx) => {
            const openingSum = source.reduce(
                (sum, row) => sum + Math.floor(row.weeks[idx]?.opening ?? 0),
                0,
            );
            result[`week_${idx}_opening`] = formatCount(openingSum);
            const forecastSum = source.reduce(
                (sum, row) =>
                    sum + Math.round(row.weeks[idx]?.consumption ?? 0),
                0,
            );
            result[`week_${idx}_forecast`] = formatCount(forecastSum);
            const inTransitSum = source.reduce(
                (sum, row) => sum + (row.weeks[idx]?.inTransit ?? 0),
                0,
            );
            result[`week_${idx}_inTransit`] = formatCount(inTransitSum);
        });
        return result;
    }, [rows, aggregateSourceRows, backendAggregateRow, weekHeaders]);

    return (
        <DataSheet<FinishedGoodsPlanningRow>
            data={rows}
            columns={columns}
            aggregateRow={aggregateRow}
            bare
            hideHeaderBar
            maxHeight="auto"
            getRowKey={(row) => row.productId}
            getSubRows={(row) => row.subRows}
            explosion={explosion}
            emptyState="No planning data available"
        />
    );
};

/* ------------------------------------------------------------------ */
/*  Pipeline (In-Transit) view table - Using DataSheet                */
/* ------------------------------------------------------------------ */

/** Parse a period label into { start, end } date strings.
 *  Supports monthly ("Mar 2026") and weekly ("16 MAR - 22 MAR") formats. */
function parsePeriodDates(
    label: string,
): { start: string; end: string } | null {
    const months: Record<string, number> = {
        jan: 0,
        feb: 1,
        mar: 2,
        apr: 3,
        may: 4,
        jun: 5,
        jul: 6,
        aug: 7,
        sep: 8,
        oct: 9,
        nov: 10,
        dec: 11,
    };
    // Use local-timezone year/month/day to avoid the UTC offset shifting the date by one day
    // for users in UTC+ timezones (e.g. IST). toISOString() always returns UTC, so
    // new Date(2026, 3, 30) at midnight IST would stringify as "2026-04-29".
    const fmt = (d: Date) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    // Monthly format: "Mar 2026"
    const monthMatch = label.match(/^(\w{3})\s+(\d{4})$/);
    if (monthMatch) {
        const month = months[monthMatch[1].toLowerCase()];
        const year = parseInt(monthMatch[2]);
        if (month === undefined || isNaN(year)) return null;
        return {
            start: fmt(new Date(year, month, 1)),
            end: fmt(new Date(year, month + 1, 0)),
        };
    }

    // Weekly format: "16 MAR - 22 MAR"
    const weekMatch = label.match(
        /^(\d{1,2})\s+(\w{3})\s*-\s*(\d{1,2})\s+(\w{3})$/,
    );
    if (weekMatch) {
        const startDay = parseInt(weekMatch[1]);
        const startMonth = months[weekMatch[2].toLowerCase()];
        const endDay = parseInt(weekMatch[3]);
        const endMonth = months[weekMatch[4].toLowerCase()];
        if (startMonth === undefined || endMonth === undefined) return null;
        const now = new Date();
        const year = now.getFullYear();
        const startDate = new Date(year, startMonth, startDay);
        const endDate = new Date(
            endMonth < startMonth ? year + 1 : year,
            endMonth,
            endDay,
        );
        return { start: fmt(startDate), end: fmt(endDate) };
    }

    return null;
}

const FinishedGoodsPipelineTable: React.FC<{
    rows: FinishedGoodsPlanningRow[];
    onInTransitClick: (
        row: FinishedGoodsPlanningRow,
        weekIndex: number,
    ) => void;
    hiddenColumns: string[];
    attrColumnDefs?: { key: string; label: string }[];
    attrValuesByProduct?: Map<string, Record<string, string>>;
    visibleAttrKeys?: string[];
    /** Label of the active rollup dimension; null when showing SKU rows. */
    rollupLabel?: string | null;
    /**
     * The SKU column's break-down control. Produced by `useSkuExplosion` in the container,
     * which also supplies the exploded `rows` — DataSheet renders the toggle, the container
     * owns the arithmetic.
     */
    explosion?: React.ComponentProps<typeof DataSheet>['explosion'];
    /**
     * Every row the filters matched, before paging — the scope the Total row is summed over.
     * See `leafRowsOf`.
     */
    aggregateSourceRows?: FinishedGoodsPlanningRow[];
    /**
     * The server's Total row, summed over every SKU the filters matched before the page was
     * cut. Wins over the client-side sum whenever it is set: when the grid holds a server-cut
     * page, `rows` is a viewport and summing it would make the Total a page subtotal that
     * changes as you page. The container leaves it unset when the client already holds the
     * whole set (rollup / break-down fetches), where the client sum is the right scope.
     */
    backendAggregateRow?: WarehouseStockPlanningAggregateRow | null;
}> = ({
    rows,
    onInTransitClick,
    hiddenColumns,
    attrColumnDefs,
    attrValuesByProduct,
    visibleAttrKeys = [],
    rollupLabel,
    explosion,
    aggregateSourceRows,
    backendAggregateRow,
}) => {
    const hiddenSet = useMemo(() => new Set(hiddenColumns), [hiddenColumns]);
    const weekHeaders = rows.length > 0 ? rows[0].weeks : [];

    const columns = useMemo<DataSheetColumn<FinishedGoodsPlanningRow>[]>(() => {
        const baseColumns: DataSheetColumn<FinishedGoodsPlanningRow>[] = [
            {
                key: 'productId',
                header: rollupLabel ?? 'Product ID',
                width: 160,
                sticky: true,
                valueAccessor: (row) =>
                    isGroupRow(row) ? row.skuCode : row.productId,
                csvValue: (_value, row) =>
                    isGroupRow(row) ? row.skuCode : row.productId,
                render: (value: string, row) =>
                    isGroupRow(row) ? (
                        <GroupNameCell row={row} />
                    ) : (
                        <span
                            style={{
                                fontFamily:
                                    "'JetBrains Mono', 'Fira Code', ui-monospace, monospace",
                                fontSize: '0.8rem',
                                fontWeight: 500,
                            }}
                        >
                            {value}
                        </span>
                    ),
            },
            {
                key: 'description',
                header: 'SKU Description',
                width: 240,
                sticky: true,
            },
        ];

        // Entity-level product metadata columns (section: 'entity' → DataSheet places after sticky)
        const productMetaCols: DataSheetColumn<FinishedGoodsPlanningRow>[] = [];
        if (!hiddenSet.has('category')) {
            productMetaCols.push({
                key: 'category',
                header: 'Category',
                width: 120,
                section: 'entity',
                render: (_: any, row: FinishedGoodsPlanningRow) => (
                    <span>{row.category ?? '—'}</span>
                ),
            });
        }
        if (!hiddenSet.has('tier')) {
            productMetaCols.push({
                key: 'tier',
                header: 'Tier',
                width: 100,
                section: 'entity',
                render: (_: any, row: FinishedGoodsPlanningRow) => (
                    <span>{row.tier ?? '—'}</span>
                ),
            });
        }
        if (!hiddenSet.has('productGroup')) {
            productMetaCols.push({
                key: 'productGroup',
                header: 'Product Group',
                width: 140,
                section: 'entity',
                render: (_: any, row: FinishedGoodsPlanningRow) => (
                    <span>{row.productGroup ?? '—'}</span>
                ),
            });
        }
        if (!hiddenSet.has('isActive')) {
            productMetaCols.push({
                key: 'isActive',
                header: 'Active',
                width: 80,
                section: 'entity',
                render: (_: any, row: FinishedGoodsPlanningRow) => (
                    <span
                        style={{
                            color: row.isActive
                                ? 'rgb(34, 197, 94)'
                                : 'rgb(239, 68, 68)',
                        }}
                    >
                        {row.isActive === null || row.isActive === undefined
                            ? '—'
                            : row.isActive
                              ? 'Yes'
                              : 'No'}
                    </span>
                ),
            });
        }

        // Dynamic entity attribute columns — only render those explicitly enabled by admin
        const attrCols: DataSheetColumn<FinishedGoodsPlanningRow>[] = (
            attrColumnDefs ?? []
        )
            .filter((def) => visibleAttrKeys.includes(def.key))
            .map((def) => ({
                key: def.key,
                header: def.label,
                width: 140,
                section: 'entity' as const,
                render: (_: any, row: FinishedGoodsPlanningRow) => {
                    const val = attrValuesByProduct?.get(row.productId)?.[
                        def.key
                    ];
                    return <span>{val ?? '—'}</span>;
                },
            }));

        const periodColumns: DataSheetColumn<FinishedGoodsPlanningRow>[] = [];

        weekHeaders.forEach((week, idx) => {
            periodColumns.push({
                key: `week_${idx}_inTransit`,
                header: 'Incoming',
                group: week.label,
                section: 'grouping',
                align: 'right',
                width: 120,
                render: (_: any, row: FinishedGoodsPlanningRow) => {
                    const weekData = row.weeks[idx];
                    const inTransit = weekData?.inTransit ?? 0;

                    // The in-transit breakdown is keyed on one product, so a
                    // rollup row spanning many SKUs shows the total only.
                    if (inTransit === 0 || isGroupRow(row)) {
                        return (
                            <span
                                style={{
                                    fontVariantNumeric: 'tabular-nums',
                                    color:
                                        inTransit === 0
                                            ? 'rgb(148, 163, 184)'
                                            : undefined,
                                    fontWeight:
                                        inTransit === 0 ? undefined : 600,
                                }}
                            >
                                {formatCount(inTransit)}
                            </span>
                        );
                    }

                    const legs = formatIncomingLegs(weekData?.incomingLegs);

                    return (
                        <MStack gap={0} align="flex-end">
                            <span
                                style={{
                                    fontVariantNumeric: 'tabular-nums',
                                    fontWeight: 600,
                                    color: 'rgb(3, 105, 161)',
                                    cursor: 'pointer',
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onInTransitClick(row, idx);
                                }}
                            >
                                {formatCount(inTransit)}
                            </span>
                            {legs && (
                                <MText size="10px" c="dimmed" ta="right">
                                    {legs}
                                </MText>
                            )}
                        </MStack>
                    );
                },
            });
        });

        return [
            ...baseColumns,
            ...productMetaCols,
            ...attrCols,
            ...periodColumns,
        ];
    }, [
        weekHeaders,
        onInTransitClick,
        hiddenSet,
        attrColumnDefs,
        attrValuesByProduct,
        visibleAttrKeys,
        rollupLabel,
    ]);

    const aggregateRow = useMemo(() => {
        const result: Record<string, string> = {};
        if (backendAggregateRow) {
            // The whole matched set, summed by the server before the page was cut.
            weekHeaders.forEach((_, idx) => {
                const bucket = backendAggregateRow.plan[idx];
                if (!bucket) return;
                result[`week_${idx}_inTransit`] = formatCount(bucket.incoming);
            });
            return result;
        }
        // Leaves of the whole filtered set, never the page — see `leafRowsOf`.
        const source = leafRowsOf(aggregateSourceRows ?? rows);
        weekHeaders.forEach((_, idx) => {
            const inTransitSum = source.reduce(
                (sum, row) => sum + (row.weeks[idx]?.inTransit ?? 0),
                0,
            );
            result[`week_${idx}_inTransit`] = formatCount(inTransitSum);
        });
        return result;
    }, [rows, aggregateSourceRows, backendAggregateRow, weekHeaders]);

    return (
        <DataSheet<FinishedGoodsPlanningRow>
            data={rows}
            columns={columns}
            aggregateRow={aggregateRow}
            bare
            hideHeaderBar
            maxHeight="auto"
            getRowKey={(row) => row.productId}
            getSubRows={(row) => row.subRows}
            explosion={explosion}
            emptyState="No in-transit data available"
        />
    );
};

/* ------------------------------------------------------------------ */
/*  Main Inventory page                                               */
/* ------------------------------------------------------------------ */

const InventoryPage: React.FC = () => {
    const { currentUser } = useCurrentUser();
    const { consultantAccess, consultantAccessDetails } = useApp();

    const { captureContext: captureSnapshot, addToChat: addSnapshotToChat } =
        useWidgetContextCapture({
            componentName: 'InventorySnapshotTable',
            label: 'Current Inventory - Stock Levels by Product',
        });
    const { captureContext: capturePlanning, addToChat: addPlanningToChat } =
        useWidgetContextCapture({
            componentName: 'InventoryPlanningTable',
            label: 'In-Transit & Stock Pipeline',
        });
    const isConsultant = currentUser?.role === 'CONSULTANT';
    const isAdmin = currentUser?.role === 'ADMIN';
    // Persistent filter state below is scoped per merchant to avoid cross-merchant contamination.
    const merchantId = currentUser?.organizationId || 'default';
    const { showToastWarning, showToastSuccess } = useToaster();

    // Determine which tabs consultant has access to
    const hasSnapshotAccess =
        !isConsultant ||
        consultantAccess.includes(
            ConsultantCapability.TABLE_INVENTORY_SNAPSHOT,
        );
    const hasPlanningAccess =
        !isConsultant ||
        consultantAccess.includes(
            ConsultantCapability.TABLE_INVENTORY_PLANNING,
        );
    // Pipeline has its own key so it can be shared without SKU Planning;
    // a Planning grant still carries it, as it always has.
    const hasPipelineAccess =
        hasPlanningAccess ||
        consultantAccess.includes(
            ConsultantCapability.TABLE_INVENTORY_PIPELINE,
        );

    // Build visible tabs based on access
    const { data: orgConfig, isInitialLoading: orgConfigLoading } = useQuery({
        queryKey: ['org-config', merchantId],
        queryFn: () => getOrgConfig(merchantId),
        enabled: !!currentUser?.organizationId,
        staleTime: 5 * 60 * 1000,
    });
    // Staged rollout: the org opts in through inventory_view_defaults.ageing.enabled.
    const ageingEnabled = orgConfig?.inventoryViewDefaults?.ageing?.enabled === true;

    const visibleTabs = React.useMemo(() => {
        const tabs: Array<{
            id: 'snapshot' | 'planning' | 'pipeline' | 'drr' | 'ageing';
            label: string;
        }> = [];
        if (hasSnapshotAccess) tabs.push({ id: 'drr', label: 'DoH View' });
        if (hasSnapshotAccess)
            tabs.push({ id: 'snapshot', label: 'SKU Snapshot' });
        if (hasPlanningAccess)
            tabs.push({ id: 'planning', label: 'SKU Planning' });
        if (hasPipelineAccess)
            tabs.push({ id: 'pipeline', label: 'SKU Pipeline' });
        if (hasSnapshotAccess && ageingEnabled) tabs.push({ id: 'ageing', label: 'SKU Ageing' });
        return tabs;
    }, [hasSnapshotAccess, hasPlanningAccess, hasPipelineAccess, ageingEnabled]);

    // Default to first available tab
    const defaultView = visibleTabs[0]?.id || 'snapshot';
    const [view, setView] = React.useState<
        'snapshot' | 'planning' | 'pipeline' | 'drr' | 'ageing'
    >(defaultView);
    const [shareModal, setShareModal] = useState<{
        resourceKey: string;
        hiddenColumns: string[];
    } | null>(null);
    const drrCaptureRef = useRef<DataSheetCaptureHandle | null>(null);
    const [planningRows, setPlanningRows] = React.useState<
        FinishedGoodsPlanningRow[]
    >([]);
    /**
     * The planning fetch's server-side Total row (every matched SKU, pre-page). Kept beside
     * the page so Planning and Pipeline can show the brand-wide total rather than the sum of
     * the 20 rows on screen.
     */
    const [planningAggregateRow, setPlanningAggregateRow] =
        React.useState<WarehouseStockPlanningAggregateRow | null>(null);
    const [rawSnapshotRows, setRawSnapshotRows] = React.useState<
        FinishedGoodsSnapshotRow[]
    >([]);
    const [drrResp, setDrrResp] = React.useState<DrrViewResponse | null>(null);
    const [drrDrill, setDrrDrill] = React.useState<{
        channelKey: string;
        productId?: string;
        skuLabel?: string;
    } | null>(null);
    /** The Stock Transfer cell whose gate passes are open in the drawer. */
    const [stockTransferDrill, setStockTransferDrill] = React.useState<{
        productId: string;
        skuLabel: string;
        warehouseName?: string;
    } | null>(null);
    /** The In Transit / Scheduled cell whose shipments are open in the drawer. */
    const [channelShipmentDrill, setChannelShipmentDrill] = React.useState<{
        productId: string;
        skuLabel: string;
        channelKey: string;
        leg: ChannelShipmentLeg;
        warehouseName?: string;
    } | null>(null);
    const ageingCaptureRef = useRef<DataSheetCaptureHandle | null>(null);
    const [ageingResp, setAgeingResp] = React.useState<InventoryAgeingResponse | null>(null);
    const [ageingDrill, setAgeingDrill] = React.useState<AgeingCellTarget | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [snapshotSummary, setSnapshotSummary] = React.useState<{
        totalQuantity: number;
        totalInTransit: number | null;
        blendedDaysOnHand: number | undefined;
        lowStockCount: number;
        overStockCount: number;
    }>({
        totalQuantity: 0,
        totalInTransit: null,
        blendedDaysOnHand: undefined,
        lowStockCount: 0,
        overStockCount: 0,
    });
    const [backendAggregateRow, setBackendAggregateRow] = React.useState<{
        quantity: number;
        on_hold_stock?: number | null;
        in_transit?: number | null;
        warehouse_transfer?: number | null;
        consumption_per_day?: number;
        consumption_per_month?: number;
        days_on_hand?: number;
        months_on_hand?: number;
        groupings: Record<
            string,
            {
                quantity: number;
                on_hold_stock?: number | null;
                in_transit?: number | null;
                warehouse_transfer?: number | null;
            }
        >;
    } | null>(null);
    /**
     * Snapshot columns the server says carry an in-transit reading. Held in state rather than
     * derived from the rows so the columns describe the scope the request was made in, not
     * whatever the current page happens to contain.
     */
    const [snapshotInTransitBuckets, setSnapshotInTransitBuckets] = React.useState<
        string[]
    >([]);
    /** Subset of the in-transit buckets whose figure the server split into Scheduled + In Transit. */
    const [snapshotScheduledBuckets, setSnapshotScheduledBuckets] = React.useState<string[]>([]);
    /**
     * Snapshot columns the server says carry a warehouse-transfer reading. Held separately
     * from the in-transit buckets because a channel can report either figure without the
     * other; empty is the normal state for every channel that does not report it.
     */
    const [snapshotWarehouseTransferBuckets, setSnapshotWarehouseTransferBuckets] =
        React.useState<string[]>([]);
    /** Non-empty when the brand's Snapshot rows carry a Stock Transfer location. */
    const [snapshotStockTransferBuckets, setSnapshotStockTransferBuckets] = React.useState<
        string[]
    >([]);

    const { attributeParams: productAttributeParams } =
        useEntityAttributes('PRODUCT');

    // Entity attribute column defs derived from productAttributeParams (must come before effective cols)
    const attrColumnDefs = useMemo(
        () =>
            productAttributeParams.map((p) => ({ key: p.key, label: p.label })),
        [productAttributeParams],
    );

    // Admin's own hidden column preferences — _v2 keys with correct defaults
    // Snapshot default: only optional non-metric columns are hidden; totalInventory + daysOnHand are ON
    const [snapshotHiddenCols, setSnapshotHiddenCols] = usePersistentState<
        string[]
    >(
        `inventory_snapshot_hidden_cols_v2_${merchantId}`,
        SNAPSHOT_DEFAULT_HIDDEN,
    );
    const [planningHiddenCols, setPlanningHiddenCols] = usePersistentState<
        string[]
    >(
        `inventory_planning_hidden_cols_v2_${merchantId}`,
        PLANNING_DEFAULT_HIDDEN,
    );
    const [pipelineHiddenCols, setPipelineHiddenCols] = usePersistentState<
        string[]
    >(
        `inventory_pipeline_hidden_cols_v2_${merchantId}`,
        PLANNING_DEFAULT_HIDDEN,
    );
    const [drrHiddenCols, setDrrHiddenCols] = usePersistentState<string[]>(
        `inventory_drr_hidden_cols_v2_${merchantId}`,
        ['category'],
    );
    const [ageingHiddenCols, setAgeingHiddenCols] = usePersistentState<string[]>(
        `inventory_ageing_hidden_cols_${merchantId}`,
        AGEING_DEFAULT_HIDDEN,
    );
    const [ageingRollupBy, setAgeingRollupBy] = usePersistentState<string>(
        `inventory_ageing_rollup_by_${merchantId}`,
        NO_ROLLUP,
    );
    const [ageingSubRollupBy, setAgeingSubRollupBy] = usePersistentState<string>(
        `inventory_ageing_sub_rollup_by_${merchantId}`,
        NO_ROLLUP,
    );
    /** Pooled (one row per SKU) or split by the warehouse the units stand in. */
    const [ageingScope, setAgeingScope] = usePersistentState<InventoryAgeingScope>(
        `inventory_ageing_scope_${merchantId}`,
        'INTERNAL',
    );
    const ageingByWarehouse = ageingScope === 'WAREHOUSE';
    /** Grouped by warehouse, the group row already names it; the column would repeat it on every leaf. */
    const ageingVisibleHiddenCols = React.useMemo(
        () => (ageingRollupBy === AGEING_WAREHOUSE_DIMENSION ? [...ageingHiddenCols, AGEING_WAREHOUSE_DIMENSION] : ageingHiddenCols),
        [ageingRollupBy, ageingHiddenCols],
    );
    // DRR view's own channel pivot (Overall layout vs one channel's nodes) —
    // deliberately NOT shared with the Snapshot channel filter.
    const [drrChannel, setDrrChannel] = usePersistentState<string>(
        `inventory_drr_channel_${merchantId}`,
        'OVERALL',
    );

    // Row rollup: collapse SKU rows into one row per value of a product property.
    // 'none' keeps the SKU-per-row view. Each tab remembers its own dimension.
    //
    // The DoH view additionally honours a per-brand default set in Settings. The stored
    // preference is `string | null` where null means "this user has never chosen" —
    // usePersistentState writes on mount, so an absent key cannot be used to detect that.
    // Resolution is user choice > brand default > NO_ROLLUP, mirroring draftBasis ?? savedBasis.
    // _v2 key: every browser that opened this page under the old code already has the
    // hardcoded 'none' written under the v1 key, which would read as a deliberate user
    // choice and suppress the brand default for everyone. The v1 entry is abandoned.
    const [drrRollupPref, setDrrRollupPref] = usePersistentState<string | null>(
        `inventory_drr_rollup_by_v2_${merchantId}`,
        null,
    );

    // Already fetched by SideNavigationBar on every page, so this dedupes against a warm cache.
    // Until the brand default is known, the DoH grid cannot tell whether it needs one
    // page or every row. Firing the paged request anyway and re-requesting once the
    // default lands is a race: both take the server the same time (it computes the full
    // set and only slices at the end), and when the paged reply lands last it clobbers
    // the full one — 20 SKUs grouped into a handful of groups under a "166 SKUs" title.
    // A user who has explicitly chosen a grouping doesn't depend on the brand default.
    // `isInitialLoading` is false on error, so a failed config fetch still falls through.
    const drrRollupPending = drrRollupPref === null && orgConfigLoading;

    // A brand default naming an entity attribute that has since been deleted must not
    // strand the grid on a dimension that no longer resolves.
    const drrOrgDefault = useMemo(
        () =>
            resolveOrgRollupDefault(
                orgConfig?.inventoryViewDefaults?.drr?.rollupBy,
                attrColumnDefs.map((d) => d.key),
            ),
        [orgConfig, attrColumnDefs],
    );

    const drrRollupBy = resolveEffectiveRollup(drrRollupPref, drrOrgDefault);
    const setDrrRollupBy = setDrrRollupPref;
    // isDrrRolledUp is derived further down — searching suspends grouping, and the
    // search term is not in scope yet here.
    const [snapshotRollupBy, setSnapshotRollupBy] = usePersistentState<string>(
        `inventory_snapshot_rollup_by_${merchantId}`,
        NO_ROLLUP,
    );
    const [planningRollupBy, setPlanningRollupBy] = usePersistentState<string>(
        `inventory_planning_rollup_by_${merchantId}`,
        NO_ROLLUP,
    );
    const [pipelineRollupBy, setPipelineRollupBy] = usePersistentState<string>(
        `inventory_pipeline_rollup_by_${merchantId}`,
        NO_ROLLUP,
    );
    // Second nesting level per tab — e.g. category on top, product group inside it.
    // Separate keys so each tab remembers its own pair.
    const [drrSubRollupBy, setDrrSubRollupBy] = usePersistentState<string>(
        `inventory_drr_sub_rollup_by_${merchantId}`,
        NO_ROLLUP,
    );
    const [snapshotSubRollupBy, setSnapshotSubRollupBy] = usePersistentState<string>(
        `inventory_snapshot_sub_rollup_by_${merchantId}`,
        NO_ROLLUP,
    );
    const [planningSubRollupBy, setPlanningSubRollupBy] = usePersistentState<string>(
        `inventory_planning_sub_rollup_by_${merchantId}`,
        NO_ROLLUP,
    );
    const [pipelineSubRollupBy, setPipelineSubRollupBy] = usePersistentState<string>(
        `inventory_pipeline_sub_rollup_by_${merchantId}`,
        NO_ROLLUP,
    );
    /**
     * Break-down (explode) toggles, one per fetch. Held here rather than inside
     * `useSkuExplosion` because the FETCH depends on them: exploding needs every matching SKU,
     * not one page, since a parent on page 1 can break down into a base SKU on page 3. Same
     * reason a rollup forces a full fetch — see the pageSize branches below.
     */
    const [snapshotExploded, setSnapshotExploded] = React.useState(false);
    const [planningExploded, setPlanningExploded] = React.useState(false);
    const [drrExploded, setDrrExploded] = React.useState(false);
    /**
     * The entity-attribute filter resolved to product ids, kept from the fetch so the
     * client-side pass can re-apply it. Null means "no attribute filter active", which is
     * different from an empty array (an attribute filter that matched nothing).
     */
    const [snapshotAttrIds, setSnapshotAttrIds] = React.useState<string[] | null>(
        null,
    );
    const [planningAttrIds, setPlanningAttrIds] = React.useState<string[] | null>(
        null,
    );

    const isSnapshotRolledUp = snapshotRollupBy !== NO_ROLLUP;
    const isPlanningRolledUp = planningRollupBy !== NO_ROLLUP;
    const isPipelineRolledUp = pipelineRollupBy !== NO_ROLLUP;
    // Planning and Pipeline share one fetch, so either tab's grouping makes it
    // a full fetch — whichever tab is on screen decides.
    const isPlanningViewRolledUp =
        view === 'pipeline' ? isPipelineRolledUp : isPlanningRolledUp;

    /*
     * Whether a fetch has to return EVERY matching SKU rather than one page.
     *
     * Two independent reasons, and the first to apply wins:
     *   - a rollup aggregates across the whole set, so a page's worth is not a group's total;
     *   - break-down cannot be done on a page — a parent on page 1 breaks down into a base SKU
     *     on page 3, and a base on page 1 needs parents from page 5.
     *
     * Named rather than inlined at each site because these also drive client-side paging, the
     * footer's row count, and the fetch's dependency array — and an `a || b` buried in a dep
     * array is something neither the linter nor a reader can check.
     */
    const needsFullSnapshotSet = isSnapshotRolledUp || snapshotExploded;
    const needsFullPlanningSet = isPlanningViewRolledUp || planningExploded;
    /**
     * The server Total for the Planning / Pipeline grids, only while they hold a server-cut
     * page. On a full fetch (rollup or break-down) the client holds every matched SKU and its
     * own sum is the right scope — and the only one that follows a rollup's group-name search
     * or a break-down's re-derived rows.
     */
    const planningPageAggregateRow = needsFullPlanningSet
        ? null
        : planningAggregateRow;
    // `needsFullDrrSet` lives with `isDrrRolledUp`, which is derived further down.

    // Admin's visible entity attribute keys (default: none — opt-in)
    const [snapshotVisibleAttrKeys, setSnapshotVisibleAttrKeys] =
        usePersistentState<string[]>(
            `inventory_snapshot_visible_attr_keys_${merchantId}`,
            [],
        );
    const [planningVisibleAttrKeys, setPlanningVisibleAttrKeys] =
        usePersistentState<string[]>(
            `inventory_planning_visible_attr_keys_${merchantId}`,
            [],
        );

    // Frozen hidden cols for consultant (from backend, set at share time)
    const frozenSnapshotHiddenCols =
        consultantAccessDetails.find(
            (d) =>
                d.resourceKey === ConsultantCapability.TABLE_INVENTORY_SNAPSHOT,
        )?.hiddenColumns ?? null;
    const frozenPlanningHiddenCols =
        consultantAccessDetails.find(
            (d) =>
                d.resourceKey === ConsultantCapability.TABLE_INVENTORY_PLANNING,
        )?.hiddenColumns ?? null;
    // A Pipeline-only grant carries its own frozen list; a Planning grant keeps
    // governing both tabs, so it takes precedence when both exist.
    const frozenPipelineHiddenCols =
        frozenPlanningHiddenCols ??
        consultantAccessDetails.find(
            (d) =>
                d.resourceKey === ConsultantCapability.TABLE_INVENTORY_PIPELINE,
        )?.hiddenColumns ??
        null;

    // Effective hidden cols: consultant sees frozen list; admin sees their own prefs
    // For admin, the full effective list also includes attr columns that aren't explicitly enabled
    const effectiveSnapshotHiddenCols = isConsultant
        ? (frozenSnapshotHiddenCols ?? [])
        : [
              ...snapshotHiddenCols,
              ...(attrColumnDefs ?? [])
                  .filter((d) => !snapshotVisibleAttrKeys.includes(d.key))
                  .map((d) => d.key),
          ];
    const effectivePlanningHiddenCols = isConsultant
        ? (frozenPlanningHiddenCols ?? [])
        : [
              ...planningHiddenCols,
              ...(attrColumnDefs ?? [])
                  .filter((d) => !planningVisibleAttrKeys.includes(d.key))
                  .map((d) => d.key),
          ];
    const effectivePipelineHiddenCols = isConsultant
        ? (frozenPipelineHiddenCols ?? [])
        : [
              ...pipelineHiddenCols,
              ...(attrColumnDefs ?? [])
                  .filter((d) => !planningVisibleAttrKeys.includes(d.key))
                  .map((d) => d.key),
          ];

    // For consultants: derive which attr keys are visible from the frozen hidden list
    // (attrs not in frozen hidden list should be shown)
    const consultantSnapshotVisibleAttrKeys = useMemo(() => {
        if (!isConsultant || !frozenSnapshotHiddenCols)
            return attrColumnDefs.map((d) => d.key);
        return attrColumnDefs
            .filter((d) => !frozenSnapshotHiddenCols.includes(d.key))
            .map((d) => d.key);
    }, [isConsultant, frozenSnapshotHiddenCols, attrColumnDefs]);
    const consultantPlanningVisibleAttrKeys = useMemo(() => {
        if (!isConsultant || !frozenPlanningHiddenCols)
            return attrColumnDefs.map((d) => d.key);
        return attrColumnDefs
            .filter((d) => !frozenPlanningHiddenCols.includes(d.key))
            .map((d) => d.key);
    }, [isConsultant, frozenPlanningHiddenCols, attrColumnDefs]);
    const consultantPipelineVisibleAttrKeys = useMemo(() => {
        if (!isConsultant || !frozenPipelineHiddenCols)
            return attrColumnDefs.map((d) => d.key);
        return attrColumnDefs
            .filter((d) => !frozenPipelineHiddenCols.includes(d.key))
            .map((d) => d.key);
    }, [isConsultant, frozenPipelineHiddenCols, attrColumnDefs]);

    // Attribute values by product ID (fetched after main data loads)
    const [attrValuesByProduct, setAttrValuesByProduct] = React.useState<
        Map<string, Record<string, string>>
    >(new Map());

    React.useEffect(() => {
        if (productAttributeParams.length === 0) {
            setAttrValuesByProduct(new Map());
            return;
        }
        // The DoH view can roll rows up by an attribute, so it needs the values too.
        const productIds = (
            view === 'drr'
                ? (drrResp?.data ?? []).map((r) => r.product_id)
                : (view === 'snapshot' ? rawSnapshotRows : planningRows).map(
                      (r) => r.productId,
                  )
        ).filter(Boolean);
        if (productIds.length === 0) return;

        getEntityMasterTable({
            entityType: 'PRODUCT',
            page: 1,
            pageSize: productIds.length,
            filterEntityIds: productIds,
        })
            .then(({ rows: masterRows, columns: masterCols }) => {
                const map = new Map<string, Record<string, string>>();
                for (const row of masterRows) {
                    const pid = (row._id ?? row.product_id ?? row.id) as string;
                    if (!pid) continue;
                    const attrVals: Record<string, string> = {};
                    for (const col of masterCols) {
                        if (col.type === 'ATTRIBUTE' && col.attributeId) {
                            attrVals[`attr:${col.attributeId}`] =
                                (row[col.key] as string) ?? '';
                        }
                    }
                    map.set(pid, attrVals);
                }
                setAttrValuesByProduct(map);
            })
            .catch(() => setAttrValuesByProduct(new Map()));
    }, [rawSnapshotRows, planningRows, drrResp, view, productAttributeParams]);

    const [snapshotFilters, setSnapshotFilters] = usePersistentState<{
        page: number;
        search: string;
        channel: string;
    }>(`inventory_snapshot_filters_v2_${merchantId}`, {
        page: 1,
        search: '',
        channel: 'OVERALL',
    });

    const [snapshotProductFilter, setSnapshotProductFilter] =
        usePersistentState<FilterValue>(
            `inventory_snapshot_product_${merchantId}_v2`,
            {},
        );

    const [snapshotSortBy, setSnapshotSortBy] =
        React.useState<SortBy>('TOP_SELLING');

    // DoH view ordering. DEFAULT leaves today's behaviour untouched — the server's
    // DRR-desc order for flat rows, alphabetical for groups — so the tab opens exactly
    // as it did before this control existed.
    const [drrSortBy, setDrrSortBy] = React.useState<SortBy>('DEFAULT');

    // Active column-header sort for the snapshot table (server-side). Mutually
    // exclusive with the Sort By preset above.
    const [snapshotColumnSort, setSnapshotColumnSort] = React.useState<{
        column: string;
        direction: 'asc' | 'desc';
    } | null>(null);

    const [snapshotGroupBy, setSnapshotGroupBy] = React.useState<string | null>(
        null,
    );

    const [planningFilters, setPlanningFilters] = usePersistentState<{
        page: number;
        search: string;
    }>(`inventory_planning_filters_v2_${merchantId}`, {
        page: 1,
        search: '',
    });

    const [planningProductFilter, setPlanningProductFilter] =
        usePersistentState<FilterValue>(
            `inventory_planning_product_${merchantId}_v2`,
            {},
        );

    const [planningSortBy, setPlanningSortBy] =
        React.useState<SortBy>('TOP_SELLING');

    const [config, setConfig] = React.useState({
        underStockLimit: DEFAULT_UNDER_STOCK,
        overStockLimit: DEFAULT_OVER_STOCK,
        inventoryPeriod: 'DAY' as 'DAY' | 'MONTH', // Default to DAY
        // Org's SAVED DoH/MoH rate policy (echoed by the API). Defaults match the org column
        // defaults until the first response lands.
        basis: 'BACKWARD' as RateBasis,
        salesLookbackDays: 30,
    });

    // Transient "explore" override of the rate policy. null = follow the saved org default.
    // These drive the request and flip to the org default on reload (session-only, not persisted).
    const [draftBasis, setDraftBasis] = React.useState<RateBasis | null>(null);
    const [draftWindow, setDraftWindow] = React.useState<number | null>(null);
    const [savingRate, setSavingRate] = React.useState(false);

    const savedBasis = config.basis;
    const savedWindow = config.salesLookbackDays;
    const effectiveBasis: RateBasis = draftBasis ?? savedBasis;
    const effectiveWindow = draftWindow ?? savedWindow;
    // The window counts on BOTH bases now — it is a length, and the basis only says which
    // direction it points in — so a window change is a real unsaved preview either way.
    const isRateDirty =
        effectiveBasis !== savedBasis || effectiveWindow !== savedWindow;

    const handleResetRate = () => {
        setDraftBasis(null);
        setDraftWindow(null);
    };

    // Commit the current preview to the org default. This re-bases every downstream
    // consumer of DoH (alerts, scheduled reports, replenishment, planning), so we warn.
    const handleSaveRateDefault = async () => {
        if (!merchantId || merchantId === 'default') return;
        setSavingRate(true);
        try {
            await ReparoApi({
                method: 'PATCH',
                url: `/organizations/${merchantId}`,
                body: JSON.stringify({
                    lookup: effectiveBasis,
                    salesLookbackDays: effectiveWindow,
                }),
            });
            setConfig((prev) => ({
                ...prev,
                basis: effectiveBasis,
                salesLookbackDays: effectiveWindow,
            }));
            setDraftBasis(null);
            setDraftWindow(null);
            showToastSuccess({
                title: 'Saved as default',
                subtitle:
                    'Alerts, reports and planning will now use this basis.',
            });
        } catch {
            showToastWarning({
                title: 'Could not save the default. Please try again.',
            });
        } finally {
            setSavingRate(false);
        }
    };

    // Helper to get current view's state
    // DRR view rides the snapshot filter state (page/search/product filters).
    const currentFilters =
        view === 'snapshot' || view === 'drr' || view === 'ageing' ? snapshotFilters : planningFilters;

    // Setters wrapper
    const setPage = (p: number) => {
        if (view === 'snapshot' || view === 'drr' || view === 'ageing')
            setSnapshotFilters((prev) => ({ ...prev, page: p }));
        else setPlanningFilters((prev) => ({ ...prev, page: p }));
    };

    const setSearch = (s: string) => {
        if (view === 'snapshot' || view === 'drr' || view === 'ageing')
            setSnapshotFilters((prev) => ({ ...prev, search: s, page: 1 }));
        else setPlanningFilters((prev) => ({ ...prev, search: s, page: 1 }));
    };

    const setSortBy = (s: SortBy) => {
        if (view === 'drr') {
            setDrrSortBy(s);
            // Row counts do not change, but the row that was on screen does — start at
            // the top rather than mid-way down a reordered list.
            setSnapshotFilters((prev) => ({ ...prev, page: 1 }));
        } else if (view === 'snapshot') {
            setSnapshotSortBy(s);
            // The Sort By preset and a column-header sort are mutually exclusive.
            setSnapshotColumnSort(null);
        } else setPlanningSortBy(s);
    };

    // Column-header click on the snapshot table. Sorting is server-side, so reset
    // to page 1 and let the fetch effect re-query with the new sort.
    const handleSnapshotSortChange = React.useCallback(
        (key: string | null, direction: 'asc' | 'desc' | null) => {
            setSnapshotColumnSort(
                key && direction ? { column: key, direction } : null,
            );
            setSnapshotFilters((prev) => ({ ...prev, page: 1 }));
        },
        [setSnapshotFilters],
    );

    const setChannel = (c: string) => {
        // Channel only exists for snapshot
        setSnapshotFilters((prev) => ({ ...prev, channel: c, page: 1 }));
        if (c === 'OVERALL') {
            // City/State group-by doesn't apply once internal and external warehouses are mixed.
            setSnapshotGroupBy(null);
        }
    };

    const [totalCount, setTotalCount] = React.useState(0);
    const [itemsPerPage, setItemsPerPage] = React.useState(20);

    // Debounce search
    const [debouncedSearch, setDebouncedSearch] = React.useState('');
    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(currentFilters.search);
        }, 500);
        return () => clearTimeout(timer);
    }, [currentFilters.search]);

    /**
     * Searching suspends grouping on the DoH view.
     *
     * A rollup row is keyed by the group name, so a SKU-level term can never match one —
     * searching an exact SKU while grouped would return an empty table even though the
     * product is sitting in a group's subRows. Rather than half-search inside groups, a
     * term drops the view back to flat SKU rows and lets the server do its usual
     * `sku ILIKE … OR name ILIKE …` match; clearing the box restores the grouping. The
     * dropdown keeps showing the chosen dimension throughout, so nothing is silently lost.
     */
    const isDrrRolledUp = isRollupActive(drrRollupBy, debouncedSearch);
    /** See `needsFullSnapshotSet` above — same rule, declared here because this flag is. */
    const needsFullDrrSet = isDrrRolledUp || drrExploded;
    const isAgeingRolledUp = isRollupActive(ageingRollupBy, debouncedSearch);
    const needsFullAgeingSet = isAgeingRolledUp;

    // Dynamic Filter Options
    const [channelOptions, setChannelOptions] = React.useState<
        { label: string; value: string }[]
    >([]);
    const [categoryOptions, setCategoryOptions] = React.useState<string[]>([]);
    const [productGroupOptions, setProductGroupOptions] = React.useState<
        string[]
    >([]);
    const [tierOptions, setTierOptions] = React.useState<string[]>([]);

    // Fetch Filters (Channels & Categories)
    React.useEffect(() => {
        const fetchFilters = async () => {
            try {
                // Fetch Warehouses (replacing Channels)
                // New endpoint: /warehouses/types/list
                // Assumption: POST method, returns string[] or object[] with names.
                // We'll treat them as the channel/warehouse options.
                const warehousesRes = (await ReparoApi({
                    method: 'POST',
                    url: '/warehouses/types/list',
                    body: undefined,
                })) as any[];

                // Map response to options
                // Handle if it's strings or objects
                const mappedWarehouses = (warehousesRes || [])
                    .map((w: any) => {
                        if (typeof w === 'string') {
                            return { label: w, value: w };
                        }
                        // If object, try to find label/value
                        const val = w.name || w.type || w.id || w.value;
                        return val ? { label: val, value: val } : null;
                    })
                    .filter(Boolean as any) as {
                    label: string;
                    value: string;
                }[];

                // Dedupe
                const uniqueWarehouses = Array.from(
                    new Map(
                        mappedWarehouses.map((item) => [item.value, item]),
                    ).values(),
                );

                // Add "Internal" if needed, or just use what api returns.
                // User requirement was just to hit the endpoint.
                // We'll keep the "Internal" + others logic if it makes sense, OR just replace it.
                // "Internal k alawa external ku ja rha h tm name bhejo n yr"
                // Let's just use what the API returns. Use "Internal" only if not present?
                // Or just prepending it as a default safe option.
                // Let's rely on the API fully but keep Internal if needed?
                // "warehouses/types/list" sounds like it might return "Internal", "Amazon", etc.
                // So I will just set options from the API.
                // But wait, existing default is 'INTERNAL'. If API doesn't return it, filter might break?
                // I'll ensure 'INTERNAL' is there if the initial state depends on it.
                // Actually initial state is 'INTERNAL'.

                let finalOptions = uniqueWarehouses;
                // If API returns empty or doesn't include Internal, should we add it?
                // Let's safe-guard by adding Internal if valid.
                const hasInternal = finalOptions.some(
                    (o) => o.value === 'INTERNAL' || o.value === 'Internal',
                );
                if (!hasInternal) {
                    finalOptions = [
                        { label: 'Internal', value: 'INTERNAL' },
                        ...finalOptions,
                    ];
                }

                // Overall consolidates inventory across internal warehouses + every external channel.
                finalOptions = [
                    { label: 'Overall', value: 'OVERALL' },
                    ...finalOptions.filter((o) => o.value !== 'OVERALL'),
                ];

                setChannelOptions(finalOptions);

                // Fetch Categories
                const categoriesRes = (await ReparoApi({
                    method: 'POST',
                    url: '/products/categories/list',
                    body: undefined,
                })) as unknown as string[];
                setCategoryOptions(
                    (categoriesRes || []).map((c) => c?.trim()).filter(Boolean),
                );

                // Fetch Product Groups
                const groupsRes = (await ReparoApi({
                    method: 'POST',
                    url: '/products/product-groups/list',
                    body: undefined,
                })) as unknown as string[];
                setProductGroupOptions(
                    (groupsRes || []).map((g) => g?.trim()).filter(Boolean),
                );
            } catch (err) {
                console.error('Failed to fetch filters', err);
            }
        };
        fetchFilters();
    }, []);

    // Fetch tiers - re-fetches when channel changes for channel-specific tier classification.
    // OVERALL spans every channel, so we fall back to brand-level tiers (same as INTERNAL).
    const tierChannelKey = React.useMemo(() => {
        const ch = snapshotFilters.channel;
        return ch && ch !== 'INTERNAL' && ch !== 'OVERALL' ? ch : undefined;
    }, [snapshotFilters.channel]);

    React.useEffect(() => {
        const fetchTiers = async () => {
            try {
                const tiersRes = (await ReparoApi({
                    method: 'POST',
                    url: '/products/tiers/list',
                    body: JSON.stringify({ channel: tierChannelKey }),
                })) as unknown as string[];
                setTierOptions(
                    (tiersRes || []).map((t) => t?.trim()).filter(Boolean),
                );
            } catch {
                setTierOptions([]);
            }
        };
        void fetchTiers();
    }, [tierChannelKey]);

    React.useEffect(() => {
        // Hold the DoH fetch until the grouping is resolved (see drrRollupPending) —
        // loading stays on so the grid doesn't flash an empty flat view meanwhile.
        if (view === 'drr' && drrRollupPending) {
            setLoading(true);
            return;
        }
        // Every dependency change re-runs this effect, so a slow earlier request can
        // resolve after a newer one and overwrite its result. The cleanup marks the
        // superseded run so nothing it awaited is ever written into state.
        let cancelled = false;
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                if (view === 'drr') {
                    const drrAttrProductIds =
                        await resolveAttributeFilterEntityIds(
                            snapshotProductFilter,
                            getMatchingEntityIds,
                        );
                    if (cancelled) return;
                    const drrRes = await getDrrView({
                        categories: snapshotProductFilter.category ?? [],
                        productGroups: snapshotProductFilter.productGroup ?? [],
                        tiers: snapshotProductFilter.tier ?? [],
                        isActive: toIsActive(snapshotProductFilter.isActive),
                        ...(drrAttrProductIds !== null && {
                            productIds: drrAttrProductIds,
                        }),
                        // Rolled up, the search box matches group names client-side, so
                        // the SKU-level server search must not also narrow the set.
                        search: needsFullDrrSet ? undefined : debouncedSearch,
                        page: 1,
                        // A rollup must cover every matching SKU, not just the current
                        // page — one page's worth would not be the group's real total.
                        // Still a single request; the server just doesn't slice it.
                        ...(needsFullDrrSet
                            ? { pageSize: 1_000_000 }
                            : {
                                  page: snapshotFilters.page,
                                  pageSize: itemsPerPage,
                              }),
                        // Flat rows are paged by the server, so Sort By has to be applied
                        // there — sorting one page client-side would only shuffle that
                        // page. Grouped, the whole set is already here and the group rows
                        // are sorted locally instead (see drrGroupRows).
                        ...drrSortParams(drrSortBy),
                        drrWindow: draftWindow ?? undefined,
                        basis: draftBasis ?? undefined,
                        channelKey:
                            drrChannel !== 'OVERALL' ? drrChannel : undefined,
                    });
                    if (cancelled) return;
                    setDrrResp(drrRes);
                    setTotalCount(drrRes.total || 0);
                    // The DoH tab is a valid landing tab, so it has to seed the saved org
                    // defaults like the other two do — otherwise the control opens claiming
                    // "Past Sales / 30d" on a merchant whose saved default is neither.
                    if (drrRes.inventory_config) {
                        setConfig((prev) => ({
                            ...prev,
                            inventoryPeriod:
                                (drrRes.inventory_config.inventory_period ||
                                    'DAY') as 'DAY' | 'MONTH',
                            basis:
                                drrRes.inventory_config.saved_basis ??
                                prev.basis,
                            salesLookbackDays:
                                drrRes.inventory_config.sales_lookback_days ??
                                prev.salesLookbackDays,
                        }));
                    }
                } else if (view === 'ageing') {
                    const ageingAttrProductIds =
                        await resolveAttributeFilterEntityIds(
                            snapshotProductFilter,
                            getMatchingEntityIds,
                        );
                    if (cancelled) return;
                    const ageingRes = await getInventoryAgeing({
                        categories: snapshotProductFilter.category ?? [],
                        productGroups: snapshotProductFilter.productGroup ?? [],
                        tiers: snapshotProductFilter.tier ?? [],
                        isActive: toIsActive(snapshotProductFilter.isActive),
                        ...(ageingAttrProductIds !== null && {
                            productIds: ageingAttrProductIds,
                        }),
                        // Same rule as the DoH view: rolled up, the search matches group
                        // names client-side and the whole set is needed.
                        search: needsFullAgeingSet ? undefined : debouncedSearch,
                        page: 1,
                        ...(needsFullAgeingSet
                            ? { pageSize: 1_000_000 }
                            : {
                                  page: snapshotFilters.page,
                                  pageSize: itemsPerPage,
                              }),
                        drrWindow: draftWindow ?? undefined,
                        scope: ageingScope,
                    });
                    if (cancelled) return;
                    setAgeingResp(ageingRes);
                    setTotalCount(ageingRes.total || 0);
                    if (ageingRes.inventory_config) {
                        setConfig((prev) => ({
                            ...prev,
                            inventoryPeriod:
                                (ageingRes.inventory_config.inventory_period ||
                                    'DAY') as 'DAY' | 'MONTH',
                            basis:
                                ageingRes.inventory_config.saved_basis ??
                                prev.basis,
                            salesLookbackDays:
                                ageingRes.inventory_config.sales_lookback_days ??
                                prev.salesLookbackDays,
                        }));
                    }
                } else if (view === 'planning' || view === 'pipeline') {
                    // Start of Planning View Logic
                    // Currently simplified in API but capable of expansion.
                    // Ideally we should pass params if API supports it,
                    // but verifying if getWarehouseStockPlanning accepts them.
                    // Based on inventory.ts it currently only takes categories.
                    // We will update it to take more if needed, but for now stick to what works or standard.
                    // Wait, I restored the API to take page/pageSize in inventory.ts?
                    // No, I only updated getWarehouseStock. getWarehouseStockPlanning I left mostly as is
                    // except for the response type.
                    // Let's check my previous edit to inventory.ts (Step 142/147).
                    // I only updated getWarehouseStock there.
                    // So for planning, I should stick to fetching all or update the API signature.
                    // The user's curl was for 'warehouse-stock' (Snapshot).
                    // "snapshot fix krna h".
                    // I will prioritize Snapshot.

                    // Pass pagination params to Planning API
                    const planningAttrProductIds =
                        await resolveAttributeFilterEntityIds(
                            planningProductFilter,
                            getMatchingEntityIds,
                        );
                    if (cancelled) return;
                    setPlanningAttrIds(planningAttrProductIds);
                    const planningRes = await getWarehouseStockPlanning({
                        // Unfiltered when breaking down — see the snapshot request above.
                        ...(planningExploded
                            ? {}
                            : {
                                  categories: planningProductFilter.category ?? [],
                                  productGroups:
                                      planningProductFilter.productGroup ?? [],
                                  tiers: planningProductFilter.tier ?? [],
                                  isActive: toIsActive(
                                      planningProductFilter.isActive,
                                  ),
                              }),
                        ...(planningAttrProductIds !== null &&
                            !planningExploded && {
                                productIds: planningAttrProductIds,
                            }),
                        // Rolled up, the search box matches group names
                        // client-side, so the server-side SKU search must not
                        // also narrow the set.
                        search: needsFullPlanningSet ? undefined : debouncedSearch,
                        // A rollup must cover every matching SKU, not just the
                        // current page — one page's worth would not be the
                        // group's real total. Still one request; the server
                        // just doesn't slice it.
                        ...(needsFullPlanningSet
                            ? { page: 1, pageSize: 1_000_000 }
                            : {
                                  page: planningFilters.page,
                                  pageSize: itemsPerPage,
                              }),
                        sortBy: planningSortBy,
                        itemType: 'product',
                        type: 'OVERALL',
                        basis: draftBasis ?? undefined,
                        drrWindow: draftWindow ?? undefined,
                    });

                    // Note: Planning API in inventory.ts currently ignores page/pageSize/sortBy in the function params
                    // even though I added them to the type? No, check Step 139.
                    // getWarehouseStockPlanning only takes categories.
                    // I'll leave Planning as full fetch for now to avoid breaking it if backend doesn't support pagination there yet,
                    // OR I will trust the response `total` if it returns it.

                    const pRows: FinishedGoodsPlanningRow[] =
                        planningRes.data.map((item) => ({
                            productId: item.product_id,
                            skuCode: item.sku || item.product_id,
                            description: item.product_name,
                            totalInventory:
                                item.plan.length > 0
                                    ? Math.floor(item.plan[0].opening_stock)
                                    : 0,
                            category: (item as any).category ?? null,
                            tier: (item as any).tier ?? null,
                            productGroup: (item as any).product_group ?? null,
                            isActive: (item as any).is_active ?? null,
                            weeks: item.plan.map((p) => {
                                // Pick doh or moh based on inventoryPeriod from config
                                const inventoryValue =
                                    planningRes.inventory_config
                                        ?.inventory_period === 'MONTH'
                                        ? p.moh !== null && p.moh !== undefined
                                            ? Number(p.moh)
                                            : null
                                        : p.doh !== null && p.doh !== undefined
                                          ? Number(p.doh)
                                          : null;

                                return {
                                    label: p.label, // Pre-formatted by backend
                                    opening: Math.floor(p.opening_stock),
                                    consumption: p.consumption || 0,
                                    doh: inventoryValue,
                                    inTransit: p.incoming ?? p.in_transit ?? 0,
                                    incomingLegs: toIncomingLegs(p),
                                    belowSafety: (inventoryValue || 0) < 14,
                                };
                            }),
                        }));
                    setPlanningRows(pRows);
                    setPlanningAggregateRow(planningRes.aggregate_row ?? null);
                    setTotalCount(planningRes.total || pRows.length);
                    capturePlanning({
                        type: 'INTERNAL',
                        data_description:
                            'Weekly in-transit stock quantities and stock pipeline by product for INTERNAL warehouses. Shows how much stock is in-transit per week.',
                        ...(planningProductFilter.category?.length
                            ? { categories: planningProductFilter.category }
                            : {}),
                        ...(planningProductFilter.productGroup?.length
                            ? {
                                  productGroups:
                                      planningProductFilter.productGroup,
                              }
                            : {}),
                        ...(planningProductFilter.tier?.length
                            ? { tiers: planningProductFilter.tier }
                            : {}),
                        sortBy: planningSortBy,
                        ...(debouncedSearch ? { search: debouncedSearch } : {}),
                    });

                    // Update config if present
                    /*  if (planningRes.inventory_config) {
                        setConfig({
                            underStockLimit:
                                planningRes.inventory_config.under_stock_limit,
                            overStockLimit:
                                planningRes.inventory_config.over_stock_limit,
                        });
                    } */
                    if (planningRes.inventory_config) {
                        const inventoryPeriod =
                            planningRes.inventory_config.inventory_period ||
                            'DAY';
                        const isDayPeriod = inventoryPeriod === 'DAY';

                        setConfig({
                            underStockLimit: isDayPeriod
                                ? planningRes.inventory_config.under_stock_limit
                                : planningRes.inventory_config
                                      .under_stock_limit / 30,
                            overStockLimit: isDayPeriod
                                ? planningRes.inventory_config.over_stock_limit
                                : planningRes.inventory_config
                                      .over_stock_limit / 30,
                            inventoryPeriod: inventoryPeriod,
                            basis:
                                planningRes.inventory_config.basis ??
                                'BACKWARD',
                            salesLookbackDays:
                                planningRes.inventory_config
                                    .sales_lookback_days ?? 30,
                        });
                    }
                } else {
                    // Snapshot Logic
                    // Use snapshotFilters
                    const attributeProductIds =
                        await resolveAttributeFilterEntityIds(
                            snapshotProductFilter,
                            getMatchingEntityIds,
                        );
                    if (cancelled) return;

                    const params = {
                        type: snapshotFilters.channel,
                        // Rolled up, the rows on screen are groups: fetch every
                        // matching SKU (a page's worth would not be a group's
                        // real total) and match the search on group names below.
                        ...(needsFullSnapshotSet
                            ? { page: 1, pageSize: 1_000_000 }
                            : {
                                  page: snapshotFilters.page,
                                  pageSize: itemsPerPage,
                              }),
                        // Breaking down needs the WHOLE catalogue, not the filtered slice: a
                        // parent can sit outside the filter and still own units that belong to
                        // a base SKU inside it. basil's `-CUST` variants have no product group
                        // at all, so a product-group filter would drop their contribution and
                        // the base row would read low — the same failure the search had. The
                        // filters are re-applied client-side after the explosion instead.
                        ...(snapshotExploded
                            ? {}
                            : {
                                  categories: snapshotProductFilter.category ?? [],
                                  productGroups:
                                      snapshotProductFilter.productGroup ?? [],
                                  tiers: snapshotProductFilter.tier ?? [],
                                  isActive: toIsActive(
                                      snapshotProductFilter.isActive,
                                  ),
                              }),
                        sortBy: snapshotSortBy,
                        search: needsFullSnapshotSet ? undefined : debouncedSearch,
                        itemType: 'product' as const,
                        basis: draftBasis ?? undefined,
                        drrWindow: draftWindow ?? undefined,
                        ...(attributeProductIds !== null &&
                            !snapshotExploded && {
                                productIds: attributeProductIds,
                            }),
                        ...(snapshotGroupBy
                            ? {
                                  groupBy: snapshotGroupBy as
                                      | 'warehouse'
                                      | 'city'
                                      | 'state',
                              }
                            : {}),
                        ...(snapshotColumnSort
                            ? {
                                  sortColumn: snapshotColumnSort.column,
                                  sortDirection: snapshotColumnSort.direction,
                              }
                            : {}),
                    };

                    setSnapshotAttrIds(attributeProductIds);
                    const snapshotRes = await getWarehouseStock(params);
                    if (cancelled) return;

                    /*  if (snapshotRes.inventory_config) {
                        setConfig({
                            underStockLimit:
                                snapshotRes.inventory_config.under_stock_limit,
                            overStockLimit:
                                snapshotRes.inventory_config.over_stock_limit,
                        });
                    } */
                    if (snapshotRes.inventory_config) {
                        const inventoryPeriod =
                            snapshotRes.inventory_config.inventory_period ||
                            'DAY';
                        const isDayPeriod = inventoryPeriod === 'DAY';

                        setConfig({
                            underStockLimit: isDayPeriod
                                ? snapshotRes.inventory_config.under_stock_limit
                                : snapshotRes.inventory_config
                                      .under_stock_limit / 30,
                            overStockLimit: isDayPeriod
                                ? snapshotRes.inventory_config.over_stock_limit
                                : snapshotRes.inventory_config
                                      .over_stock_limit / 30,
                            inventoryPeriod: inventoryPeriod,
                            basis:
                                snapshotRes.inventory_config.basis ??
                                'BACKWARD',
                            salesLookbackDays:
                                snapshotRes.inventory_config
                                    .sales_lookback_days ?? 30,
                        });
                    }

                    // Transform logic based on the user's provided JSON structure
                    // The structure is flat array of objects, each having `groupings`.
                    // data: [ { product_id, sku, groupings: { "ChannelName": { quantity... } } } ]

                    // Note: The `SnapshotWarehouse` type in my code expects `name` and `quantity`.
                    // `FinishedGoodsSnapshotRow` expects `warehouses: SnapshotWarehouse[]`.

                    /* const newRows: FinishedGoodsSnapshotRow[] = (
                        snapshotRes.data as any[]
                    ).map((item) => {
                        const skuCode = item.sku || item.product_id || '—';
                        const description =
                            item.product_name || 'Unknown Product';

                        const totalInventory = Math.floor(item.quantity || 0);
                        const daysOnHand = item.days_on_hand;
                        const consumptionPerDay = item.consumption_per_day || 0; */
                    const newRows: FinishedGoodsSnapshotRow[] = (
                        snapshotRes.data as any[]
                    ).map((item) => {
                        const skuCode = item.sku || item.product_id || '—';
                        const description =
                            item.product_name || 'Unknown Product';

                        const totalInventory = Math.floor(item.quantity || 0);
                        // Pick days_on_hand or months_on_hand based on inventoryPeriod from config
                        const daysOnHand =
                            snapshotRes.inventory_config?.inventory_period ===
                            'MONTH'
                                ? item.months_on_hand
                                : item.days_on_hand;
                        const consumptionPerDay = item.consumption_per_day || 0;

                        // Parse groupings
                        const warehouses: SnapshotWarehouse[] = [];
                        if (item.groupings) {
                            Object.entries(item.groupings).forEach(
                                ([key, val]: [string, any]) => {
                                    // If we asked for 'AMAZON', we likely only get Amazon data?
                                    // Or if backend treats 'AMAZON' as 'EXTERNAL' under the hood and returns all?
                                    // If backend returns all externalities, we should filter here by the selected channel name.

                                    // Matching logic:
                                    // key is typically the channel name (e.g. "Amazon").
                                    // snapshotFilters.channel is the ID/Key (e.g. "AMAZON").
                                    // The map I built earlier uses abbreviationKey or Name as value.
                                    // Let's assume loose matching or just inclusion.

                                    warehouses.push({
                                        name: key,
                                        quantity: Math.floor(val.quantity || 0),
                                        onHoldStock:
                                            val.on_hold_stock !== null &&
                                            val.on_hold_stock !== undefined
                                                ? parseFloat(val.on_hold_stock)
                                                : null,
                                        inTransit:
                                            val.in_transit !== null &&
                                            val.in_transit !== undefined
                                                ? parseFloat(val.in_transit)
                                                : null,
                                        scheduled:
                                            val.scheduled !== null &&
                                            val.scheduled !== undefined
                                                ? parseFloat(val.scheduled)
                                                : null,
                                        warehouseTransfer:
                                            val.warehouse_transfer !== null &&
                                            val.warehouse_transfer !== undefined
                                                ? parseFloat(val.warehouse_transfer)
                                                : null,
                                        // Channel rollups only appear in OVERALL mode; per-channel views still
                                        // show individual sub-warehouses, so we don't suffix them.
                                        isChannel:
                                            !val.is_stock_transfer &&
                                            snapshotFilters.channel ===
                                                'OVERALL' &&
                                            !val.warehouse_name,
                                        isStockTransfer: !!val.is_stock_transfer,
                                        isMaterialStore: !!val.is_material_store,
                                    });
                                },
                            );
                        }

                        return {
                            productId: item.product_id,
                            skuCode,
                            description,
                            totalInventory,
                            onHoldStock: item.on_hold_stock ?? null,
                            inTransit: item.in_transit ?? null,
                            scheduled: item.scheduled ?? null,
                            warehouseTransfer: item.warehouse_transfer ?? null,
                            consumptionPerDay,
                            daysOnHand,
                            warehouses,
                            category: item.category ?? null,
                            tier: item.tier ?? null,
                            productGroup: item.product_group ?? null,
                            isActive: item.is_active ?? null,
                        };
                    });

                    setRawSnapshotRows(newRows);

                    // If a non-default groupBy is active, check if any valid grouping keys came back
                    if (snapshotGroupBy && snapshotGroupBy !== 'warehouse') {
                        const groupByLabels: Record<string, string> = {
                            city: 'City',
                            state: 'State',
                        };
                        const label =
                            groupByLabels[snapshotGroupBy] ?? snapshotGroupBy;
                        const hasValidGroupings = (
                            snapshotRes.data as any[]
                        ).some(
                            (item) =>
                                item.groupings &&
                                Object.keys(item.groupings).some(
                                    (k) => k && k.trim() !== '',
                                ),
                        );
                        if (!hasValidGroupings) {
                            showToastWarning({
                                title: `No ${label} groupings exist`,
                                subtitle: `Please try another Group By!`,
                            });
                        }
                    }

                    setTotalCount(snapshotRes.total || 0);
                    setSnapshotSummary({
                        totalQuantity: snapshotRes.total_quantity ?? 0,
                        totalInTransit: snapshotRes.total_incoming ?? snapshotRes.total_in_transit ?? null,
                        blendedDaysOnHand: snapshotRes.blended_days_on_hand,
                        lowStockCount: snapshotRes.low_stock_count ?? 0,
                        overStockCount: snapshotRes.over_stock_count ?? 0,
                    });
                    setBackendAggregateRow(snapshotRes.aggregate_row ?? null);
                    setSnapshotInTransitBuckets(
                        snapshotRes.in_transit_buckets ?? [],
                    );
                    setSnapshotScheduledBuckets(snapshotRes.scheduled_buckets ?? []);
                    setSnapshotWarehouseTransferBuckets(
                        snapshotRes.warehouse_transfer_buckets ?? [],
                    );
                    setSnapshotStockTransferBuckets(
                        snapshotRes.stock_transfer_buckets ?? [],
                    );
                    captureSnapshot({
                        type: snapshotFilters.channel,
                        ...(snapshotProductFilter.category?.length
                            ? { categories: snapshotProductFilter.category }
                            : {}),
                        ...(snapshotProductFilter.productGroup?.length
                            ? {
                                  productGroups:
                                      snapshotProductFilter.productGroup,
                              }
                            : {}),
                        ...(snapshotProductFilter.tier?.length
                            ? { tiers: snapshotProductFilter.tier }
                            : {}),
                        sortBy: snapshotSortBy,
                        ...(debouncedSearch ? { search: debouncedSearch } : {}),
                    });
                }
            } catch (err: any) {
                if (cancelled) return;
                console.error('Failed to fetch inventory data', err);
                setError(err.message || 'Failed to load inventory data');
            } finally {
                // A superseded run must not clear the spinner the newer run turned on.
                if (!cancelled) setLoading(false);
            }
        };

        fetchData();
        return () => {
            cancelled = true;
        };
    }, [
        drrRollupPending,
        debouncedSearch,
        // Trigger refetch on other filter changes immediately
        view,
        planningFilters.page,
        planningProductFilter,
        planningSortBy,
        snapshotFilters.page,
        snapshotProductFilter,
        snapshotSortBy,
        snapshotColumnSort,
        snapshotFilters.channel,
        snapshotGroupBy,
        itemsPerPage,
        draftBasis,
        draftWindow,
        drrChannel,
        isDrrRolledUp,
        isSnapshotRolledUp,
        isPlanningViewRolledUp,
        /*
         * The COMBINED "do we need every row" flags, not the break-down toggles themselves.
         *
         * Break-down is a pure client-side rejig of numbers already fetched — except on a
         * server-paginated grid, where one page cannot be exploded correctly: a parent on page
         * 1 breaks down into a base SKU on page 3, and a base on page 1 needs parents from page
         * 5. So the first thing that forces a full set wins, and toggling break-down while a
         * rollup already forced one does NOT refetch. Depending on the raw toggles reloaded the
         * grid every time even when every row was already in hand.
         */
        needsFullSnapshotSet,
        needsFullPlanningSet,
        needsFullDrrSet,
        needsFullAgeingSet,
        ageingScope,
    ]);

    // Rollup dimensions mirror what the product filter can filter on: the static
    // product properties plus every configured entity attribute.
    const rollupOptions = React.useMemo(
        () => [
            ...STATIC_ROLLUP_OPTIONS,
            // attrColumnDefs keys are already 'attr:<id>', matching attrValuesByProduct.
            ...attrColumnDefs.map((def) => ({
                label: def.label,
                value: def.key,
            })),
        ],
        [attrColumnDefs],
    );

    const drrRollupLabel = React.useMemo(
        () =>
            isDrrRolledUp
                ? (rollupOptions.find((o) => o.value === drrRollupBy)?.label ??
                  'Group')
                : null,
        [isDrrRolledUp, drrRollupBy, rollupOptions],
    );

    /**
     * DoH view rows as rendered: SKU rows as-is, or one rollup row per property value
     * with its member SKUs hanging off `subRows` for the expandable drill-in.
     *
     * A search term suspends grouping (see isDrrRolledUp), so rows here are already the
     * server's filtered SKU list whenever one is present — there is no group-name
     * filtering to do.
     */
    /**
     * DoH rows broken down to base SKUs. Same placement rule as the other tabs: before the
     * rollup, so a group aggregates exploded leaves, and before the search, so a base SKU is
     * found complete rather than missing whatever its parents hold.
     */
    const { rows: explodedDrrRows, explosion: drrExplosion } =
        useSkuExplosion<DrrViewRow>({
            rows: drrResp?.data ?? [],
            enabled: drrExploded,
            onEnabledChange: setDrrExploded,
            getId: getDrrRowId,
            cloneRow: cloneDrrRow,
            addScaled: addScaledDrrRow,
            recompute: recomputeDrrRow,
            makeEmptyRow: makeEmptyDrrRow,
        });

    const drrSearchedRows = React.useMemo(() => {
        const term = debouncedSearch.trim().toLowerCase();
        if (!drrExploded || !term) return explodedDrrRows;
        return explodedDrrRows.filter(
            (row) =>
                row.sku.toLowerCase().includes(term) ||
                row.name.toLowerCase().includes(term) ||
                row.product_id.toLowerCase().includes(term),
        );
    }, [explodedDrrRows, drrExploded, debouncedSearch]);

    const drrGroupRows = React.useMemo(() => {
        const rows = drrSearchedRows;
        if (!isDrrRolledUp) return rows;
        // The DRR row's product fields are snake_case; map them onto the shared
        // resolver's shape rather than duplicating the dimension switch.
        const dimensionOf = (dimension: string) => (row: DrrViewRow) =>
            resolveRollupValue(
                dimension,
                {
                    productId: row.product_id,
                    category: row.category ?? null,
                    tier: row.tier ?? null,
                    productGroup: row.product_group ?? null,
                    isActive: row.is_active ?? null,
                },
                attrValuesByProduct,
            );
        const grouped = rollupDrrRows(
            rows,
            dimensionOf(drrRollupBy),
            drrSubRollupBy === NO_ROLLUP || drrSubRollupBy === drrRollupBy
                ? undefined
                : dimensionOf(drrSubRollupBy),
            // Ranks groups by their aggregate, so Top Selling puts the group with the
            // highest combined DRR on top rather than the alphabetically first one.
            // Undefined under DEFAULT, which leaves the alphabetical order alone.
            compareDrrBySortBy(drrSortBy),
        );
        return grouped;
    }, [
        drrSearchedRows,
        drrSubRollupBy,
        isDrrRolledUp,
        drrRollupBy,
        drrSortBy,
        attrValuesByProduct,
    ]);

    /**
     * Page size applies to the rows on screen — groups when rolled up. Expanding
     * a group adds its SKUs inline without consuming page slots. Ungrouped rows
     * arrive already paged by the server, so they pass through untouched.
     */
    /**
     * Client-side paging for the two modes where the server did not slice for us.
     *
     * `rolledUp` was the original reason; break-down is the second and works identically —
     * both fetch every matching SKU, so both have to page here or the footer would claim
     * "1–20 of N" over a table rendering all N.
     */
    const pageOfGroups = React.useCallback(
        <T,>(rows: T[], rolledUp: boolean, page: number): T[] => {
            if (!rolledUp) return rows;
            const totalPages = Math.max(
                1,
                Math.ceil(rows.length / itemsPerPage),
            );
            const safePage = Math.min(page, totalPages);
            return rows.slice(
                (safePage - 1) * itemsPerPage,
                safePage * itemsPerPage,
            );
        },
        [itemsPerPage],
    );

    const drrDisplayRows = React.useMemo(
        () =>
            pageOfGroups(
                drrGroupRows,
                needsFullDrrSet,
                snapshotFilters.page,
            ),
        [
            drrGroupRows,
            needsFullDrrSet,
            snapshotFilters.page,
            pageOfGroups,
        ],
    );

    /**
     * Snapshot / Planning / Pipeline rows as rendered: SKU rows as-is, or one
     * rollup row per property value with its member SKUs on `subRows`. Same
     * contract as the DoH view — when grouped the fetch is unpaginated, the
     * search box matches group names, and paging happens here over groups.
     */
    const buildGroupedRows = React.useCallback(
        <T extends RollupSource & { subRows?: T[] }>(
            rows: T[],
            rollupBy: string,
            buildGroupRow: (groupValue: string, members: T[]) => T,
            getGroupName: (row: T) => string,
            subRollupBy: string = NO_ROLLUP,
        ): T[] => {
            if (rollupBy === NO_ROLLUP) return rows;
            const grouped = groupRowsByValue(
                rows,
                (row) =>
                    resolveRollupValue(rollupBy, row, attrValuesByProduct),
                // Second level built by the SAME buildGroupRow over the parent's own
                // members, so a sub-group aggregates by exactly the rule its parent does
                // rather than a second implementation of it. The parent's subRows are then
                // the sub-groups instead of the leaves; each sub-group keeps the leaves.
                (groupValue, members) => {
                    const parent = buildGroupRow(groupValue, members);
                    if (subRollupBy === NO_ROLLUP || subRollupBy === rollupBy) {
                        return parent;
                    }
                    return {
                        ...parent,
                        subRows: groupRowsByValue(
                            members,
                            (row) =>
                                resolveRollupValue(
                                    subRollupBy,
                                    row,
                                    attrValuesByProduct,
                                ),
                            buildGroupRow,
                        ),
                    };
                },
            );
            const term = debouncedSearch.trim().toLowerCase();
            if (!term) return grouped;
            return grouped.filter((group) =>
                getGroupName(group).toLowerCase().includes(term),
            );
        },
        [attrValuesByProduct, debouncedSearch],
    );

    /**
     * The two grids' rows restated one level down the product relation graph when the SKU
     * column's break-down toggle is on — combos, customisation variants and clearance
     * listings replaced by the base SKUs they are made of.
     *
     * Placed BEFORE `buildGroupedRows` on purpose: a rollup has to aggregate exploded leaves,
     * not pre-explosion ones. And after the server fetch, so it always operates on the
     * filtered selection the user is looking at.
     *
     * `snapshotRecompute` is memoised because the DoH figure depends on the merchant's
     * inventory period, and `useSkuExplosion` keys its work on callback identity — an inline
     * closure would rebuild the whole exploded set on every render.
     */
    const snapshotRecompute = React.useMemo(
        () => makeSnapshotRecompute(config.inventoryPeriod),
        [config.inventoryPeriod],
    );

    /**
     * Search AND the product filters, applied AFTER the explosion.
     *
     * With break-down on, none of these go to the server: the explosion needs the whole
     * catalogue, because a parent outside the filter can still own units belonging to a base
     * SKU inside it. Filtering first would drop those units before they could be folded in —
     * which is exactly how the same SKU came to read 8 unsearched and 2 searched.
     *
     * So the rule is: a filter chooses which complete rows you see, never what a row is made of.
     * Inert when break-down is off, in which case the server has already filtered.
     */
    const filterExploded = React.useCallback(
        <
            T extends {
                productId: string;
                skuCode: string;
                description: string;
                category: string | null;
                tier: string | null;
                productGroup: string | null;
                isActive: boolean | null;
            },
        >(
            rows: T[],
            active: boolean,
            filter: Record<string, string[] | undefined>,
            attrIds: string[] | null,
        ): T[] => {
            if (!active) return rows;

            const term = debouncedSearch.trim().toLowerCase();
            const categories = filter.category ?? [];
            const productGroups = filter.productGroup ?? [];
            const tiers = filter.tier ?? [];
            const isActive = toIsActive(filter.isActive);
            const attrSet = attrIds === null ? null : new Set(attrIds);

            return rows.filter((row) => {
                if (
                    term &&
                    !row.skuCode.toLowerCase().includes(term) &&
                    !row.description.toLowerCase().includes(term)
                ) {
                    return false;
                }
                // An empty selection means "no constraint", matching what the server does with
                // an empty array — not "match nothing".
                if (categories.length > 0 && !categories.includes(row.category ?? ''))
                    return false;
                if (
                    productGroups.length > 0 &&
                    !productGroups.includes(row.productGroup ?? '')
                )
                    return false;
                if (tiers.length > 0 && !tiers.includes(row.tier ?? '')) return false;
                if (isActive !== undefined && (row.isActive ?? true) !== isActive)
                    return false;
                if (attrSet && !attrSet.has(row.productId)) return false;
                return true;
            });
        },
        [debouncedSearch],
    );

    const { rows: explodedSnapshotRows, explosion: snapshotExplosion } =
        useSkuExplosion<FinishedGoodsSnapshotRow>({
            rows: rawSnapshotRows,
            enabled: snapshotExploded,
            onEnabledChange: setSnapshotExploded,
            getId: getSnapshotRowId,
            cloneRow: cloneSnapshotRow,
            addScaled: addScaledSnapshotRow,
            recompute: snapshotRecompute,
            makeEmptyRow: makeEmptySnapshotRow,
        });

    const { rows: explodedPlanningRows, explosion: planningExplosion } =
        useSkuExplosion<FinishedGoodsPlanningRow>({
            rows: planningRows,
            enabled: planningExploded,
            onEnabledChange: setPlanningExploded,
            getId: getPlanningRowId,
            cloneRow: clonePlanningRow,
            addScaled: addScaledPlanningRow,
            recompute: recomputePlanningRow,
            makeEmptyRow: makeEmptyPlanningRow,
        });

    const snapshotGroupedRows = React.useMemo(
        () =>
            buildGroupedRows(
                // Applied whether or not a rollup is active: the rollup groups whatever this
                // returns, so the filter has to run first either way.
                filterExploded(
                    explodedSnapshotRows,
                    snapshotExploded,
                    snapshotProductFilter,
                    snapshotAttrIds,
                ),
                snapshotRollupBy,
                (groupValue, members) =>
                    buildSnapshotGroupRow(
                        groupValue,
                        members,
                        config.inventoryPeriod,
                    ),
                (row) => row.skuCode,
                snapshotSubRollupBy,
            ),
        [
            explodedSnapshotRows,
            filterExploded,
            snapshotExploded,
            snapshotProductFilter,
            snapshotAttrIds,
            isSnapshotRolledUp,
            snapshotRollupBy,
            snapshotSubRollupBy,
            buildGroupedRows,
            config.inventoryPeriod,
        ],
    );

    const planningGroupedRows = React.useMemo(
        () =>
            buildGroupedRows(
                filterExploded(
                    explodedPlanningRows,
                    planningExploded,
                    planningProductFilter,
                    planningAttrIds,
                ),
                view === 'pipeline' ? pipelineRollupBy : planningRollupBy,
                buildPlanningGroupRow,
                (row) => row.skuCode,
                view === 'pipeline' ? pipelineSubRollupBy : planningSubRollupBy,
            ),
        [
            explodedPlanningRows,
            filterExploded,
            planningExploded,
            planningProductFilter,
            planningAttrIds,
            isPlanningViewRolledUp,
            view,
            pipelineRollupBy,
            planningRollupBy,
            pipelineSubRollupBy,
            planningSubRollupBy,
            buildGroupedRows,
        ],
    );

    const snapshotDisplayRows = React.useMemo(
        () =>
            pageOfGroups(
                snapshotGroupedRows,
                needsFullSnapshotSet,
                snapshotFilters.page,
            ),
        [
            snapshotGroupedRows,
            needsFullSnapshotSet,
            snapshotFilters.page,
            pageOfGroups,
        ],
    );

    const planningDisplayRows = React.useMemo(
        () =>
            pageOfGroups(
                planningGroupedRows,
                needsFullPlanningSet,
                planningFilters.page,
            ),
        [
            planningGroupedRows,
            needsFullPlanningSet,
            planningFilters.page,
            pageOfGroups,
        ],
    );

    /**
     * How many groups the active tab is showing, or null when it is not rolled
     * up. Drives the pagination footer, which counts the rows actually on
     * screen: sub-rows expand inline within their parent's slot, so a page of
     * 20 groups can reveal hundreds of SKUs.
     */
    const ageingEdges = ageingResp?.bucket_edges ?? DEFAULT_AGEING_EDGES;

    /** The DoH cell exactly as the DoH view colours it, in the period the org reads in. */
    const formatAgeingDoh = React.useCallback(
        (doh: number | null) =>
            renderDohValue(
                doh == null ? null : toDisplayDohFor(config.inventoryPeriod)(doh),
                config.underStockLimit,
                config.overStockLimit,
            ),
        [config.inventoryPeriod, config.underStockLimit, config.overStockLimit],
    );


    /**
     * SKU Ageing rows as rendered. Grouping reuses the DoH view's rollup — the ageing row
     * IS a DoH row plus an `age` block — and then each group gets its age from its leaves,
     * buckets summed and the weighted age recomputed rather than averaged.
     */
    const ageingGroupRows = React.useMemo((): InventoryAgeingRow[] => {
        const rows = ageingResp?.data ?? [];
        if (!isAgeingRolledUp) return rows;
        const dimensionOf = (dimension: string) => (row: DrrViewRow) =>
            dimension === AGEING_WAREHOUSE_DIMENSION
                ? ((row as InventoryAgeingRow).warehouse_name ?? 'Unknown warehouse')
                : resolveRollupValue(
                      dimension,
                      {
                          productId: row.product_id,
                          category: row.category ?? null,
                          tier: row.tier ?? null,
                          productGroup: row.product_group ?? null,
                          isActive: row.is_active ?? null,
                      },
                      attrValuesByProduct,
                  );
        return attachAgeAggregates(
            rollupDrrRows(
                rows,
                dimensionOf(ageingRollupBy),
                ageingSubRollupBy === NO_ROLLUP || ageingSubRollupBy === ageingRollupBy
                    ? undefined
                    : dimensionOf(ageingSubRollupBy),
            ),
            ageingEdges.length + 1,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ageingResp, isAgeingRolledUp, ageingRollupBy, ageingSubRollupBy, attrValuesByProduct, ageingEdges.length]);

    /** In the WAREHOUSE scope the warehouse itself is a grouping level, first in the list. */
    const ageingRollupOptions = React.useMemo(
        () =>
            ageingByWarehouse
                ? [{ label: 'Warehouse', value: AGEING_WAREHOUSE_DIMENSION }, ...rollupOptions]
                : rollupOptions,
        [ageingByWarehouse, rollupOptions],
    );

    const ageingDisplayRows = React.useMemo(
        () => pageOfGroups(ageingGroupRows, needsFullAgeingSet, snapshotFilters.page),
        [ageingGroupRows, needsFullAgeingSet, snapshotFilters.page, pageOfGroups],
    );

    const activeGroupCount = React.useMemo(() => {
        if (view === 'ageing') return needsFullAgeingSet ? ageingGroupRows.length : null;
        if (view === 'drr') return needsFullDrrSet ? drrGroupRows.length : null;
        if (view === 'snapshot')
            return needsFullSnapshotSet ? snapshotGroupedRows.length : null;
        return needsFullPlanningSet ? planningGroupedRows.length : null;
    }, [
        view,
        needsFullDrrSet,
        needsFullAgeingSet,
        drrGroupRows,
        ageingGroupRows,
        needsFullSnapshotSet,
        snapshotGroupedRows,
        needsFullPlanningSet,
        planningGroupedRows,
    ]);

    /**
     * Whether the row count on screen is a count of GROUPS rather than of base SKUs. Both
     * rollup and break-down make it differ from the server's total, but they differ in
     * different directions and the footer has to say which.
     */
    const activeRollupIsOn =
        view === 'drr'
            ? isDrrRolledUp
            : view === 'ageing'
              ? isAgeingRolledUp
              : view === 'snapshot'
              ? isSnapshotRolledUp
              : isPlanningViewRolledUp;

    /** The rollup dimension label for the tab on screen; null when ungrouped. */
    const rollupLabelFor = React.useCallback(
        (rollupBy: string): string | null =>
            rollupBy === NO_ROLLUP
                ? null
                : (rollupOptions.find((option) => option.value === rollupBy)
                      ?.label ?? 'Group'),
        [rollupOptions],
    );

    // Modal State
    const [modalData, setModalData] = React.useState<{
        isOpen: boolean;
        row: FinishedGoodsPlanningRow | null;
        weekIndex: number;
    }>({
        isOpen: false,
        row: null,
        weekIndex: -1,
    });

    const handleOpeningStockClick = (
        row: FinishedGoodsPlanningRow,
        index: number,
    ) => {
        setModalData({
            isOpen: true,
            row,
            weekIndex: index,
        });
    };

    const closeModal = () => {
        setModalData((prev) => ({ ...prev, isOpen: false }));
    };

    // In-Transit Breakdown Modal State
    const [inTransitModal, setInTransitModal] = React.useState<{
        isOpen: boolean;
        row: FinishedGoodsPlanningRow | null;
        weekIndex: number;
    }>({
        isOpen: false,
        row: null,
        weekIndex: -1,
    });

    const handleInTransitClick = (
        row: FinishedGoodsPlanningRow,
        index: number,
    ) => {
        setInTransitModal({ isOpen: true, row, weekIndex: index });
    };

    const closeInTransitModal = () => {
        setInTransitModal((prev) => ({ ...prev, isOpen: false }));
    };

    const tableContent = React.useMemo(() => {
        if (loading) {
            return (
                <div className="flex h-48 items-center justify-center text-slate-500">
                    Loading...
                </div>
            );
        }

        if (error) {
            return (
                <div className="flex h-48 items-center justify-center text-red-500">
                    Error: {error}
                </div>
            );
        }

        if (view === 'drr') {
            return (
                <DrrViewTable
                    captureRef={drrCaptureRef}
                    resp={drrResp}
                    rows={drrDisplayRows}
                    rollupLabel={drrRollupLabel}
                    underStockLimit={config.underStockLimit}
                    overStockLimit={config.overStockLimit}
                    inventoryPeriod={config.inventoryPeriod}
                    hiddenColumns={drrHiddenCols}
                    isInternalPivot={drrChannel === 'INTERNAL'}
                    explosion={drrExplosion}
                    aggregateSourceRows={drrGroupRows}
                    onChannelClick={(channelKey, row) =>
                        setDrrDrill({
                            channelKey,
                            productId: row.product_id,
                            skuLabel: `${row.sku} – ${row.name}`,
                        })
                    }
                    onStockTransferClick={(bucketKey, row) =>
                        setStockTransferDrill({
                            productId: row.product_id,
                            skuLabel: `${row.sku} – ${row.name}`,
                            // A warehouse column narrows to transfers into that warehouse.
                            ...(bucketKey !== 'overall' && bucketKey !== 'internal'
                                ? { warehouseName: bucketKey }
                                : {}),
                        })
                    }
                    onInTransitClick={(bucketKey, leg, row) =>
                        setChannelShipmentDrill({
                            productId: row.product_id,
                            skuLabel: `${row.sku} – ${row.name}`,
                            leg,
                            // Overall layout: the bucket IS the channel. Channel pivot: the
                            // channel is the one pivoted on and the bucket is one of its FCs
                            // (or 'overall', its total).
                            ...(drrChannel !== 'OVERALL'
                                ? {
                                      channelKey: drrChannel,
                                      ...(bucketKey !== 'overall'
                                          ? { warehouseName: bucketKey }
                                          : {}),
                                  }
                                : { channelKey: bucketKey }),
                        })
                    }
                />
            );
        }

        if (view === 'ageing') {
            return (
                <InventoryAgeingTable
                    captureRef={ageingCaptureRef}
                    resp={ageingResp}
                    rows={ageingDisplayRows}
                    rollupLabel={
                        isAgeingRolledUp
                            ? (ageingRollupOptions.find((o) => o.value === ageingRollupBy)?.label ?? 'Group')
                            : null
                    }
                    hiddenColumns={ageingVisibleHiddenCols}
                    dohLabel={dohHeaderLabel(config.inventoryPeriod)}
                    formatDoh={formatAgeingDoh}
                    onCellClick={setAgeingDrill}
                    byWarehouse={ageingByWarehouse}
                />
            );
        }

        if (view === 'pipeline') {
            return (
                <FinishedGoodsPipelineTable
                    rows={planningDisplayRows}
                    onInTransitClick={handleInTransitClick}
                    hiddenColumns={effectivePipelineHiddenCols}
                    attrColumnDefs={attrColumnDefs}
                    attrValuesByProduct={attrValuesByProduct}
                    visibleAttrKeys={
                        isConsultant
                            ? consultantPipelineVisibleAttrKeys
                            : planningVisibleAttrKeys
                    }
                    rollupLabel={rollupLabelFor(pipelineRollupBy)}
                    explosion={planningExplosion}
                    aggregateSourceRows={planningGroupedRows}
                    backendAggregateRow={planningPageAggregateRow}
                />
            );
        }

        if (view === 'planning') {
            return (
                <FinishedGoodsPlanningTable
                    rows={planningDisplayRows}
                    onOpeningStockClick={handleOpeningStockClick}
                    underStockLimit={config.underStockLimit}
                    overStockLimit={config.overStockLimit}
                    inventoryPeriod={config.inventoryPeriod}
                    hiddenColumns={effectivePlanningHiddenCols}
                    attrColumnDefs={attrColumnDefs}
                    attrValuesByProduct={attrValuesByProduct}
                    visibleAttrKeys={
                        isConsultant
                            ? consultantPlanningVisibleAttrKeys
                            : planningVisibleAttrKeys
                    }
                    rollupLabel={rollupLabelFor(planningRollupBy)}
                    explosion={planningExplosion}
                    aggregateSourceRows={planningGroupedRows}
                    backendAggregateRow={planningPageAggregateRow}
                />
            );
        }

        return (
            <FinishedGoodsSnapshotTable
                rows={snapshotDisplayRows}
                underStockLimit={config.underStockLimit}
                overStockLimit={config.overStockLimit}
                inventoryPeriod={config.inventoryPeriod}
                hiddenColumns={effectiveSnapshotHiddenCols}
                attrColumnDefs={attrColumnDefs}
                attrValuesByProduct={attrValuesByProduct}
                visibleAttrKeys={
                    isConsultant
                        ? consultantSnapshotVisibleAttrKeys
                        : snapshotVisibleAttrKeys
                }
                backendAggregateRow={backendAggregateRow}
                columnSort={snapshotColumnSort}
                onColumnSortChange={handleSnapshotSortChange}
                rollupLabel={rollupLabelFor(snapshotRollupBy)}
                explosion={snapshotExplosion}
                aggregateSourceRows={snapshotGroupedRows}
                inTransitBucketKeys={snapshotInTransitBuckets}
                scheduledBucketKeys={snapshotScheduledBuckets}
                warehouseTransferBucketKeys={snapshotWarehouseTransferBuckets}
                onStockTransferClick={(row) =>
                    setStockTransferDrill({
                        productId: row.productId,
                        skuLabel: `${row.skuCode} – ${row.description}`,
                    })
                }
                onInTransitClick={(bucketKey, leg, row) =>
                    setChannelShipmentDrill({
                        productId: row.productId,
                        skuLabel: `${row.skuCode} – ${row.description}`,
                        channelKey: snapshotFilters.channel,
                        leg,
                        // A warehouse column is keyed by name only when the grid is grouped by
                        // warehouse; a city / state column spans several FCs, so it opens the
                        // channel-wide list instead.
                        ...(bucketKey !== 'overall' &&
                        (!snapshotGroupBy || snapshotGroupBy === 'warehouse')
                            ? { warehouseName: bucketKey }
                            : {}),
                    })
                }
            />
        );
    }, [
        snapshotInTransitBuckets,
        snapshotScheduledBuckets,
        snapshotWarehouseTransferBuckets,
        snapshotFilters.channel,
        snapshotGroupBy,
        loading,
        error,
        view,
        planningDisplayRows,
        planningGroupedRows,
        planningPageAggregateRow,
        snapshotDisplayRows,
        snapshotGroupedRows,
        rollupLabelFor,
        snapshotExplosion,
        planningExplosion,
        snapshotRollupBy,
        planningRollupBy,
        pipelineRollupBy,
        config,
        handleOpeningStockClick,
        effectiveSnapshotHiddenCols,
        effectivePlanningHiddenCols,
        effectivePipelineHiddenCols,
        attrColumnDefs,
        attrValuesByProduct,
        isConsultant,
        backendAggregateRow,
        snapshotVisibleAttrKeys,
        planningVisibleAttrKeys,
        consultantSnapshotVisibleAttrKeys,
        consultantPlanningVisibleAttrKeys,
        consultantPipelineVisibleAttrKeys,
        snapshotColumnSort,
        handleSnapshotSortChange,
        drrResp,
        drrDisplayRows,
        drrGroupRows,
        drrExplosion,
        drrRollupLabel,
        drrHiddenCols,
        drrChannel,
        ageingResp,
        ageingDisplayRows,
        ageingVisibleHiddenCols,
        ageingRollupBy,
        isAgeingRolledUp,
        ageingRollupOptions,
        formatAgeingDoh,
        ageingByWarehouse,
    ]);

    // Calculate props for modal
    const modalProps = React.useMemo(() => {
        if (!modalData.row || modalData.weekIndex < 0) return null;

        const currentWeek = modalData.row.weeks[modalData.weekIndex];
        const prevWeek =
            modalData.weekIndex > 0
                ? modalData.row.weeks[modalData.weekIndex - 1]
                : null;
        const isInitialWeek = modalData.weekIndex === 0;

        return {
            skuCode: modalData.row.productId,
            productName: modalData.row.description,
            weekLabel: currentWeek.label,
            // If initial week, we treat current opening as the "prev" or just initial stock
            prevWeekLabel: prevWeek ? prevWeek.label : 'Initial Stock',
            prevOpening: prevWeek ? prevWeek.opening : currentWeek.opening,
            prevInTransit: prevWeek ? prevWeek.inTransit : 0,
            prevConsumption: prevWeek ? prevWeek.consumption : 0,
            currentOpening: currentWeek.opening,
            isInitialWeek,
        };
    }, [modalData]);

    const snapshotProductFilterConfig = useMemo<DataSheetFilterConfig>(
        () => ({
            label: 'Product',
            params: [
                {
                    key: 'category',
                    label: 'Category',
                    mode: 'multi',
                    options: categoryOptions.map((c) => ({
                        label: c,
                        value: c,
                    })),
                },
                {
                    key: 'productGroup',
                    label: 'Product Group',
                    mode: 'multi',
                    options: productGroupOptions.map((g) => ({
                        label: g,
                        value: g,
                    })),
                },
                {
                    key: 'tier',
                    label: 'Tier',
                    mode: 'multi',
                    options: tierOptions.map((t) => ({ label: t, value: t })),
                },
                {
                    key: 'isActive',
                    label: 'Active Status',
                    mode: 'single',
                    options: ACTIVE_STATUS_OPTIONS,
                },
                ...productAttributeParams,
            ],
            value: snapshotProductFilter,
            onChange: setSnapshotProductFilter,
        }),
        [
            categoryOptions,
            productGroupOptions,
            tierOptions,
            snapshotProductFilter,
            setSnapshotProductFilter,
            productAttributeParams,
        ],
    );

    const planningProductFilterConfig = useMemo<DataSheetFilterConfig>(
        () => ({
            label: 'Product',
            params: [
                {
                    key: 'category',
                    label: 'Category',
                    mode: 'multi',
                    options: categoryOptions.map((c) => ({
                        label: c,
                        value: c,
                    })),
                },
                {
                    key: 'productGroup',
                    label: 'Product Group',
                    mode: 'multi',
                    options: productGroupOptions.map((g) => ({
                        label: g,
                        value: g,
                    })),
                },
                {
                    key: 'tier',
                    label: 'Tier',
                    mode: 'multi',
                    options: tierOptions.map((t) => ({ label: t, value: t })),
                },
                {
                    key: 'isActive',
                    label: 'Active Status',
                    mode: 'single',
                    options: ACTIVE_STATUS_OPTIONS,
                },
                ...productAttributeParams,
            ],
            value: planningProductFilter,
            onChange: setPlanningProductFilter,
        }),
        [
            categoryOptions,
            productGroupOptions,
            tierOptions,
            planningProductFilter,
            setPlanningProductFilter,
            productAttributeParams,
        ],
    );

    const currentProductFilterConfig =
        view === 'snapshot' || view === 'drr' || view === 'ageing'
            ? snapshotProductFilterConfig
            : planningProductFilterConfig;

    // ── Download: allColumns definitions (all static columns, including hidden) ──

    const snapshotDownloadAllColumns = useMemo<
        DataSheetColumn<FinishedGoodsSnapshotRow>[]
    >(
        () => [
            { key: 'productId', header: 'Product ID' },
            { key: 'description', header: 'SKU Description' },
            { key: 'totalInventory', header: 'Total Inventory' },
            {
                key: 'onHoldStock',
                header: 'On Hold Stock',
                csvValue: (_, r) => r.onHoldStock ?? '',
            },
            {
                key: 'daysOnHand',
                header: config.inventoryPeriod === 'MONTH' ? 'MoH' : 'DoH',
                csvValue: (_, r) =>
                    r.daysOnHand != null ? Number(r.daysOnHand).toFixed(1) : '',
            },
            {
                key: 'category',
                header: 'Category',
                csvValue: (_, r) => r.category ?? '',
            },
            { key: 'tier', header: 'Tier', csvValue: (_, r) => r.tier ?? '' },
            {
                key: 'productGroup',
                header: 'Product Group',
                csvValue: (_, r) => r.productGroup ?? '',
            },
            {
                key: 'isActive',
                header: 'Active',
                csvValue: (_, r) =>
                    r.isActive == null ? '' : r.isActive ? 'Yes' : 'No',
            },
            // Entity attribute columns (admin-defined)
            ...attrColumnDefs.map((def) => ({
                key: def.key,
                header: def.label,
                csvValue: (_: any, r: FinishedGoodsSnapshotRow) =>
                    attrValuesByProduct?.get(r.productId)?.[def.key] ?? '',
            })),
        ],
        [config.inventoryPeriod, attrColumnDefs, attrValuesByProduct],
    );

    const planningDownloadAllColumns = useMemo<
        DataSheetColumn<FinishedGoodsPlanningRow>[]
    >(
        () => [
            { key: 'productId', header: 'Product ID' },
            { key: 'description', header: 'SKU Description' },
            {
                key: 'category',
                header: 'Category',
                csvValue: (_, r) => r.category ?? '',
            },
            { key: 'tier', header: 'Tier', csvValue: (_, r) => r.tier ?? '' },
            {
                key: 'productGroup',
                header: 'Product Group',
                csvValue: (_, r) => r.productGroup ?? '',
            },
            {
                key: 'isActive',
                header: 'Active',
                csvValue: (_, r) =>
                    r.isActive == null ? '' : r.isActive ? 'Yes' : 'No',
            },
            // Dynamic period columns come from extraColumns in onFetchAllData
        ],
        [],
    );

    /**
     * DoH view export columns. The grid lives in a bare, header-less DataSheet, so the
     * download button belongs to the outer toolbar — which means the CSV columns have to
     * be built out here. They come from the same builder the grid renders from, so the
     * per-channel groups cannot drift between what is shown and what is exported.
     */
    const drrDownloadColumns = useMemo(() => {
        const hiddenSet = new Set(drrHiddenCols);
        return buildDrrColumns({
            resp: drrResp,
            channels: drrResp?.channels ?? [],
            rollupLabel: null,
            hiddenSet,
            showInventory: !hiddenSet.has('inv'),
            showInTransit: !hiddenSet.has('inTransit'),
            showStockTransfer: !hiddenSet.has(STOCK_TRANSFER_HIDE_KEY),
            showDrr: !hiddenSet.has('drr'),
            showDoh: !hiddenSet.has('doh'),
            dohLabel: dohHeaderLabel(config.inventoryPeriod),
            toDisplayDoh: toDisplayDohFor(config.inventoryPeriod),
            underStockLimit: config.underStockLimit,
            overStockLimit: config.overStockLimit,
            isNodeMode: drrResp?.mode === 'nodes',
            isInternalPivot: drrChannel === 'INTERNAL',
            // Render-only; the CSV never invokes a cell's click handler.
            onChannelClick: () => {},
        });
    }, [drrResp, drrHiddenCols, config, drrChannel]);

    /**
     * One row per SKU, whatever the grid is showing. A CSV of group subtotals loses the
     * detail the file is wanted for, so this re-requests the unrolled set — the same
     * thing the Snapshot's export does. `rollupLabel: null` above keeps the identity
     * column headed "Product ID" to match.
     */
    const drrFetchAllData = React.useCallback(async () => {
        // Attribute filters resolve to ids server-side, exactly as the live fetch does —
        // an export must not quietly ignore a filter the grid is honouring.
        const attrProductIds = await resolveAttributeFilterEntityIds(
            snapshotProductFilter,
            getMatchingEntityIds,
        );
        const res = await getDrrView({
            categories: snapshotProductFilter.category ?? [],
            productGroups: snapshotProductFilter.productGroup ?? [],
            tiers: snapshotProductFilter.tier ?? [],
            isActive: toIsActive(snapshotProductFilter.isActive),
            ...(attrProductIds !== null && { productIds: attrProductIds }),
            search: debouncedSearch || undefined,
            page: 1,
            pageSize: 1_000_000,
            ...drrSortParams(drrSortBy),
            drrWindow: draftWindow ?? undefined,
            channelKey: drrChannel !== 'OVERALL' ? drrChannel : undefined,
        });
        return { rows: res.data ?? [] };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        snapshotProductFilter,
        debouncedSearch,
        drrSortBy,
        draftWindow,
        drrChannel,
    ]);

    const ageingDownloadColumns = useMemo(
        () =>
            buildAgeingColumns({
                edges: ageingEdges,
                receiptsFromDate: ageingResp?.receipts_from_date ?? null,
                rollupLabel: null,
                hiddenSet: new Set(ageingVisibleHiddenCols),
                dohLabel: dohHeaderLabel(config.inventoryPeriod),
                formatDoh: formatAgeingDoh,
                byWarehouse: ageingByWarehouse,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ageingEdges.join(','), ageingResp?.receipts_from_date, ageingVisibleHiddenCols, config.inventoryPeriod, formatAgeingDoh, ageingByWarehouse],
    );

    /** One row per SKU whatever the grid shows — same rule as the DoH export. */
    const ageingFetchAllData = React.useCallback(async () => {
        const attrProductIds = await resolveAttributeFilterEntityIds(
            snapshotProductFilter,
            getMatchingEntityIds,
        );
        const res = await getInventoryAgeing({
            categories: snapshotProductFilter.category ?? [],
            productGroups: snapshotProductFilter.productGroup ?? [],
            tiers: snapshotProductFilter.tier ?? [],
            isActive: toIsActive(snapshotProductFilter.isActive),
            ...(attrProductIds !== null && { productIds: attrProductIds }),
            search: debouncedSearch || undefined,
            page: 1,
            pageSize: 1_000_000,
            drrWindow: draftWindow ?? undefined,
            scope: ageingScope,
        });
        return { rows: res.data ?? [] };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snapshotProductFilter, debouncedSearch, draftWindow, ageingScope]);

    // Visible-only allColumns — filters out hidden columns so CSV matches the table
    const snapshotVisibleDownloadColumns = useMemo(
        () =>
            snapshotDownloadAllColumns.filter(
                (c) => !effectiveSnapshotHiddenCols.includes(c.key),
            ),
        [snapshotDownloadAllColumns, effectiveSnapshotHiddenCols],
    );
    const planningVisibleDownloadColumns = useMemo(
        () =>
            planningDownloadAllColumns.filter(
                (c) => !effectivePlanningHiddenCols.includes(c.key),
            ),
        [planningDownloadAllColumns, effectivePlanningHiddenCols],
    );

    // ── Download: onFetchAllData — fetches all rows, returns typed rows + dynamic columns ──

    const snapshotFetchAllData = React.useCallback(async (): Promise<{
        rows: FinishedGoodsSnapshotRow[];
        extraColumns: DataSheetColumn<FinishedGoodsSnapshotRow>[];
    }> => {
        const res = await getWarehouseStock({
            type: snapshotFilters.channel,
            page: 1,
            pageSize: 1_000_000,
            categories: snapshotProductFilter.category ?? [],
            productGroups: snapshotProductFilter.productGroup ?? [],
            tiers: snapshotProductFilter.tier ?? [],
            isActive: toIsActive(snapshotProductFilter.isActive),
            sortBy: snapshotSortBy,
            search: debouncedSearch,
            itemType: 'product',
            ...(snapshotColumnSort
                ? {
                      sortColumn: snapshotColumnSort.column,
                      sortDirection: snapshotColumnSort.direction,
                  }
                : {}),
        });

        const inventoryPeriod = res.inventory_config?.inventory_period || 'DAY';

        const rows: FinishedGoodsSnapshotRow[] = (res.data as any[]).map(
            (item) => ({
                productId: item.product_id,
                skuCode: item.sku || item.product_id || '—',
                description: item.product_name || 'Unknown Product',
                totalInventory: Math.floor(item.quantity || 0),
                onHoldStock: item.on_hold_stock ?? null,
                inTransit: item.in_transit ?? null,
                scheduled: item.scheduled ?? null,
                warehouseTransfer: item.warehouse_transfer ?? null,
                consumptionPerDay: item.consumption_per_day || 0,
                daysOnHand:
                    inventoryPeriod === 'MONTH'
                        ? item.months_on_hand
                        : item.days_on_hand,
                warehouses: item.groupings
                    ? Object.entries(item.groupings).map(
                          ([name, val]: [string, any]) => ({
                              name,
                              quantity: Math.floor(val.quantity || 0),
                              onHoldStock:
                                  val.on_hold_stock != null
                                      ? parseFloat(val.on_hold_stock)
                                      : null,
                              inTransit:
                                  val.in_transit != null
                                      ? parseFloat(val.in_transit)
                                      : null,
                              scheduled:
                                  val.scheduled != null
                                      ? parseFloat(val.scheduled)
                                      : null,
                              warehouseTransfer:
                                  val.warehouse_transfer != null
                                      ? parseFloat(val.warehouse_transfer)
                                      : null,
                              isChannel:
                                  !val.is_stock_transfer &&
                                  snapshotFilters.channel === 'OVERALL' &&
                                  !val.warehouse_name,
                              isStockTransfer: !!val.is_stock_transfer,
                              isMaterialStore: !!val.is_material_store,
                          }),
                      )
                    : [],
                category: item.category ?? null,
                tier: item.tier ?? null,
                productGroup: item.product_group ?? null,
                isActive: item.is_active ?? null,
            }),
        );

        // Dynamic warehouse/channel columns — internal warehouses first (alphabetical), then channel rollups
        // (alphabetical). Channel headers get an " Inventory" suffix. Per-warehouse on-hold only when visible.
        const showOnHold = !effectiveSnapshotHiddenCols.includes('onHoldStock');
        const warehouseEntries = orderSnapshotLocations(rows).filter(
            (entry) =>
                !entry.isStockTransfer ||
                !effectiveSnapshotHiddenCols.includes(STOCK_TRANSFER_HIDE_KEY),
        );
        // Same server-declared rule as the grid, read off this response rather than the one
        // on screen, so an export taken right after a channel switch matches its own data.
        const inTransitKeys = new Set(res.in_transit_buckets ?? []);
        const scheduledKeys = new Set(res.scheduled_buckets ?? []);
        const showInTransit =
            !effectiveSnapshotHiddenCols.includes('inTransit');
        const extraColumns: DataSheetColumn<FinishedGoodsSnapshotRow>[] =
            warehouseEntries.flatMap(({ name, isChannel, isStockTransfer }) => {
                if (isStockTransfer) {
                    return [
                        {
                            key: `warehouse_${name}`,
                            header: STOCK_TRANSFER_LABEL,
                            csvValue: (_: any, r: FinishedGoodsSnapshotRow) =>
                                r.warehouses.find((w) => w.name === name)?.quantity ?? 0,
                        },
                    ];
                }
                const displayName = isChannel ? `${name} Inventory` : name;
                const grouped =
                    showOnHold || (showInTransit && inTransitKeys.has(name));
                const cols: DataSheetColumn<FinishedGoodsSnapshotRow>[] = [
                    {
                        key: `warehouse_${name}`,
                        header: grouped
                            ? `${displayName} - Total Inv`
                            : displayName,
                        csvValue: (_: any, r: FinishedGoodsSnapshotRow) =>
                            r.warehouses.find((w) => w.name === name)
                                ?.quantity ?? 0,
                    },
                ];
                if (showInTransit && inTransitKeys.has(name)) {
                    cols.push({
                        key: `warehouse_${name}_inTransit`,
                        header: `${displayName} - In Transit`,
                        csvValue: (_: any, r: FinishedGoodsSnapshotRow) =>
                            r.warehouses.find((w) => w.name === name)
                                ?.inTransit ?? '',
                    });
                    if (scheduledKeys.has(name)) {
                        cols.push({
                            key: `warehouse_${name}_scheduled`,
                            header: `${displayName} - Scheduled`,
                            csvValue: (_: any, r: FinishedGoodsSnapshotRow) =>
                                r.warehouses.find((w) => w.name === name)
                                    ?.scheduled ?? '',
                        });
                    }
                }
                if (showOnHold) {
                    cols.push({
                        key: `warehouse_${name}_onHold`,
                        header: `${displayName} - On Hold`,
                        csvValue: (_: any, r: FinishedGoodsSnapshotRow) =>
                            r.warehouses.find((w) => w.name === name)
                                ?.onHoldStock ?? '',
                    });
                }
                return cols;
            });

        // A grouped grid downloads what it shows: one row per group.
        return {
            rows: buildGroupedRows(
                rows,
                snapshotRollupBy,
                (groupValue, members) =>
                    buildSnapshotGroupRow(groupValue, members, inventoryPeriod),
                (row) => row.skuCode,
            ),
            extraColumns,
        };
    }, [
        snapshotFilters,
        snapshotProductFilter,
        snapshotSortBy,
        snapshotColumnSort,
        debouncedSearch,
        effectiveSnapshotHiddenCols,
        snapshotRollupBy,
        buildGroupedRows,
    ]);

    const planningFetchAllData = React.useCallback(async (): Promise<{
        rows: FinishedGoodsPlanningRow[];
        extraColumns: DataSheetColumn<FinishedGoodsPlanningRow>[];
    }> => {
        const res = await getWarehouseStockPlanning({
            categories: planningProductFilter.category ?? [],
            productGroups: planningProductFilter.productGroup ?? [],
            tiers: planningProductFilter.tier ?? [],
            isActive: toIsActive(planningProductFilter.isActive),
            page: 1,
            pageSize: 1_000_000,
            sortBy: planningSortBy,
            search: debouncedSearch,
            itemType: 'product',
            type: 'OVERALL',
        });

        const inventoryPeriod = res.inventory_config?.inventory_period || 'DAY';
        const dohLabel = inventoryPeriod === 'MONTH' ? 'MoH' : 'DoH';

        const rows: FinishedGoodsPlanningRow[] = (res.data as any[]).map(
            (item) => ({
                productId: item.product_id,
                skuCode: item.sku || item.product_id,
                description: item.product_name,
                totalInventory:
                    item.plan.length > 0
                        ? Math.floor(item.plan[0].opening_stock)
                        : 0,
                category: item.category ?? null,
                tier: item.tier ?? null,
                productGroup: item.product_group ?? null,
                isActive: item.is_active ?? null,
                weeks: item.plan.map((p: any) => ({
                    label: p.label,
                    opening: Math.floor(p.opening_stock),
                    consumption: p.consumption || 0,
                    doh:
                        inventoryPeriod === 'MONTH'
                            ? (p.moh ?? null)
                            : (p.doh ?? null),
                    inTransit: p.incoming ?? p.in_transit ?? 0,
                    incomingLegs: toIncomingLegs(p),
                    belowSafety: false,
                })),
            }),
        );

        // Dynamic period columns — Opening Stock / DoH / In-Transit per period
        const weekLabels: string[] = rows[0]?.weeks.map((w) => w.label) ?? [];
        const extraColumns: DataSheetColumn<FinishedGoodsPlanningRow>[] =
            weekLabels.flatMap((label, idx) => [
                {
                    key: `week_${idx}_opening`,
                    header: `${label} - ${idx === 0 ? 'Current Stock' : 'Opening Stock'}`,
                    csvValue: (_: any, r: FinishedGoodsPlanningRow) =>
                        r.weeks[idx]?.opening ?? 0,
                },
                {
                    key: `week_${idx}_doh`,
                    header: `${label} - ${dohLabel}`,
                    csvValue: (_: any, r: FinishedGoodsPlanningRow) => {
                        const doh = r.weeks[idx]?.doh;
                        return doh != null ? Number(doh).toFixed(1) : '';
                    },
                },
                {
                    key: `week_${idx}_inTransit`,
                    header: `${label} - Incoming`,
                    csvValue: (_: any, r: FinishedGoodsPlanningRow) =>
                        r.weeks[idx]?.inTransit ?? 0,
                },
            ]);

        // A grouped grid downloads what it shows: one row per group. Planning and
        // Pipeline share this fetch, so the tab on screen picks the dimension.
        return {
            rows: buildGroupedRows(
                rows,
                view === 'pipeline' ? pipelineRollupBy : planningRollupBy,
                buildPlanningGroupRow,
                (row) => row.skuCode,
            ),
            extraColumns,
        };
    }, [
        planningProductFilter,
        planningSortBy,
        debouncedSearch,
        view,
        pipelineRollupBy,
        planningRollupBy,
        buildGroupedRows,
    ]);

    return (
        <div className="flex h-full w-full flex-col page-shell">
            {/* Shared PageHeader Component */}
            <PageHeader
                icon="📦"
                title="Inventory"
                subtitle="Monitor stock levels and plan replenishment"
                actions={<AskAiButton />}
            />

            {/* Top toggle */}
            {/* <div className="inline-flex overflow-hidden rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
                    <button
                        className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium ${
                            topTab === 'finished'
                                ? 'bg-blue-50 text-blue-700'
                                : 'text-slate-500 hover:text-slate-700'
                        }`}
                        onClick={() => setTopTab('finished')}
                        type="button"
                    >
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-white text-blue-600 shadow-sm">
                            📦
                        </span>
                        Finished Goods
                    </button>
                    <button
                        className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium ${
                            topTab === 'materials'
                                ? 'bg-blue-50 text-blue-700'
                                : 'text-slate-500 hover:text-slate-700'
                        }`}
                        onClick={() => setTopTab('materials')}
                        type="button"
                    >
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-white text-slate-500 shadow-sm">
                            🧪
                        </span>
                        Materials
                    </button>
                </div> */}

            {/* Section header */}
            {/* <section className="space-y-1">
                    <h2 className="text-lg font-semibold text-slate-900">
                        Finished Goods Management
                    </h2>
                    <p className="text-sm text-slate-600">
                        Monitor stock levels and plan replenishment
                    </p>
                </section> */}

            {/* Table card - DataSheet with tabs */}
            <DataSheet<FinishedGoodsSnapshotRow | FinishedGoodsPlanningRow>
                className="min-h-0 flex-1"
                data={[]}
                columns={[]}
                tabs={visibleTabs}
                activeTab={view}
                onTabChange={(tabId) =>
                    setView(tabId as 'snapshot' | 'planning' | 'pipeline' | 'drr' | 'ageing')
                }
                onShare={
                    isConsultant
                        ? undefined
                        : () => {
                              const resourceKey =
                                  view === 'planning'
                                      ? ConsultantCapability.TABLE_INVENTORY_PLANNING
                                      : view === 'pipeline'
                                        ? ConsultantCapability.TABLE_INVENTORY_PIPELINE
                                        : ConsultantCapability.TABLE_INVENTORY_SNAPSHOT;
                              const hiddenCols =
                                  view === 'snapshot'
                                      ? effectiveSnapshotHiddenCols
                                      : view === 'ageing'
                                        ? ageingHiddenCols
                                        : view === 'planning'
                                          ? effectivePlanningHiddenCols
                                          : effectivePipelineHiddenCols;
                              setShareModal({
                                  resourceKey,
                                  hiddenColumns: hiddenCols,
                              });
                          }
                }
                title={
                    view === 'snapshot'
                        ? 'Current Inventory'
                        : view === 'pipeline'
                          ? 'Inventory Pipeline'
                          : view === 'drr'
                            ? 'DoH by Channel'
                            : view === 'ageing'
                              ? 'SKU Ageing'
                              : 'Inventory Planning'
                }
                subtitle={
                    // Three states, because "groups" would be a lie about an exploded flat
                    // view and `totalCount` is the server's pre-explosion SKU count.
                    activeGroupCount === null
                        ? `${totalCount} SKUs`
                        : activeRollupIsOn
                          ? `${activeGroupCount} groups · ${totalCount} SKUs`
                          : `${activeGroupCount} base SKUs · broken down from ${totalCount}`
                }
                onAddToChat={
                    view === 'snapshot'
                        ? addSnapshotToChat
                        : view === 'drr' || view === 'ageing'
                          ? undefined
                          : addPlanningToChat
                }
                groupByOptions={
                    view === 'snapshot' && snapshotFilters.channel !== 'OVERALL'
                        ? [
                              { label: 'Warehouse', value: 'warehouse' },
                              { label: 'City', value: 'city' },
                              { label: 'State', value: 'state' },
                          ]
                        : undefined
                }
                activeGroupBy={
                    view === 'snapshot' && snapshotFilters.channel !== 'OVERALL'
                        ? snapshotGroupBy
                        : null
                }
                onGroupByChange={
                    view === 'snapshot' && snapshotFilters.channel !== 'OVERALL'
                        ? setSnapshotGroupBy
                        : undefined
                }
                enableSearch
                searchPlaceholder="Search SKU..."
                searchValue={currentFilters.search}
                onSearchChange={setSearch}
                filters={[currentProductFilterConfig]}
                toolbarControls={
                    <div className="flex items-center gap-2">
                        {view === 'snapshot' && (
                            <FilterDropdown
                                value={snapshotFilters.channel}
                                onChange={(val) => setChannel(val)}
                                options={channelOptions.map((opt) => ({
                                    label: opt.label,
                                    value: opt.value,
                                }))}
                                placeholder="Channel"
                            />
                        )}
                        {view === 'drr' && (
                            <FilterDropdown
                                value={drrChannel}
                                onChange={(val) => setDrrChannel(val)}
                                options={channelOptions.map((opt) => ({
                                    label: opt.label,
                                    value: opt.value,
                                }))}
                                placeholder="Channel"
                            />
                        )}
                        {view === 'ageing' && (
                            <FilterDropdown
                                value={ageingScope}
                                onChange={(val) => {
                                    const next = val as InventoryAgeingScope;
                                    if (next === ageingScope) return;
                                    setSnapshotFilters((prev) => ({ ...prev, page: 1 }));
                                    setAgeingScope(next);
                                    // The warehouse grouping only exists in the warehouse scope: switch it
                                    // on when entering, and back off when leaving.
                                    if (next === 'WAREHOUSE' && ageingRollupBy === NO_ROLLUP) {
                                        setAgeingRollupBy(AGEING_WAREHOUSE_DIMENSION);
                                    } else if (next === 'INTERNAL' && ageingRollupBy === AGEING_WAREHOUSE_DIMENSION) {
                                        setAgeingRollupBy(NO_ROLLUP);
                                        setAgeingSubRollupBy(NO_ROLLUP);
                                    }
                                }}
                                options={[
                                    { label: 'All warehouses', value: 'INTERNAL' },
                                    { label: 'By warehouse', value: 'WAREHOUSE' },
                                ]}
                                placeholder="Scope"
                            />
                        )}
                        <GroupingLevels
                            options={view === 'ageing' ? ageingRollupOptions : rollupOptions}
                            noneValue={NO_ROLLUP}
                            groupBy={
                                view === 'drr'
                                    ? drrRollupBy
                                    : view === 'ageing'
                                      ? ageingRollupBy
                                      : view === 'snapshot'
                                      ? snapshotRollupBy
                                      : view === 'planning'
                                        ? planningRollupBy
                                        : pipelineRollupBy
                            }
                            subGroupBy={
                                view === 'drr'
                                    ? drrSubRollupBy
                                    : view === 'ageing'
                                      ? ageingSubRollupBy
                                      : view === 'snapshot'
                                      ? snapshotSubRollupBy
                                      : view === 'planning'
                                        ? planningSubRollupBy
                                        : pipelineSubRollupBy
                            }
                            onChange={(val, subVal) => {
                                // Row counts change with the dimension, so restart
                                // at page 1 rather than stranding the user past
                                // the end of the grouped set.
                                setSnapshotFilters((prev) => ({
                                    ...prev,
                                    page: 1,
                                }));
                                setPlanningFilters((prev) => ({
                                    ...prev,
                                    page: 1,
                                }));
                                if (view === 'drr') {
                                    setDrrRollupBy(val);
                                    setDrrSubRollupBy(subVal);
                                } else if (view === 'ageing') {
                                    setAgeingRollupBy(val);
                                    setAgeingSubRollupBy(subVal);
                                } else if (view === 'snapshot') {
                                    setSnapshotRollupBy(val);
                                    setSnapshotSubRollupBy(subVal);
                                } else if (view === 'planning') {
                                    setPlanningRollupBy(val);
                                    setPlanningSubRollupBy(subVal);
                                } else {
                                    setPipelineRollupBy(val);
                                    setPipelineSubRollupBy(subVal);
                                }
                            }}
                            hint="Rows roll up on the first level, then on the second inside it. Quantities sum at both levels; ratios like DoH are recomputed from the group's own totals."
                        />
                        {/* Ageing is ordered oldest-first by the server; the presets below are stock-health orderings. */}
                        {view !== 'ageing' && (
                            <FilterDropdown
                                value={
                                    view === 'drr'
                                        ? drrSortBy
                                        : view === 'snapshot'
                                          ? snapshotSortBy
                                          : planningSortBy
                                }
                                onChange={(val) => setSortBy(val as SortBy)}
                                options={[
                                    { label: 'Default Order', value: 'DEFAULT' },
                                    { label: 'Top Selling', value: 'TOP_SELLING' },
                                    { label: 'Low on Stock', value: 'LOW_STOCK' },
                                    { label: 'Overstock', value: 'OVER_STOCK' },
                                ]}
                                placeholder="Sort By"
                            />
                        )}
                        {/* Pipeline shares Planning's ordering, as it always has. */}
                        {view !== 'pipeline' && view !== 'ageing' && (
                            <RateBasisControl
                                metricLabel={
                                    view === 'drr'
                                        ? 'DRR'
                                        : config.inventoryPeriod === 'MONTH'
                                          ? 'MoH'
                                          : 'DoH'
                                }
                                savedBasis={savedBasis}
                                effectiveBasis={effectiveBasis}
                                effectiveWindow={effectiveWindow}
                                isDirty={isRateDirty}
                                saving={savingRate}
                                onBasisChange={setDraftBasis}
                                onWindowChange={setDraftWindow}
                                onSave={handleSaveRateDefault}
                                onReset={handleResetRate}
                                // A node pivot has no forecast to read: forecast is stored
                                // per channel, so there is nothing honest to split across a
                                // channel's individual FCs. The Overall layout does, and
                                // gets the full control.
                                hideBasis={view === 'drr' && drrChannel !== 'OVERALL'}
                            />
                        )}
                    </div>
                }
                toolbarActionsRight={(() => {
                    const isSnapshot = view === 'snapshot';
                    const isDrr = view === 'drr';
                    const isAgeing = view === 'ageing';
                    const drrChannelDefs =
                        isDrr && drrResp?.mode === 'channels'
                            ? drrResp.channels.map((channel) => ({
                                  key: `ch_${channel.key}`,
                                  label: channel.key,
                              }))
                            : [];
                    const defaultOnDefs = isSnapshot
                        ? [
                              ...SNAPSHOT_DEFAULT_ON_DEFS,
                              // Offered only in a channel scope, where the server declares the
                              // buckets — a switch for a column that can never exist is worse
                              // than no switch.
                              ...(snapshotInTransitBuckets.length
                                  ? [
                                        {
                                            key: 'inTransit',
                                            label: 'In transit columns',
                                        },
                                    ]
                                  : []),
                              // Only for brands whose rows carry the Stock Transfer location.
                              ...(snapshotStockTransferBuckets.length
                                  ? [
                                        {
                                            key: STOCK_TRANSFER_HIDE_KEY,
                                            label: 'Stock transfer column',
                                        },
                                    ]
                                  : []),
                          ]
                        : isDrr
                          ? [
                                { key: 'inv', label: 'Inventory columns' },
                                // Offered only when the response actually declares in-transit
                                // buckets, so a brand that is not switched on does not see a
                                // switch for columns it can never have.
                                ...(drrResp?.in_transit_buckets?.length
                                    ? [
                                          {
                                              key: 'inTransit',
                                              label: 'In transit columns',
                                          },
                                      ]
                                    : []),
                                // Only for brands the server declares Stock Transfer buckets for.
                                ...(drrResp?.stock_transfer_buckets?.length
                                    ? [
                                          {
                                              key: STOCK_TRANSFER_HIDE_KEY,
                                              label: 'Stock transfer columns',
                                          },
                                      ]
                                    : []),
                                { key: 'drr', label: 'DRR columns' },
                                {
                                    key: 'doh',
                                    label: `${dohHeaderLabel(config.inventoryPeriod)} columns`,
                                },
                                { key: 'internal', label: 'Internal columns' },
                                ...drrChannelDefs,
                            ]
                          : isAgeing
                            ? ageingByWarehouse
                                ? AGEING_WAREHOUSE_ON_DEFS
                                : AGEING_DEFAULT_ON_DEFS
                            : [];
                    const defaultOffDefs = isSnapshot
                        ? SNAPSHOT_DEFAULT_OFF_DEFS
                        : isDrr
                          ? [{ key: 'category', label: 'Category' }]
                          : isAgeing
                            ? AGEING_DEFAULT_OFF_DEFS
                            : PLANNING_DEFAULT_OFF_DEFS;
                    const hiddenCols = isSnapshot
                        ? snapshotHiddenCols
                        : isDrr
                          ? drrHiddenCols
                          : isAgeing
                            ? ageingHiddenCols
                            : view === 'planning'
                              ? planningHiddenCols
                              : pipelineHiddenCols;
                    const setHiddenCols = isSnapshot
                        ? setSnapshotHiddenCols
                        : isDrr
                          ? setDrrHiddenCols
                          : isAgeing
                            ? setAgeingHiddenCols
                            : view === 'planning'
                              ? setPlanningHiddenCols
                              : setPipelineHiddenCols;
                    const visibleAttrKeys = isSnapshot
                        ? snapshotVisibleAttrKeys
                        : planningVisibleAttrKeys;
                    const setVisibleAttrKeys = isSnapshot
                        ? setSnapshotVisibleAttrKeys
                        : setPlanningVisibleAttrKeys;
                    return (
                        <div className="flex items-center gap-2">
                            {isAdmin && (
                                <ColumnPicker
                                    defaultOnDefs={defaultOnDefs}
                                    defaultOffDefs={defaultOffDefs}
                                    hiddenKeys={hiddenCols}
                                    onChange={setHiddenCols}
                                    attrDefs={isDrr || isAgeing ? undefined : attrColumnDefs}
                                    visibleAttrKeys={isDrr || isAgeing ? undefined : visibleAttrKeys}
                                    onAttrChange={isDrr || isAgeing ? undefined : setVisibleAttrKeys}
                                />
                            )}
                        </div>
                    );
                })()}
                enableDownload
                allColumns={
                    (view === 'drr'
                        ? drrDownloadColumns
                        : view === 'ageing'
                          ? ageingDownloadColumns
                          : view === 'snapshot'
                          ? snapshotVisibleDownloadColumns
                          : planningVisibleDownloadColumns) as any
                }
                onFetchAllData={
                    (view === 'drr'
                        ? drrFetchAllData
                        : view === 'ageing'
                          ? ageingFetchAllData
                          : view === 'snapshot'
                          ? snapshotFetchAllData
                          : planningFetchAllData) as any
                }
                downloadFilename={
                    view === 'drr'
                        ? 'inventory_doh_by_channel.csv'
                        : view === 'ageing'
                          ? ageingByWarehouse
                              ? 'inventory_sku_ageing_by_warehouse.csv'
                              : 'inventory_sku_ageing.csv'
                          : view === 'snapshot'
                          ? 'inventory_snapshot.csv'
                          : view === 'planning'
                            ? 'inventory_planning.csv'
                            : 'inventory_pipeline.csv'
                }
                isLoading={loading}
                legend={
                    view === 'ageing' ? (
                        <DataSheetLegend
                            title="Age since receipt:"
                            items={[
                                ...ageBucketLabels(ageingEdges).map((label, index) => ({
                                    color: AGE_BUCKET_COLORS[Math.min(index, AGE_BUCKET_COLORS.length - 1)],
                                    label: `${label} days`,
                                })),
                                { color: OLDER_THAN_HISTORY_COLOR, label: ageingByWarehouse ? 'Untraced' : 'Before our receipt history' },
                            ]}
                        />
                    ) : (
                    <DataSheetLegend
                        title="Stock Color Codes:"
                        items={[
                            {
                                color: '#ef4444',
                                label: `Under Stock (< ${config.underStockLimit})`,
                            },
                            {
                                color: '#f59e0b',
                                label: `Over Stock (> ${config.overStockLimit})`,
                            },
                        ]}
                    />
                    )
                }
                footer={
                    <DataSheetPagination
                        currentPage={currentFilters.page}
                        // Rolled up, the page counts GROUPS — the rows actually on
                        // screen. Sub-rows expand inline within their parent's slot,
                        // so a page of 20 groups can reveal hundreds of SKUs.
                        totalPages={Math.max(
                            1,
                            Math.ceil(
                                (activeGroupCount ?? totalCount) / itemsPerPage,
                            ),
                        )}
                        pageSize={itemsPerPage}
                        totalItems={activeGroupCount ?? totalCount}
                        pageSizeOptions={[20, 50, 100]}
                        onPageChange={setPage}
                        onPageSizeChange={(size) => {
                            setItemsPerPage(size);
                            setPage(1);
                        }}
                    />
                }
            >
                {/* Table content rendered inside DataSheet */}
                {!loading && tableContent}
            </DataSheet>

            {/* Modal */}
            {modalProps && (
                <OpeningStockCalculationModal
                    isOpen={modalData.isOpen}
                    onClose={closeModal}
                    {...modalProps}
                />
            )}

            {/* In-Transit Breakdown Modal */}
            {inTransitModal.isOpen &&
                inTransitModal.row &&
                (() => {
                    const week =
                        inTransitModal.row.weeks[inTransitModal.weekIndex];
                    const periodDates = week
                        ? parsePeriodDates(week.label)
                        : null;
                    return periodDates ? (
                        <InTransitBreakdownModal
                            isOpen={inTransitModal.isOpen}
                            onClose={closeInTransitModal}
                            skuCode={inTransitModal.row.productId}
                            productName={inTransitModal.row.description}
                            periodLabel={week.label}
                            productId={inTransitModal.row.productId}
                            periodStart={periodDates.start}
                            periodEnd={periodDates.end}
                        />
                    ) : null;
                })()}

            {ageingDrill && (
                <AgeingReceiptsModal
                    isOpen={!!ageingDrill}
                    onClose={() => setAgeingDrill(null)}
                    productId={ageingDrill.row.product_id}
                    skuLabel={`${ageingDrill.row.product_id} - ${ageingDrill.row.name}`}
                    bucketEdges={ageingEdges}
                    highlight={ageingDrill.bucketIndex}
                    warehouseId={ageingDrill.row.warehouse_id ?? null}
                    warehouseName={ageingDrill.row.warehouse_name ?? null}
                />
            )}

            {drrDrill && (
                <DrrNodeBreakdownModal
                    isOpen={!!drrDrill}
                    onClose={() => setDrrDrill(null)}
                    channelKey={drrDrill.channelKey}
                    productId={drrDrill.productId}
                    skuLabel={drrDrill.skuLabel}
                    drrWindow={draftWindow ?? undefined}
                    inventoryPeriod={config.inventoryPeriod}
                />
            )}

            {stockTransferDrill && (
                <StockTransferModal
                    isOpen
                    onClose={() => setStockTransferDrill(null)}
                    productId={stockTransferDrill.productId}
                    skuLabel={stockTransferDrill.skuLabel}
                    warehouseName={stockTransferDrill.warehouseName}
                />
            )}

            {channelShipmentDrill && (
                <ChannelShipmentModal
                    isOpen
                    onClose={() => setChannelShipmentDrill(null)}
                    productId={channelShipmentDrill.productId}
                    skuLabel={channelShipmentDrill.skuLabel}
                    channelKey={channelShipmentDrill.channelKey}
                    leg={channelShipmentDrill.leg}
                    warehouseName={channelShipmentDrill.warehouseName}
                />
            )}

            {shareModal && (
                <ShareModal
                    opened={!!shareModal}
                    onClose={() => setShareModal(null)}
                    resourceKey={shareModal.resourceKey}
                    merchantId={currentUser?.organizationId ?? ''}
                    hiddenColumns={shareModal.hiddenColumns}
                />
            )}

        </div>
    );
};

export default InventoryPage;
